import { animate as Motion_animate, inView as Motion_inView, scroll as Motion_scroll, stagger as Motion_stagger, timeline as Motion_timeline, ScrollOffset as Motion_ScrollOffset, FocusTrap, Delegate } from "vendor";
import Swiper from 'swiper';


  /*------------------------------------*\
    #UTITLITIES
  \*------------------------------------*/

  function throttle(callback) {
    let requestId = null, lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  }


  function debounce(fn, delay) {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay);
    };
  }

  // This function listens for a specified event on an element and resolves the promise when the event is fired. This pattern is useful for asynchronously managing state changes that depend on certain events.
  function waitForEvent(element, eventName) {
    return new Promise((resolve) => {
      const done = (event) => {
        if (event.target === element) {
          element.removeEventListener(eventName, done);
          resolve(event);
        }
      };

      element.addEventListener(eventName, done);
    });
  }

  /**
   * An enhancement of `fetch` allowing us to cache responses so we can avoid making further network requests.
   */
  var cachedMap = new Map();

  function cachedFetch(url, options) {
    // cachedMap stores key-value pairs for successful fetch requests. The key is the url 
    const cacheKey = url;

    if (cachedMap.has(cacheKey)) {
      /**
       * If we have a cached response, we return a promise that resolves to a Response object.
       * This response is created using a Blob with the cached content, allowing us to use cached content as if it was a fresh response from the server.
       */
      return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
    }

    // If there is no cached response, we run a fetch request
    return fetch(url, options).then((response) => {
      if (response.status === 200) {
        // If successful, get the contentType
        const contentType = response.headers.get("Content-Type");

        // Check if the contentType is json or text
        if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
          // As Response object can only be consumed once, we need to clone the response to extract the content
          // We store the cloned response as text, which then gets cached
          response.clone().text().then((content) => {
            cachedMap.set(cacheKey, content);
          });
        }
      }
      return response;
    });
  }


  class Player extends EventTarget {
    constructor(durationInSec, stopOnVisibility = true) {
      super();
  
      this.callback = () => this.dispatchEvent(new CustomEvent("player:end"));
      this.duration = durationInSec * 1000;
      this.remainingTime = this.duration;
      this.startTime = null;
      this.timer = null;
      this.state = "paused";
  
      this.onVisibilityChangeListener = this.onVisibilityChange.bind(this);
  
      if (stopOnVisibility) {
        document.addEventListener("visibilitychange", this.onVisibilityChangeListener);
      }
    }
  
    getDuration() {
      return this.duration / 1000;
    }
  
    setDuration(durationInSec) {
      this.duration = durationInSec * 1000;
      this.remainingTime = this.duration;
    }
  
    pause() {
      if (this.state !== "started") {
        return;
      }
      clearTimeout(this.timer);
      this.state = "paused";
      this.remainingTime -= new Date().getTime() - this.startTime;
      this.dispatchEvent(new CustomEvent("player:pause", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
    }
  
    resume(restartTimer = false) {
      if (this.state !== "stopped") {
        if (restartTimer) {
          this.start();
        } else {
          clearTimeout(this.timer);
          this.startTime = new Date().getTime();
          this.state = "started";
          this.timer = setTimeout(this.callback, this.remainingTime);
          this.dispatchEvent(new CustomEvent("player:resume", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
        }
      }
    }
  
    start() {
      clearTimeout(this.timer);
      this.startTime = new Date().getTime();
      this.state = "started";
      this.remainingTime = this.duration;
      this.timer = setTimeout(this.callback, this.remainingTime);
      this.dispatchEvent(new CustomEvent("player:start", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
    }
  
    stop() {
      clearTimeout(this.timer);
      this.state = "stopped";
      this.dispatchEvent(new CustomEvent("player:stop"));
    }
  
    onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        this.pause();
      } else if (document.visibilityState === "visible") {
        this.resume();
      }
    }
  }
  
  if (!window.customElements.get("player-element")) {
    window.customElements.define("player-element", Player);
  }
  





  /*------------------------------------*\
    #SHOPIFY UTITLITIES
  \*------------------------------------*/

  function extractSectionId(element) {
    element = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
    return element.id.replace("shopify-section-", "");
  }


  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;

    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    let value = "";

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  var loadedProducts = {};

  var ProductLoader = class {
    static load(productHandle) {
      if (!productHandle) {
        return;
      }

      if (loadedProducts[productHandle]) {
        return loadedProducts[productHandle];
      }

      loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
        const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);

        if (response.ok) {
          const responseAsJson = await response.json();
          resolve(responseAsJson);
        } else {
          reject(`
            Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
            switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
            to "active".
          `);
        }
      });

      return loadedProducts[productHandle];
    }
  }

  var CountrySelector = class extends HTMLElement {
    constructor() {
      super();
      this._onCountryChangedListener = this._onCountryChanged.bind(this);
    }

    connectedCallback() {
      this.countryElement = this.querySelector('[name="address[country]"]');
      this.provinceElement = this.querySelector('[name="address[province]"]');
      this.countryElement.addEventListener("change", this._onCountryChangedListener);

      /**
       * If the country selector is in the Shipping Estimator, we define this variable.
       */
      this.inShippingEstimator = this.closest("shipping-estimator") ? true : void 0;

      if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
        this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country")));
        this.countryElement.dispatchEvent(new Event("change"));
      } else {
        this._onCountryChanged();
      }
    }

    disconnectedCallback() {
      this.countryElement.removeEventListener("change", this._onCountryChangedListener);
    }

    _onCountryChanged() {
      const option = this.countryElement.options[this.countryElement.selectedIndex]; const provinces = JSON.parse(option.getAttribute("data-provinces"));

      this.provinceElement.parentElement.hidden = provinces.length === 0;

      if (provinces.length === 0) {
        return;
      }

      this.provinceElement.innerHTML = "";
      provinces.forEach((data) => {
        const selected = data[1] === this.getAttribute("province");
        this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
      });

      // If UK is selected, make England the default province
      if (this.inShippingEstimator && this.countryElement.value === 'United Kingdom') {
        this.provinceElement.value = 'England';
      }
    }
  };

  if (!window.customElements.get("country-selector")) {
    window.customElements.define("country-selector", CountrySelector);
  }




  /*------------------------------------*\
    #IMAGE UTILITIES
  \*------------------------------------*/

  function imageLoaded(imageOrArray) {
    if (!imageOrArray) {
      return Promise.resolve();
    }

    imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);

    return Promise.all(imageOrArray.map((image) => {
      return new Promise((resolve) => {
        if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
          resolve();
        } else {
          image.onload = () => resolve();
        }
      });
    }));
  }

  function generateSrcset(imageObject, widths = []) {
    const imageUrl = new URL(imageObject["src"]);

    return widths.filter((width) => width <= imageObject["width"]).map((width) => {
      imageUrl.searchParams.set("width", width.toString());
      return `${imageUrl.href} ${width}w`;
    }).join(", ");
  }

  function createMediaImg(media, widths = [], properties = {}) {
    const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), featuredMediaUrl = new URL(media["preview_image"]["src"]);

    for (const propertyKey in properties) {
      image.setAttribute(propertyKey, properties[propertyKey]);
    }

    image.alt = media["alt"];
    image.src = featuredMediaUrl.href;
    image.srcset = generateSrcset(media["preview_image"], widths);
    return image;
  }



  /*------------------------------------*\
    #ASSORTED UTILITY ELEMENTS
  \*------------------------------------*/

  /**
   * Gets the height of an element so we can store and update a CSS variable.
   */
  var HeightObserver = class extends HTMLElement {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(this._updateCustomProperties.bind(this)).observe(this);
      }
    }

    connectedCallback() {
      if (!window.ResizeObserver) {
        document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(1)}px`);
      }
    }

    _updateCustomProperties(entries) {
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          if (entry.target === this) {
            const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
            document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${Math.round(height)}px`);
          }
        });
      });
    }
  };

  if (!window.customElements.get("height-observer")) {
    window.customElements.define("height-observer", HeightObserver);
  }

  /**
   * This allows us to scroll a sticky container if the content is taller than the viewport.
   */
  var SafeSticky = class extends HTMLElement {
    constructor() {
      super();
      this._resizeObserver = new ResizeObserver(this._recalculateStyles.bind(this));
      this._checkPositionListener = throttle(this._checkPosition.bind(this));
      this._initialTop = 0;
      this._lastKnownY = 0;
      this._currentTop = 0;
      this._position = "relative";
    }

    connectedCallback() {
      Motion_inView(this, () => {
        this._resizeObserver.observe(this);
        window.addEventListener("scroll", this._checkPositionListener);

        return () => {
          this._resizeObserver.unobserve(this);
          window.removeEventListener("scroll", this._checkPositionListener);
        };
      }, { margin: "500px" });
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._checkPositionListener);
    }

    _recalculateStyles() {
      this.style.removeProperty("top");
      const computedStyles = getComputedStyle(this);
      this._initialTop = parseInt(computedStyles.top);
      this._position = computedStyles.position;
      this._checkPosition.call(this);
    }

    _checkPosition() {
      if (this._position !== "sticky") {
        return this.style.removeProperty("top");
      }

      let bounds = this.getBoundingClientRect(); 
      let maxTop = bounds.top + window.scrollY - this.offsetTop + this._initialTop; 
      let minTop = this.clientHeight - window.innerHeight + 20;

      if (window.scrollY < this._lastKnownY) {
        this._currentTop = this._currentTop - window.scrollY - this._lastKnownY;
      } else {
        this._currentTop = this._currentTop + this._lastKnownY - window.scrollY;
      }

      this._currentTop = Math.min(Math.max(this._currentTop, -minTop), maxTop, this._initialTop);
      this._lastKnownY = window.scrollY;
      this.style.top = `${Math.round(this._currentTop)}px`;
    }
  };

  if (!window.customElements.get("safe-sticky")) {
    window.customElements.define("safe-sticky", SafeSticky);
  }



  /*------------------------------------*\
    #NAVIGATION ELEMENTS
  \*------------------------------------*/

  var PageDots = class extends HTMLElement {
    _pendingAnimationControls = [];

    connectedCallback() {
      this._abortController = new AbortController();
      this.items = Array.from(this.children);

      // Add event listener to all buttons
      this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));

      // If we filter our controlled elements e.g. on a product gallery carousel we only show certain images upon variant selection, this will filter our dots or thumbnails
      this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });

      // Link the page dots to changes on the controlled element
      if (this.controlledElement) {
        this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
      }

      if (this.controlledElement.hasAttribute("autoplay")) {
        this.duration = this.controlledElement.getAttribute("autoplay");
        this.circles = Array.from(this.querySelectorAll(".numbered-dots__item"));

        const activeElement = this.querySelector('[aria-current="true"]');
        const activeCircle = activeElement.querySelector("circle:last-child");

        if (this.controlledElement.parentElement.classList.contains("hidden")) {
          return;
        }

        if (activeCircle) {
          Motion_animate(activeCircle, { strokeDasharray: [`0px, ${activeCircle.getTotalLength()}px`, `${activeCircle.getTotalLength()}px, ${activeCircle.getTotalLength()}px`] }, { duration: this.duration, easing: "linear" });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get autoplayoff() {
      return this.hasAttribute("autoplay-off");
    }

    get hasNumberedDots() {
      return this.hasAttribute("numbered-dots");
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }

    get selectedIndex() {
      return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
    }

    select(selectedIndex, dispatchEvent = true) {
      if (selectedIndex < 0 || selectedIndex >= this.items.length) {
        return;
      }

      if (this.hasAttribute("align-selected")) {
        const scrollElement = this.getAttribute("align-selected") !== "" ? this.closest(this.getAttribute("align-selected")) : this;
        const tabButton = this.items[selectedIndex];

        /**
         * If we have scrolling tabs, we want to scroll to the selected tab and position it
         * in the center if possible. This guarantees the user will see the previous and next
         * tabs.
         */

        if (tabButton && scrollElement) {
          scrollElement.scrollTo({
            left: tabButton.offsetLeft - scrollElement.clientWidth / 2 + tabButton.clientWidth / 2,
            top: tabButton.offsetTop - scrollElement.clientHeight / 2 - tabButton.clientHeight / 2,
            behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
          });
        }
      }

      if (this.selectedIndex === selectedIndex) {
        return;
      }

      // Set aria-current attribute on all elements. Selected element gets true while others get false.
      this.items.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));

      if (dispatchEvent) {
        this._dispatchEvent(selectedIndex);
      }

      if (this.controlledElement.parentElement.classList.contains("hidden")) {
        return;
      }

      if (this.circles) {
        this.circles.forEach((item) => {
          if (window.getComputedStyle(item).display === "none") {
            return;
          }

          const circle = item.querySelector("circle:last-child");

          let circleAnimationControl = null;

          if (item.parentElement.parentElement.getAttribute("aria-current") === "true") {
            circleAnimationControl = Motion_animate(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: this.duration, easing: "linear" });
          } else {
            circleAnimationControl = Motion_animate(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
          }
        });
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _dispatchEvent(index) {
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index: index, externalClick: true } }));
    }
  };

  var PrevButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:prev", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      // Enable or disable the prev button when approaching end of a scrolling element
      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "start", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "start" ? false : this.disabled, { signal: this._abortController.signal });

        // Disables the previous button on the first slide for non-looping effectCarousels 
        if (!this.controlledElement.loop) {  
          this.controlledElement.addEventListener("effect-carousel:change", (event) => {
            this.disabled = event.detail.index == 0;
          });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };

  var NextButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:next", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "end", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "end" ? false : this.disabled, { signal: this._abortController.signal });

        // Disables the next button on the last slide for non-looping effectCarousels 
        if (!this.controlledElement.loop) {  
          this.controlledElement.addEventListener("effect-carousel:change", (event) => {
            this.disabled = event.detail.index == this.controlledElement.items.length - 1;
          });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }
    
    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };

  if (!window.customElements.get("page-dots")) {
    window.customElements.define("page-dots", PageDots);
  }

  if (!window.customElements.get("prev-button")) {
    window.customElements.define("prev-button", PrevButton, { extends: "button" });
  }

  if (!window.customElements.get("next-button")) {
    window.customElements.define("next-button", NextButton, { extends: "button" });
  }

  /**
   * Adds gesture event listeners. For example, we can use this in our `EffectCarousel` which allows us to check for a swipe and then change slides.
   */
  var GestureArea = class {
    constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
      this._domElement = domElement;
      this._thresholdDistance = thresholdDistance;
      this._thresholdTime = thresholdTime;
      this._signal = signal;
      this._firstClientX = null;
      this._tracking = false;
      this._start = {};
  
      this._domElement.addEventListener("touchstart", this._touchStart.bind(this), { passive: true, signal: this._signal });
      this._domElement.addEventListener("touchmove", this._preventTouch.bind(this), { passive: false, signal: this._signal });
      this._domElement.addEventListener("pointerdown", this._gestureStart.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointermove", this._gestureMove.bind(this), { passive: false, signal: this._signal });
      this._domElement.addEventListener("pointerup", this._gestureEnd.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointerleave", this._gestureEnd.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointercancel", this._gestureEnd.bind(this), { signal: this._signal });
    }
  
    _touchStart(event) {
      this._firstClientX = event.touches[0].clientX;
    }
  
    _preventTouch(event) {
      if (Math.abs(event.touches[0].clientX - this._firstClientX) > 10) {
        event.preventDefault();
      }
    }
  
    _gestureStart(event) {
      this._tracking = true;
      this._start = {
        time: new Date().getTime(),
        x: event.clientX,
        y: event.clientY
      };
    }
  
    _gestureMove(event) {
      if (this._tracking) {
        event.preventDefault();
      }
    }
  
    _gestureEnd(event) {
      if (!this._tracking) {
        return;
      }
      this._tracking = false;
      const now = new Date().getTime();
      const deltaTime = now - this._start.time;
      const deltaX = event.clientX - this._start.x;
      const deltaY = event.clientY - this._start.y;
  
      if (deltaTime > this._thresholdTime) {
        return;
      }
  
      let matchedEvent;
      if (deltaX === 0 && deltaY === 0 && !event.target.matches("a, button, a :scope, button :scope")) {
        matchedEvent = "tap";
      } else if (deltaX > this._thresholdDistance && Math.abs(deltaY) < this._thresholdDistance) {
        matchedEvent = "swiperight";
      } else if (-deltaX > this._thresholdDistance && Math.abs(deltaY) < this._thresholdDistance) {
        matchedEvent = "swipeleft";
      } else if (deltaY > this._thresholdDistance && Math.abs(deltaX) < this._thresholdDistance) {
        matchedEvent = "swipedown";
      } else if (-deltaY > this._thresholdDistance && Math.abs(deltaX) < this._thresholdDistance) {
        matchedEvent = "swipeup";
      }
  
      if (matchedEvent) {
        this._domElement.dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
      }
    }
  }

  /**
   * Adds scroll event listeners. We use this on many of our scroll carousels to determine the scroll position and enable or disable carousel navigation as needed. E.g. if we reach the end of a scroll container, any 'next' buttons should be disabled.
   */
  var ScrollArea = class {
    constructor(element, abortController2 = null) {
      this._element = element;
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._allowTriggerNearingEndEvent = true;
      this._allowTriggerLeavingEndEvent = false;

      new ResizeObserver(this._checkIfScrollable.bind(this)).observe(element);
      this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController2?.signal });
    }

    get scrollNearingThreshold() {
      // return 125;
      return 48;
    }

    get scrollDirection() {
      if (this._element.scrollWidth > this._element.clientWidth) {
        return "inline";
      } else if (this._element.scrollHeight - this._element.clientHeight > 1) {
        return "block";
      } else {
        return "none";
      }
    }

    _checkIfScrollable() {
      this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
    }

    _onScroll() {
      clearTimeout(this._scrollTimeout);
      this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));

      let direction;

      if (this.scrollDirection === "inline") {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollLeft);
      } else {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollTop);
      }

      const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop));

      const scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);

      if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = false;
        this._allowTriggerLeavingStartEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
      } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = true;
        if (this._allowTriggerLeavingStartEvent) {
          this._allowTriggerLeavingStartEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
        }
      }

      if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = false;
        this._allowTriggerLeavingEndEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
      } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = true;
        if (this._allowTriggerLeavingEndEvent) {
          this._allowTriggerLeavingEndEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
        }
      }

      if (window.onscrollend === void 0) {
        this._scrollTimeout = setTimeout(() => {
          this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
        }, 75);
      }
    }
  };

  /**
   * Used for many of our scroll containers to provide a visual element tracking scroll progress.
   */
  var ScrollProgress = class extends HTMLElement {
    connectedCallback() {
      this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));

      if (window.ResizeObserver) {
        new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
      }
    }

    get scrolledElement() {
      return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
    }

    _updateScrollProgress() {
      const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft); 
      const advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
      this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
    }
  };

  if (!window.customElements.get("scroll-progress")) {
    window.customElements.define("scroll-progress", ScrollProgress);
  }

  /**
   * #SCROLL SHADOW
   * The following elements allow us to add a Scroll Shadow parent to scroll carousels. This tracks the position of a scroll container and adds a shadow as a visual indicator that there is more content to scroll.
   */
  var template = `
    <style>
      :host {
        display: inline-block;
        contain: layout;
        position: relative;
      }
      
      :host([hidden]) {
        display: none;
      }
      
      s {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        background-image:
          var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--background)), rgb(var(--background) / 0)));
        background-position: top, bottom, left, right;
        background-repeat: no-repeat;
        background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
      }
    </style>
    <slot></slot>
    <s></s>
  `;

  var Updater = class {
    constructor(targetElement) {
      this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
    }

    start(element) {
      if (this.element) {
        this.stop();
      }

      if (element) {
        element.addEventListener("scroll", this.scheduleUpdate);
        this.resizeObserver.observe(element);
        this.element = element;
      }
    }

    stop() {
      if (!this.element) {
        return;
      }

      this.element.removeEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.unobserve(this.element);
      this.element = null;
    }

    update(targetElement, style) {
      if (!this.element) {
        return;
      }

      const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
      const scroll = {
        top: Math.max(this.element.scrollTop, 0),
        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
        left: Math.max(this.element.scrollLeft, 0),
        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
      };

      requestAnimationFrame(() => {
        for (const position of ["top", "bottom", "left", "right"]) {
          targetElement.style.setProperty(
            `--${position}`,
            `${scroll[position] > maxSize ? maxSize : scroll[position]}px`
          );
        }
      });
    }
  };

  var ScrollShadow = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).innerHTML = template;
      this.updater = new Updater(this.shadowRoot.lastElementChild);
    }

    connectedCallback() {
      this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
      this.start();
    }

    disconnectedCallback() {
      this.updater.stop();
    }

    start() {
      if (this.firstElementChild) {
        this.updater.start(this.firstElementChild);
      }
    }
  };

  if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
    window.customElements.define("scroll-shadow", ScrollShadow);
  }


  
  /*------------------------------------*\
    #CAROUSELS
  \*------------------------------------*/

  /**
   * Our BaseCarousel. Not currently used by any elements, but we extend this to create other carousels.
   */
  var BaseCarousel = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._reloaded = false;

      if (Shopify.designMode) {
        this.closest(".shopify-section")?.addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
      }

      this._attachMultiItemBehavior();

      if (this.selectedIndex === 0) {
        this.selectedSlide?.classList.add("is-selected");
        this._dispatchEvent("carousel:select", 0);
      } else {
        this.select(this.selectedIndex, { animate: false, force: true });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
      this._multiItemBehaviorAttached = false;
      this._items = null;
    }

    _attachMultiItemBehavior() {
      if (this._multiItemBehaviorAttached || this.items.length <= 1) {
        return;
      }

      this._multiItemBehaviorAttached = true;

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
      }

      if (this.hasAttribute("adaptive-height")) {
        this.addEventListener("carousel:settle", this._adjustHeight);
        this._adjustHeight();
      }

      this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
      this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
      this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
      this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
      this.addEventListener("control:select", (event) => this.select(event.detail.index, event.detail.externalClick), { signal: this._abortController.signal });
    }

    /**
     * Invalidate cached slide nodes and wire multi-item listeners when slides are injected after connect (e.g. async fetch into an empty carousel).
     */
    refreshItems() {
      const previousCount = this.items.length;
      this._items = null;
      this._attachMultiItemBehavior();

      if (this.items.length === 0) {
        return;
      }

      if (previousCount === 0) {
        if (this.selectedIndex === 0) {
          this.selectedSlide?.classList.add("is-selected");
          this._dispatchEvent("carousel:select", 0);
        } else {
          this.select(this.selectedIndex, { animate: false, force: true });
        }
      } else {
        this.items.forEach((item, index) => item.classList.toggle("is-selected", index === this.selectedIndex));
      }
    }

    get items() {
      if (this.hasAttribute("reversed")) {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
      } else {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
      }
    }

    /**
     * offsetParent checks if the element has a parent. If the item itself is display: none, visibility: hidden, or opacity: 0, then it will not be registered.
     */
    get visibleItems() {
      return this.items.filter((item) => item.offsetParent !== null);
    }

    // If we have no selected slide, we set this as 0
    get selectedIndex() {
      return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
    }

    get selectedIndexAmongVisible() {
      return this.visibleItems.indexOf(this.selectedSlide);
    }

    get loop() {
      return false;
    }

    get selectedSlide() {
      return this.items[this.selectedIndex];
    }

    get previousSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
    }

    get nextSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
    }

    previous(animate11 = true) {
      const slide = this.previousSlide;
      if (!slide) {
        return;
      }
      this.select(this.items.indexOf(slide), false, { direction: "previous", animate: animate11 });
    }

    next(animate11 = true) {
      const slide = this.nextSlide;
      if (!slide) {
        return;
      }
      this.select(this.items.indexOf(slide), false, { direction: "next", animate: animate11 });
    }

    _transitionTo(fromSlide, toSlide, options = {}) {
    }

    _adjustHeight() {
      if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
        this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _preloadImages() {
      [this.previousSlide, this.nextSlide].forEach((item) => {
        if (item) {
          Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        }
      });
    }

    _dispatchEvent(eventName, index) {
      this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
    }
  };


  /**
   * The EffectCarousel allows us to add specific transitions such as a fade. It essentially functions like a traditional slideshow. We use the `GestureArea` element to listen for swipe events.
   * In many cases, we use the `EffectCarousel` in combination with the `PageDots` element to simulate tabbed functionality such as on the `ImageTabs`, `Timeline` and `MultipleImagesWithText` sections.
   */
  var EffectCarousel = class extends BaseCarousel {
    constructor() {
      super();
      this.addEventListener("carousel:select", this._onSlideSelected);
    }

    connectedCallback() {
      super.connectedCallback();

      if (this.items.length > 1 && this.hasAttribute("autoplay") && window.matchMedia("screen and (min-width: 1000px)").matches) {
        this._player = new Player(this.getAttribute("autoplay"));
        this._player.addEventListener("player:end", this.next.bind(this));
        Motion_inView(this, () => {
          this._player.resume(true);
          return () => this._player.pause();
        });

        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", () => this._player.stop());
          this.addEventListener("shopify:block:deselect", () => this._player.start());
        }
      }

      if (this.items.length > 1) {
        if (this.swipeable) {
          new GestureArea(this, { signal: this._abortController.signal });
          this.addEventListener("swipeleft", this.next, { signal: this._abortController.signal });
          this.addEventListener("swiperight", this.previous, { signal: this._abortController.signal });
        }
      }
    }

    get player() {
      return this._player;
    }

    get loop() {
      return true;
    }

    get swipeable() {
      return this.getAttribute("swipeable") !== "false";
    }

    get staticMedia() {
      return this.hasAttribute("static-media");
    }

    async select(index, externalClick = false, { direction, animate: animate11 = true } = {}) {
      const toSlide = this.items[index];
      if (!toSlide || index < 0 || index >= this.items.length) {
        return;
      }

      const indexBeforeChange = this.selectedIndex;
      this._selectedIndex = index;
      this._dispatchEvent("carousel:select", index);

      if (!direction) {
        direction = index > indexBeforeChange ? "next" : "previous";
      }

      if (index !== indexBeforeChange) {
        const fromSlide = this.items[indexBeforeChange];
        this._dispatchEvent("carousel:change", index);
        this._dispatchEvent("effect-carousel:change", index);
        this.player?.pause();

        if (!this.staticMedia) {
          await this._transitionTo(fromSlide, toSlide, { direction, animate: animate11 }); 
        }
        
        if (externalClick) {
          document.querySelector(`[aria-controls="${this.id}"]`)?.setAttribute("autoplay-off", "");
        } else {
          this.player?.resume(true);
        }

        this._dispatchEvent("carousel:settle", index);
      }
    }

    /**
     * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
     * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
     * that resolves when the animation is finished
     */
    _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate11 = true } = {}) {
      if (!fromSlide || !toSlide) {
        return Promise.resolve();
      }

      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      /**
       * `step-end` easing means the z-index value will change immediately once
       * everything is complete. We do this to ensure the z-index value isn't
       * being gradually animated.
       */
      const timelineControls = Motion_timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { zIndex: { easing: "step-end" } }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { at: "<", zIndex: { easing: "step-end" } }]
      ], { duration: animate11 ? 0.3 : 0 });

      return timelineControls.finished;
    }

    async _onSlideSelected(event) {
      Array.from(this.querySelectorAll("video-media")).forEach((video) => video.pause());

      if (!event.detail.slide) {
        return;
      }

      if (event.detail.slide.getAttribute("data-slide-type") === "video") {
        const visibleVideo = Array.from(event.detail.slide.querySelectorAll("video-media")).filter((video) => video.offsetParent !== null).pop();

        visibleVideo.play({ restart: true });

        if (this.player) {
          const video = visibleVideo.querySelector("video");

          if (isNaN(video.duration)) {
            await new Promise((resolve) => {
              video.onloadedmetadata = () => resolve();
            });
          }
          
          this._player.setDuration(video.duration);
        }
      } else {
        this._player?.setDuration(parseInt(this.getAttribute("autoplay")));
      }
    }
  };

  if (!window.customElements.get("effect-carousel")) {
    window.customElements.define("effect-carousel", EffectCarousel);
  }

  /**
   * Our scrolling carousel element. Along with users being able to manually scroll, it listens for navigation events which will automatically scroll a user to their selection.
   */
  var ScrollCarousel = class extends BaseCarousel {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
      }
    }

    connectedCallback() {
      this._abortController = new AbortController();
      
      this._hasPendingProgrammaticScroll = false;
      this._scrollArea = new ScrollArea(this, this._abortController);
      super.connectedCallback();

      this.distanceToBleed = getComputedStyle(this).getPropertyValue('padding-inline-start');
      this.addEventListener("scroll", throttle(this._onCarouselScroll.bind(this)), { signal: this._abortController.signal });

      this.addEventListener("scrollend", this._onScrollSettled, { signal: this._abortController.signal });
    }

    /**
     * This is assuming that all items are the same width. It consistently stores itemOffset as the difference
     * between the index 1 and 0 items.
     */
    get itemOffset() {
      return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
    }

    /**
     * This also assumes equal width items. It uses the 0 index element to help determine slide size
     */
    get slidesPerPage() {
      return this.visibleItems.length < 2 ? 1 : Math.floor((this.clientWidth - this.visibleItems[0].offsetLeft) / (Math.abs(this.itemOffset) - (parseInt(getComputedStyle(this).gap) || 0)));
    }

    /**
     * As above, the totalPages will be determined by the slidesPerPage which means assuming equal width items.
     */
    get totalPages() {
      return this.visibleItems.length < 2 ? 1 : this.visibleItems.length - this.slidesPerPage + 1;
    }

    select(index, { animate: animate11 = true, force = false } = {}) {
      const indexBeforeChange = this.selectedIndex;

      if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
        return this._selectedIndex = index;
      }

      const indexAmongVisible = this.visibleItems.indexOf(this.items[index]);
      index = this.items.indexOf(this.visibleItems[Math.min(this.totalPages, indexAmongVisible)]);
      this._selectedIndex = index;
      this._dispatchEvent("carousel:select", index);

      if (index !== indexBeforeChange || force) {
        const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
        this._dispatchEvent("carousel:change", index);
        this._transitionTo(fromSlide, toSlide, { animate: animate11 });
      }
    }

    /**
     * Transition using the scrollTo method. To prevent the intersection observer from catching the change, we set
     * a "hasPendingProgrammaticScroll" variable to true, that is set back to false once the scroll has settled
     */
    _transitionTo(fromSlide, toSlide, { animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      let slideAlign = this._extractSlideAlign(toSlide);
      let scrollAmount = 0;

      switch (slideAlign) {
        case "start":
          // Assuming equal sized items. It simply gets the offset and multiplies by the index of the new slide
          scrollAmount = this.itemOffset * this.visibleItems.indexOf(toSlide);

          /* The below is commented out but works if we ever have different sized items */
          // scrollAmount = toSlide.offsetLeft - parseInt(this.distanceToBleed);
          break;
        case "end":
          scrollAmount = toSlide.offsetLeft + toSlide.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0);
          break;
        case "center":
          scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
          break;
      }

      this._hasPendingProgrammaticScroll = animate11;
      this.scrollTo({ left: scrollAmount, behavior: animate11 ? "smooth" : "auto" });
    }

    /**
     * Update the index when manually scrolling (which allows to update the controls)
     */
    _onCarouselScroll() {
      if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
        return;
      }

      const newIndex = this.items.indexOf(this.visibleItems[Math.round(this.scrollLeft / this.itemOffset)]);

      if (newIndex !== this.selectedIndex) {
        this._selectedIndex = newIndex;
        this._dispatchEvent("carousel:select", this.selectedIndex);
        this._dispatchEvent("carousel:change", this.selectedIndex);
      }
    }

    /**
     * On the scroll has settled we dispatch the event (which covers both programmatic scroll and swipe)
     */
    _onScrollSettled() {
      this.items.forEach((item) => item.classList.remove("is-selected"));
      this.selectedSlide.classList.add("is-selected");
      this._hasPendingProgrammaticScroll = false;
      this._dispatchEvent("carousel:settle", this.selectedIndex);
    }

    _adjustHeight() {
      this.style.maxHeight = null;
      if (this._scrollArea.scrollDirection !== "none") {
        super._adjustHeight();
      }
    }

    _extractSlideAlign(slide) {
      const align = getComputedStyle(slide).scrollSnapAlign;

      if (align === "center" || align.startsWith("center")) {
        return "center";
      }

      if (align === "end" || align.endsWith("end")) {
        return "end";
      }

      return "start";
    }
  };

  if (!window.customElements.get("scroll-carousel")) {
    window.customElements.define("scroll-carousel", ScrollCarousel);
  }

  /**
   * Scroll carousel with infinite loop: duplicates the slide set before and after the originals (triple buffer)
   * so the active slide can stay scroll-snap centered while scrolling wraps. Exposes logical slide indices (0..n-1)
   * via selectedIndex and carousel events.
   */
  var CentredCarousel = class extends ScrollCarousel {
    connectedCallback() {
      this._ensureCentredClones();
      if (this._centredTriple) {
        const n = this._originalCount;
        const initialLogical = parseInt(this.getAttribute("initial-index") || "0", 10);
        this._selectedIndex = this._centredSegmentSize + ((initialLogical % n) + n) % n;
      }
      super.connectedCallback();
      if (this._centredTriple) {
        queueMicrotask(() => {
          const dom = this._selectedIndex ?? this._centredSegmentSize;
          this._hasPendingProgrammaticScroll = true;
          this.scrollTo({ left: this._scrollLeftForCenteredSlide(this.items[dom]), behavior: "auto" });
          this._syncCentredSlideClasses();
        });
      }
    }

    get selectedIndex() {
      if (this._centredTriple && this._originalCount) {
        return this._domToLogical(this._selectedIndex ?? this._centredSegmentSize);
      }
      return super.selectedIndex;
    }

    get selectedSlide() {
      if (this._centredTriple && this._originalCount) {
        const dom = this._selectedIndex ?? this._centredSegmentSize;
        return this.items[dom];
      }
      return super.selectedSlide;
    }

    get loop() {
      return this._centredTriple ? true : super.loop;
    }

    select(index, { animate: animate11 = true, force = false } = {}) {
      if (!this._centredTriple) {
        return super.select(index, { animate: animate11, force });
      }

      const indexBeforeLogical = this.selectedIndex;
      const fromDom = this._selectedIndex ?? this._centredSegmentSize;
      const domIndex = this._resolveDomIndexForLogical(index, fromDom, indexBeforeLogical);

      if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
        return (this._selectedIndex = domIndex);
      }

      this._selectedIndex = domIndex;
      this._dispatchEvent("carousel:select", this.selectedIndex);

      if (index !== indexBeforeLogical || force) {
        const fromSlide = this.items[fromDom];
        const toSlide = this.items[domIndex];
        this._dispatchEvent("carousel:change", this.selectedIndex);
        this._transitionTo(fromSlide, toSlide, { animate: animate11 });
        if (!animate11) {
          this._syncCentredSlideClasses();
        }
      }
    }

    /**
     * Scroll only — selection classes are applied once in _syncCentredSlideClasses (scrollend or instant select).
     * Avoids is-selected being toggled by ScrollCarousel._transitionTo and again by _onScrollSettled.
     */
    _transitionTo(fromSlide, toSlide, { animate: animate11 = true } = {}) {
      if (!this._centredTriple) {
        return super._transitionTo(fromSlide, toSlide, { animate: animate11 });
      }

      let slideAlign = this._extractSlideAlign(toSlide);
      let scrollAmount = 0;

      switch (slideAlign) {
        case "start":
          scrollAmount = this.itemOffset * this.visibleItems.indexOf(toSlide);
          break;
        case "center":
          scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
          break;
      }

      this._hasPendingProgrammaticScroll = animate11;
      this.scrollTo({ left: scrollAmount, behavior: animate11 ? "smooth" : "auto" });
    }

    previous(animate11 = true) {
      if (!this._centredTriple) {
        return super.previous(animate11);
      }
      const n = this._originalCount;
      const logical = (this.selectedIndex - 1 + n) % n;
      this.select(logical, { animate: animate11 });
    }

    next(animate11 = true) {
      if (!this._centredTriple) {
        return super.next(animate11);
      }
      const n = this._originalCount;
      const logical = (this.selectedIndex + 1) % n;
      this.select(logical, { animate: animate11 });
    }

    _onCarouselScroll() {
      if (!this._centredTriple) {
        return super._onCarouselScroll();
      }
      if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none" || this._centredRealigning) {
        return;
      }

      const domIdx = this._domIndexOfCenteredSlide();
      if (domIdx < 0) {
        return;
      }

      const newLogical = this._domToLogical(domIdx);
      if (newLogical !== this.selectedIndex) {
        this._selectedIndex = domIdx;
        this._dispatchEvent("carousel:select", newLogical);
        this._dispatchEvent("carousel:change", newLogical);
      }
    }

    /**
     * scrollLeft / itemOffset only matches slide index for start-aligned snaps; center-aligned snaps need the
     * slide whose midpoint is nearest the viewport centre.
     */
    /**
     * Scroll bar progress for centred-carousel: 0 on first logical slide, 1 on last (loop wrap uses logical index).
     */
    getLogicalScrollProgress() {
      if (!this._centredTriple || !this._originalCount) {
        return null;
      }
      const n = this._originalCount;
      if (n <= 1) {
        return 1;
      }
      const domIdx = this._domIndexOfCenteredSlide();
      if (domIdx < 0) {
        return null;
      }
      const logical = this._domToLogical(domIdx);
      return logical / (n - 1);
    }

    _domIndexOfCenteredSlide() {
      const items = this.items;
      if (!items.length) {
        return -1;
      }

      const scrollPadding = parseInt(getComputedStyle(this).scrollPaddingInline, 10) || 0;
      const viewportCenter = this.scrollLeft + this.clientWidth / 2 - scrollPadding;

      let bestDom = -1;
      let bestDist = Infinity;
      for (let i = 0; i < items.length; i++) {
        const el = items[i];
        if (el.offsetParent === null) {
          continue;
        }
        const slideCenter = el.offsetLeft + el.clientWidth / 2;
        const dist = Math.abs(slideCenter - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestDom = i;
        }
      }
      return bestDom;
    }

    _onScrollSettled() {
      if (!this._centredTriple) {
        return super._onScrollSettled();
      }

      const n = this._originalCount;
      const rawDom = this._selectedIndex ?? this._centredSegmentSize;
      let alignedDom = rawDom;
      if (rawDom < n) {
        alignedDom = rawDom + n;
      } else if (rawDom >= 2 * n) {
        alignedDom = rawDom - n;
      }

      if (alignedDom !== rawDom) {
        this._centredRealigning = true;
        this._hasPendingProgrammaticScroll = true;
        this.scrollTo({ left: this._scrollLeftForCenteredSlide(this.items[alignedDom]), behavior: "auto" });
        this._selectedIndex = alignedDom;
        this._centredRealigning = false;
        this._syncCentredSlideClasses();
        this._hasPendingProgrammaticScroll = false;
        this._dispatchEvent("carousel:settle", this.selectedIndex);
        return;
      }

      this._syncCentredSlideClasses();
      this._hasPendingProgrammaticScroll = false;
      this._dispatchEvent("carousel:settle", this.selectedIndex);
    }

    /**
     * Single source of truth for slide state. Keeps is-selected (compat) plus Swiper-style neighbour classes
     * on the physically adjacent slides in the track (optional attributes carousel-active-class, carousel-prev-class, carousel-next-class).
     */
    _syncCentredSlideClasses() {
      const items = this.items;
      if (!items.length) {
        return;
      }

      const activeCls = this.getAttribute("carousel-active-class") || "carousel-slide-active";
      const prevCls = this.getAttribute("carousel-prev-class") || "carousel-slide-prev";
      const nextCls = this.getAttribute("carousel-next-class") || "carousel-slide-next";

      const dom = this._selectedIndex ?? this._centredSegmentSize;
      const prevDom = dom - 1;
      const nextDom = dom + 1;

      for (let i = 0; i < items.length; i++) {
        const el = items[i];
        el.classList.remove("is-selected", activeCls, prevCls, nextCls);
      }

      const cur = items[dom];
      if (cur) {
        cur.classList.add("is-selected", activeCls);
      }
      if (prevDom >= 0) {
        items[prevDom]?.classList.add(prevCls);
      }
      if (nextDom < items.length) {
        items[nextDom]?.classList.add(nextCls);
      }
    }

    _scrollLeftForCenteredSlide(slide) {
      return slide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + slide.clientWidth / 2;
    }

    _ensureCentredClones() {
      const selector = this.getAttribute("selector");
      if (!selector || this._centredDomBuilt) {
        return;
      }
      const originals = Array.from(this.querySelectorAll(selector));
      if (originals.length <= 1) {
        return;
      }
      this._centredDomBuilt = true;
      const n = originals.length;
      this._originalCount = n;
      this._centredSegmentSize = n;
      const first = originals[0];
      for (let i = 0; i < n; i++) {
        this.insertBefore(originals[i].cloneNode(true), first);
      }
      for (let i = 0; i < n; i++) {
        this.appendChild(originals[i].cloneNode(true));
      }
      this._items = null;
      this._centredTriple = true;
    }

    _domToLogical(domIndex) {
      const n = this._originalCount;
      return ((domIndex % n) + n) % n;
    }

    _logicalToPreferredDom(logicalIndex) {
      return this._centredSegmentSize + logicalIndex;
    }

    /**
     * Same logical slide exists three times (A/B/C segments). For a single step next/prev we must scroll to the
     * physically adjacent replica; always using the middle segment would jump across the whole strip on wrap.
     */
    _resolveDomIndexForLogical(toLogical, fromDom, fromLogical) {
      const n = this._originalCount;
      const steppedNext = toLogical === (fromLogical + 1) % n;
      const steppedPrev = toLogical === (fromLogical + n - 1) % n;
      if (steppedNext) {
        return this._nearestDomReplica(toLogical, fromDom, "next");
      }
      if (steppedPrev) {
        return this._nearestDomReplica(toLogical, fromDom, "previous");
      }
      return this._logicalToPreferredDom(toLogical);
    }

    _nearestDomReplica(logicalTarget, fromDom, direction) {
      const n = this._originalCount;
      const candidates = [logicalTarget, logicalTarget + n, logicalTarget + 2 * n].filter(
        (i) => i >= 0 && i < this.items.length
      );
      const fromEl = this.items[fromDom];
      if (!fromEl) {
        return this._logicalToPreferredDom(logicalTarget);
      }

      const currentCenter = fromEl.offsetLeft + fromEl.clientWidth / 2;

      if (direction === "next") {
        let best = -1;
        let bestCenter = Infinity;
        for (const domIdx of candidates) {
          const el = this.items[domIdx];
          if (!el.offsetParent) {
            continue;
          }
          const sc = el.offsetLeft + el.clientWidth / 2;
          if (sc > currentCenter && sc < bestCenter) {
            bestCenter = sc;
            best = domIdx;
          }
        }
        if (best >= 0) {
          return best;
        }
      } else if (direction === "previous") {
        let best = -1;
        let bestCenter = -Infinity;
        for (const domIdx of candidates) {
          const el = this.items[domIdx];
          if (!el.offsetParent) {
            continue;
          }
          const sc = el.offsetLeft + el.clientWidth / 2;
          if (sc < currentCenter && sc > bestCenter) {
            bestCenter = sc;
            best = domIdx;
          }
        }
        if (best >= 0) {
          return best;
        }
      }

      return this._logicalToPreferredDom(logicalTarget);
    }
  };

  if (!window.customElements.get("centred-carousel")) {
    window.customElements.define("centred-carousel", CentredCarousel);
  }

  /*------------------------------------*\
    #CENTER CAROUSEL
  \*------------------------------------*/  
  
  class CenterCarousel extends ScrollCarousel {
    constructor() {
      super();
      this._resizeObserver = new ResizeObserver(() => this._updateLayout());
      this._player = new Player(4); // autoplay every 4 seconds
    }
  
    connectedCallback() {
      super.connectedCallback();
      this._resizeObserver.observe(this);
      this._updateLayout();
      this._initAutoplay();
    }
  
    disconnectedCallback() {
      super.disconnectedCallback();
      this._resizeObserver.disconnect();
      this._player.stop();
    }
  
    _updateLayout() {
      const width = this.clientWidth;
      if (width < 700) {
        // Mobile layout: 1 slide + 25% peek
        this.style.setProperty('--visible-slides', 1);
        this.style.setProperty('--peek-width', '25%');
        this._applySlideScaling();
      } else {
        // Desktop layout: odd number of visible slides, center slide enlarged
        const slides = width < 1000 ? 3 : 5; // example
        this.style.setProperty('--visible-slides', slides);
        this.style.setProperty('--peek-width', '0');
        this._applySlideScaling();
      }
    }
  
    _applySlideScaling() {
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === this.selectedIndex);
      });
    }
  
    select(index, options = {}) {
      super.select(index, options);
      this._applySlideScaling();
      this._resetAutoplay();
    }
  
    _initAutoplay() {
      this._player.addEventListener('player:end', () => this._autoplayNext());
      this._player.start();
    }
  
    _autoplayNext() {
      const nextIndex = (this.selectedIndex + 1) % this.items.length;
      this.select(nextIndex, { animate: true });
    }
  
    _resetAutoplay() {
      this._player.stop();
      this._player.start();
    }
  }
  
  if (!window.customElements.get('center-carousel')) {
    window.customElements.define('center-carousel', CenterCarousel);
  }





  /*------------------------------------*\
    #INTERACTION ELEMENTS
  \*------------------------------------*/

  /**
   * This replaces the existing button temporarily with a loading animation providing feedback to the user. Used for things such as Add to Cart.
   */
  var CustomButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    constructor() {
      super();
      if (this.type === "submit" && this.form) {
        this.form.addEventListener("submit", () => this.setAttribute("aria-busy", "true"));
      }

      this.append(this.contentElement, this.animationElement);

      // Ensure the animation isn't running on back/forward page cachae
      window.addEventListener("pageshow", () => this.removeAttribute("aria-busy"));
    }

    get contentElement() {
      if (this._contentElement) {
        return this._contentElement;
      }

      this._contentElement = document.createElement("div");
      this._contentElement.append(...this.childNodes);
      return this._contentElement = this._contentElement || document.createElement("div").append();
    }

    get animationElement() {
      return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
        <span class="button__loader">
          <span></span>
          <span></span>
          <span></span>
        </span>
      `).firstElementChild;
    }

    async attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === "true") {
        /**
         * We hide the content element, and show the animation element
         */
        Motion_timeline([
          [this.contentElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
          [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
        ]);

        /**
         * We run a complex loading animation which contains 3 dots which will essentially pulse.
         */
        Motion_animate(this.animationElement.children, { opacity: [1, 0.1] }, { duration: 0.35, delay: Motion_stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
      } else {
        /**
         * We hide the animation element and show the content element
         */
        Motion_timeline([
          [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
          [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
        ]);
      }
    }
  };

  if (!window.customElements.get("custom-button")) {
    window.customElements.define("custom-button", CustomButton, { extends: "button" });
  }



  /**
   * A standard Tabbing element. This primarily used within our SearchDrawer component. In most cases, areas that look like tabs are often using a combination of `EffectCarousel` and `PageDots` elements.
   */
  var Tabs = class extends HTMLElement {
    static get observedAttributes() {
      return ["selected-index"];
    }

    constructor() {
      super();
      if (!this.shadowRoot) {
        this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.buttons.indexOf(event.target));
      }
      this.addEventListener("keydown", this._handleKeyboard);
    }

    connectedCallback() {
      this._abortController = new AbortController();

      this.buttons = Array.from(this.shadowRoot.querySelector('slot[name="title"]').assignedNodes(), (item) => item.matches("button") && item || item.querySelector("button"));

      this.panels = Array.from(this.shadowRoot.querySelector('slot[name="content"]').assignedNodes());

      this.buttons.forEach((button, index) => button.addEventListener("click", () => this.selectedIndex = index, { signal: this._abortController.signal }));

      this.selectedIndex = this.selectedIndex;
      this._setupAccessibility();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
    }

    get selectedIndex() {
      return parseInt(this.getAttribute("selected-index")) || 0;
    }

    set selectedIndex(index) {
      this.setAttribute("selected-index", Math.min(Math.max(index, 0), this.buttons.length - 1).toString());

      this.style.setProperty("--selected-index", this.selectedIndex.toString());

      this.style.setProperty("--item-count", this.buttons.length.toString());
    }

    attributeChangedCallback(name, oldValue, newValue) {
      this.buttons.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));

      if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
        this._transition(this.panels[parseInt(oldValue)], this.panels[parseInt(newValue)]);
      }
    }

    _setupAccessibility() {
      const componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);

      this.buttons.forEach((button, index) => {
        button.setAttribute("role", "tab");
        button.setAttribute("aria-controls", `tab-panel-${componentID}-${index}`);
        button.id = `tab-${componentID}-${index}`;
      });

      this.panels.forEach((panel, index) => {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `tab-${componentID}-${index}`);
        panel.id = `tab-panel-${componentID}-${index}`;
        panel.hidden = index !== this.selectedIndex;
      });
    }

    /**
     * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
     * left and right arrow should switch the tab
     */
    _handleKeyboard(event) {
      const index = this.buttons.indexOf(document.activeElement);

      if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }

      if (event.key === "ArrowLeft") {
        this.selectedIndex = (this.selectedIndex - 1 + this.buttons.length) % this.buttons.length;
      } else {
        this.selectedIndex = (this.selectedIndex + 1 + this.buttons.length) % this.buttons.length;
      }

      this.buttons[this.selectedIndex].focus();
    }

    /**
     * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
     */
    async _transition(fromPanel, toPanel) {
      await Motion_animate(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
      fromPanel.hidden = true;
      toPanel.hidden = false;
      await Motion_animate(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
    }
  };

  if (!window.customElements.get("x-tabs")) {
    window.customElements.define("x-tabs", Tabs);
  }
  
  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }


  var AnimatedDetails = class extends HTMLDetailsElement {
    constructor() {
      super();
      this.summaryElement = this.firstElementChild;
      this.contentElement = this.lastElementChild;
      this._open = this.hasAttribute("open");

      this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
      
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }
    }

    get open() {
      return this._open;
    }

    _onSummaryClicked(event) {
      event.preventDefault();
      this.open = !this.open;
    }

    _transition(value) {
    }
  };


  var AccordionDisclosure = class extends AnimatedDetails {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this.setAttribute("aria-expanded", this._open ? "true" : "false");
    }

    set open(value) {
      super.open = value;
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return super.open;
    }

    async _transition(value) {
      const content = this.contentElement;

      if (!content) return;

      if (value) {
        this.setAttribute("open", "");

        const startHeight = 0;
        const endHeight = content.scrollHeight;

        content.style.overflow = "hidden";
        content.style.height = `${startHeight}px`;

        await Motion_timeline([
          [content, { height: [`${startHeight}px`, `${endHeight}px`] }, { duration: 0.25, easing: "ease" }],
          [content, { opacity: [0, 1], transform: ["translateY(0)", "translateY(-4px)"] }, { duration: 0.15, at: "-0.1" }]
        ]).finished;

        content.style.height = "";
        content.style.overflow = "";
      } else {
        const container = this;
        const summary = this.summaryElement;

        if (!summary) return;

        const startHeight = container.clientHeight;
        const endHeight = summary.clientHeight;

        container.style.overflow = "hidden";
        container.style.height = `${startHeight}px`;

        await Motion_timeline([
          [content, { opacity: 0 }, { duration: 0.15 }],
          [container, { height: [`${startHeight}px`, `${endHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;

        this.removeAttribute("open");

        container.style.height = "";
        container.style.overflow = "";
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open") {
        this.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
      }
    }
  };

  if (!window.customElements.get("accordion-disclosure")) {
    window.customElements.define("accordion-disclosure", AccordionDisclosure, { extends: "details" });
  }
  
  /**
   * A toggle button that allows us to open elements externally. E.g. Our Shop The Look dot buttons control opening of accordions.
   */
  var ToggleButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-expanded"];
    }

    constructor() {
      super();
      this.addEventListener("click", this._onButtonClick.bind(this));
    }

    connectedCallback() {
      document.addEventListener("openable-element:open", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
          event.stopPropagation();
        }
      });

      document.addEventListener("openable-element:close", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
          event.stopPropagation();
        }
      });
    }

    _onButtonClick(event) {
      if (!this.controlledElement) {
        return;
      }

      // This allows us to disable closing of the active element. E.g. on a Shop the Look section, if we have element 1 open, then clicking on dot 1 shouldn't close this.
      if (this.disableToggleIfOpen) {
        // If the current element is already active, we scroll to it
        if (this.isExpanded && this.controlledElement.scrollToActive) {
          this.controlledElement.scrollToActiveElement(this.controlledElement);
        } else {
          // If the element isn't active, we open it
          this.controlledElement.openElement({ event });
        }
      } else {
        if (this.isExpanded) {
          this.controlledElement.closeElement({ event });
        } else {
          this.controlledElement.openElement({ event });
        }
      }
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.getElementById(this.getAttribute("aria-controls"));
    }

    get disableToggleIfOpen() {
      return this.hasAttribute("disable-toggle-if-open");
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (!this.controlledElement) {
            return;
          }

          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
          break;
      }
    }
  };

  if (!window.customElements.get("toggle-button")) {
    window.customElements.define("toggle-button", ToggleButton, { extends: "button" });
  }

  /*------------------------------------*\
    #PRODUCT TAB PANELS
  \*------------------------------------*/

  /**
   * Tab content panels. On desktop only the selected panel is visible; on mobile all panels are shown as stacked blocks.
   */
  var ProductTabPanel = class extends HTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this._open = this.hasAttribute("open");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    connectedCallback() {
      _loadResourcesArticlesIfNeeded(this);
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }

      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return this.hasAttribute("open");
    }

    async _transition(value) {
      this.style.overflow = "hidden";

      if (value) {
        this.setAttribute("open", "");
        await Motion_timeline([
          [this, { height: [`${this.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: "ease"}]
        ]).finished;
        // Fixed height is required for the animation. We then set to auto height.
        this.style.height = "auto";
      } else {
        await Motion_timeline([
          [this, { height: [`${this.clientHeight}px`, `0px`] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;
        this.removeAttribute("open");
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open" && newValue === "") {
        this.setAttribute("aria-expanded", "true");
        _loadResourcesArticlesIfNeeded(this);
      } else {
        this.setAttribute("aria-expanded", "false");
      }
    }
    
  }

  if (!window.customElements.get("product-tab-panel")) {
    window.customElements.define("product-tab-panel", ProductTabPanel);
  }

  function _blogFetchUrl(blogHandle, tagHandle) {
    const base = `${window.Shopify.routes.root}blogs/${blogHandle}`;
    const tag = tagHandle && String(tagHandle).trim();
    return tag
      ? `${base}/tagged/${encodeURIComponent(tag)}?view=fetch&section_id=fetch-blog`
      : `${base}?view=fetch&section_id=fetch-blog`;
  }

  /**
   * Loads tagged blog posts into the resources tab (product page). Safe to call repeatedly; skips if already loaded.
   */
  function _loadResourcesArticlesIfNeeded(panel) {
    if (!panel || panel.id !== "resources-tab-content") {
      return;
    }

    const element = panel.querySelector(".resources__articles");
    if (!element || element.hasAttribute("blog-posts-loaded") || element.hasAttribute("blog-posts-loading")) {
      return;
    }

    const blogHandle = element.getAttribute("blog-handle") || "support-advice";
    const tagHandle = element.getAttribute("tag-handle") || undefined;
    const numberOfPosts = parseInt(element.getAttribute("number-of-posts"), 10);

    _fetchBlogPosts(blogHandle, tagHandle, element, numberOfPosts);
  }

  function _fetchBlogPosts(blogHandle, tagHandle, element, numberOfPosts) {
    if (!Shopify.designMode) {
      if (!blogHandle) {
        return;
      }
    }

    try {
      return _loadBlog(blogHandle, tagHandle, element, numberOfPosts);
    } catch (e) {
      if (e.name !== "AbortError") {
        throw e;
      }
    }
  }

  async function _loadBlog(blogHandle, tagHandle, element, numberOfPosts) {
    element.setAttribute("blog-posts-loading", "");

    try {
      const newElement = new DOMParser().parseFromString(await (await cachedFetch(_blogFetchUrl(blogHandle, tagHandle))).text(), "text/html");

      const blogPostsResults = element.querySelector(".blog-posts-list");
      const blogResultsChildren = newElement.querySelector(".blog-results").children;
      const buttonWrapper = element.querySelector(".blog-posts__button-wrapper");

      blogPostsResults.innerHTML = "";
      let i = 0;
      const postsLimit = numberOfPosts || blogResultsChildren.length;

      for (const child of blogResultsChildren) {
        if (i >= postsLimit) break;
        blogPostsResults.appendChild(document.importNode(child, true));
        i++;
      }

      buttonWrapper.style.opacity = "1";

      element.setAttribute("blog-posts-loaded", "");
    } finally {
      element.removeAttribute("blog-posts-loading");
    }
  }


  /**
   * Controls tab selection and visibility within the Product Tabs section.
   */
  var TabsNav = class extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button[aria-controls]"));

      this.buttons.forEach((button) => button.addEventListener("click", () => this.selectButton(button)));

      this.addEventListener("shopify:block:select", (event) => this.selectButton(event.target, !event.detail.load));
    }

    get selectedTabIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-expanded") === "true");
    }

    get selectedButton() {
      return this.buttons.find((button) => button.getAttribute("aria-expanded") === "true");
    }

    selectButton(button, animate = true) {
      if (!this.buttons.includes(button) || this.selectedButton === button) {
        return;
      }

      _loadResourcesArticlesIfNeeded(document.getElementById("resources-tab-content"));

      const from = document.getElementById(this.selectedButton.getAttribute("aria-controls"));
       
      const to = document.getElementById(button.getAttribute("aria-controls"));

      if (animate) {
        this._transitionContent(from, to);
      } else {
        from.hidden = true;
        to.hidden = false;
      }

      this.selectedButton.setAttribute("aria-expanded", "false");

      button.setAttribute("aria-expanded", "true");
    }

    async _transitionContent(from, to) {
      await Motion_animate(from, { opacity: [1, 0] }, { duration: 0.25 }).finished;
      from.hidden = true;
      to.hidden = false;
      await Motion_animate(to, { opacity: [0, 1] }, { duration: 0.25 }).finished;
    }
  };

  window.customElements.define("tabs-nav", TabsNav);


  /**
   * External links that scroll to a product tab: on desktop selects the tab and scrolls to the tabs area; on mobile scrolls to the relevant content block.
   */
  var ProductTabScroller = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._onButtonClick.bind(this));
    }

    connectedCallback() {
      this.tabsNav = document.querySelector("tabs-nav");
      this.tabButton = document.querySelector('[aria-controls="' + this.tabLink + '"');
      this.tabContent = document.getElementById(this.tabLink);
    }

    get tabLink() {
      return this.getAttribute("tab-link");
    }

    _onButtonClick(event) {
      event.preventDefault();
      var isMobile = window.matchMedia("(max-width: 999px)").matches;

      if (isMobile) {
        if (this.tabContent) {
          this.tabContent.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        if (this.tabButton && this.tabsNav) {
          this.tabsNav.selectButton(this.tabButton);
        }
        if (this.tabsNav) {
          this.tabsNav.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }

  if (!window.customElements.get("product-tab-scroller")) {
    window.customElements.define("product-tab-scroller", ProductTabScroller, { extends: "button" });
  }



  /*------------------------------------*\
    #DIALOG ELEMENTS
  \*------------------------------------*/

  class MegaMenuDisclosure extends AnimatedDetails {
    constructor() {
      super();
  
      this._detectClickOutsideListener = this._detectClickOutside.bind(this);
      this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
      this._detectFocusOutListener = this._detectFocusOut.bind(this);
      this._detectHoverListener = this._detectHover.bind(this);
      this._hoverTimer = null;
  
      // Hover detection
      this.addEventListener("mouseover", this._detectHoverListener);
      this.addEventListener("mouseout", this._detectHoverListener);
    }
  
    get trigger() {
      return !window.matchMedia("screen and (pointer: fine)").matches
        ? "click"
        : this.getAttribute("trigger");
    }
  
    get mouseOverDelayTolerance() {
      return 250;
    }
  
    _onSummaryClicked(event) {
      if (this.trigger === "hover") {
        event.preventDefault();
        if (event.currentTarget.hasAttribute("data-url")) {
          window.location.href = event.currentTarget.getAttribute("data-url");
        }
      } else {
        super._onSummaryClicked(event);
      }
    }
  
    async _transition(value) {
      const header = document.querySelector(".header");
      if (!header) return;
      const backdrop = header.querySelector(".header-backdrop");
  
      if (value) {
        this.setAttribute("open", "");
  
        document.addEventListener("click", this._detectClickOutsideListener);
        document.addEventListener("keydown", this._detectEscKeyboardListener);
        document.addEventListener("focusout", this._detectFocusOutListener);
  
        // Close siblings
        const openSiblings = Array.from(this.closest("ul").querySelectorAll("[open]")).filter(
          (item) => item !== this
        );
        openSiblings.forEach((sibling) => sibling.open = false);
  
        await this._transitionIn(backdrop);
      } else {
        document.removeEventListener("click", this._detectClickOutsideListener);
        document.removeEventListener("keydown", this._detectEscKeyboardListener);
        document.removeEventListener("focusout", this._detectFocusOutListener);
  
        await this._transitionOut(backdrop);
  
        this.removeAttribute("open");
      }
    }
  
    async _transitionIn(backdrop) {
      const header = document.querySelector(".header");
      const submenu = document.querySelector(".header-submenu");
      if (!header || !submenu || !backdrop) return;
  
      document.body.classList.add("menu-open");

      // Wait one frame so the newly opened details content has a stable layout before measuring.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const headerRect = header.getBoundingClientRect();
      const submenuRect = submenu.getBoundingClientRect();
      const contentRect = this.contentElement.getBoundingClientRect();
      const totalHeight = Math.max(submenuRect.bottom, contentRect.bottom) - headerRect.bottom;

      // Size the backdrop to the rendered bounds of the floating submenu + mega menu.
      backdrop.style.height = `${Math.ceil(totalHeight)}px`;
  
      // Animate opacity only
      await Promise.all([
        Motion_animate(
          backdrop,
          { opacity: [0, 1] },
          { duration: 0.3, easing: "ease-in-out" }
        ),
        Motion_animate(
          this.contentElement,
          { opacity: [0, 1] },
          { duration: 0.3, easing: "ease-in-out", delay: 0.1 }
        )
      ]);
    }
  
    async _transitionOut(backdrop) {
      const header = document.querySelector(".header");
      if (!header || !backdrop) return;
  
      // Check if any other disclosures are still open
      const openSiblings = Array.from(header.querySelectorAll("details[open]")).filter(
        (d) => d !== this
      );
  
      // Fade out mega menu
      await Motion_animate(
        this.contentElement,
        { opacity: [1, 0] },
        { duration: 0.3, easing: "ease-in-out" }
      );
  
      // Only fade out backdrop if no other menus open
      if (openSiblings.length === 0) {
        await Motion_animate(
          backdrop,
          { opacity: [1, 0] },
          { duration: 0.3, easing: "ease-in-out" }
        );
        backdrop.style.height = "0px";
        document.body.classList.remove("menu-open");
      }
    }
  
    _detectClickOutside(event) {
      if (this.trigger !== "click") return;
      if (!this.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
        this.open = false;
      }
    }
  
    _detectHover(event) {
      if (this.trigger !== "hover") return;
      if (event.type === "mouseover") {
        this.open = true;
        clearTimeout(this._hoverTimer);
      } else {
        this._hoverTimer = setTimeout(() => this.open = false, this.mouseOverDelayTolerance);
      }
    }
  
    _detectEscKeyboard(event) {
      if (event.code === "Escape") {
        const targetMenu = event.target.closest("details[open]");
        if (targetMenu) targetMenu.open = false;
      }
    }
  
    _detectFocusOut(event) {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.open = false;
      }
    }
  }
  
  // Register custom element if not already defined
  if (!window.customElements.get("mega-menu-disclosure")) {
    window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure, { extends: "details" });
  }
  

  var AccountMenuDisclosure = class extends AnimatedDetails {
    constructor() {
      super();
      this._detectClickOutsideListener = this._detectClickOutside.bind(this);
      this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
      this._detectFocusOutListener = this._detectFocusOut.bind(this);
      this._detectHoverListener = this._detectHover.bind(this);
      this._hoverTimer = null;
      this.addEventListener("mouseover", this._detectHoverListener.bind(this));
      this.addEventListener("mouseout", this._detectHoverListener.bind(this));
    }

    get trigger() {
      return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
    }

    get mouseOverDelayTolerance() {
      return 250;
    }

    /**
     * Summary click always toggles open/close. Hover still opens/closes on mouseover/mouseout;
     * do not follow data-url (login destinations live in the dropdown links).
     */
    _onSummaryClicked(event) {
      event.preventDefault();
      this.open = !this.open;
    }

    async _transition(value) {
      if (value) {
        const container = this.parentElement;
        if (container) {
          const openSiblings = Array.from(
            container.querySelectorAll('details[is="account-menu-disclosure"]')
          ).filter((item) => item !== this && item.open);

          for (const sibling of openSiblings) {
            sibling._open = false;
            await sibling._transition(false);
          }
        }

        this.setAttribute("open", "");
        document.addEventListener("click", this._detectClickOutsideListener);
        document.addEventListener("keydown", this._detectEscKeyboardListener);
        document.addEventListener("focusout", this._detectFocusOutListener);
        await this._transitionIn();
      } else {
        document.removeEventListener("click", this._detectClickOutsideListener);
        document.removeEventListener("keydown", this._detectEscKeyboardListener);
        document.removeEventListener("focusout", this._detectFocusOutListener);
        await this._transitionOut();
        this.removeAttribute("open");
      }
    }

    _transitionIn() {
      Motion_animate(
        this.contentElement, 
        { opacity: 1 },
        { duration: 0.3 }
      );
    }

    _transitionOut() {
      Motion_animate(
        this.contentElement, 
        { opacity: 0 },
        { duration: 0.3 }
      );
    }

    /**
     * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
     * close the navigation.
     */
    _detectClickOutside(event) {
      if (this.trigger !== "click") {
        return;
      }
      if (!this.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
        this.open = false;
      }
    }

    /**
     * On desktop, if the mode is set to hover, we open/close the dropdown on hover
     */
    _detectHover(event) {
      if (this.trigger !== "hover") {
        return;
      }
      if (event.type === "mouseover") {
        this.open = true;
        clearTimeout(this._hoverTimer);
      } else {
        this._hoverTimer = setTimeout(() => this.open = false, this.mouseOverDelayTolerance);
      }
    }

    /**
     * Detect if we hit the "Escape" key to automatically close the dropdown
     */
    _detectEscKeyboard(event) {
      if (event.code === "Escape") {
        const targetMenu = event.target.closest("details[open]");
        if (targetMenu) {
          targetMenu.open = false;
        }
      }
    }

    /**
     * Close the dropdown automatically when the dropdown is focused out
     */
    _detectFocusOut(event) {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.open = false;
      }
    }
  };

  if (!window.customElements.get("account-menu-disclosure")) {
    window.customElements.define("account-menu-disclosure", AccountMenuDisclosure, { extends: "details" });
  }

  var CloseButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
    }
  };

  if (!window.customElements.get("close-button")) {
    window.customElements.define("close-button", CloseButton, { extends: "button" });
  }


  /**
   * A base dialog element that powers drawers, modals, popovers etc.
   */
  var DialogElement = class extends HTMLElement {
    static get observedAttributes() {
      return ["id", "open"];
    }

    static #lockLayerCount = 0;
    #isLocked = false;

    constructor() {
      super();

      // Force closing of dialog and ensure not to propagate to other dialogs
      this.addEventListener("dialog:force-close", (event) => {
        this.hide();
        event.stopPropagation();
      });
    }

    get overlay() {
      return this.querySelector(".overlay-element");
    }

    connectedCallback() {
      // Check if we have an id element
      if (this.id) {
        // As these elements can be added/removed from the DOM, we need to run delegate.off() first before reattaching
        this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
      }

      if (this.overlay) {
        this.overlay.addEventListener("click", this.hide.bind(this));
      }
    
      this._abortController = new AbortController();

      // Accessibility
      this.setAttribute("role", "dialog");

      // Shopify theme editor events
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
        this.addEventListener("shopify:block:deselect", this.hide, { signal: this._abortController.signal });
        this._shopifySection = this._shopifySection || this.closest(".shopify-section");

        if (this._shopifySection) {
          if (this.hasAttribute("handle-section-events")) {
            this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
            this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this._abortController.signal });
          }

          this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this._abortController.signal });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
      this.delegate.off();
      this.focusTrap?.deactivate({ onDeactivate: false });

      if (this.#isLocked) {
        this.#isLocked = false;
        document.documentElement.classList.toggle("lock", --DialogElement.#lockLayerCount > 0);
      }
    }

    // animate11 is just a convenient variable flag rather than using `animate` which could overlap with other JS
    show(animate11 = true) {
      if (this.open) {
        return;
      }

      // If no animation, we set an attribute open attribute of immediate
      this.setAttribute("open", animate11 ? "" : "immediate");

      // Once show is complete, we dispatch this event
      return waitForEvent(this, "dialog:after-show");
    }

    hide() {
      if (!this.open) {
        return;
      }

      this.removeAttribute("open");

      // Once hide is complete, we dispatch this event
      return waitForEvent(this, "dialog:after-hide");
    }

    // Uses the Delegate library in vendor.min.js
    get delegate() {
      return this._delegate = this._delegate || new Delegate(document.body);
    }

    get controls() {
      return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get open() {
      return this.hasAttribute("open");
    }

    get shouldTrapFocus() {
      return true;
    }

    get shouldLock() {
      return false;
    }

    /**
     * Sometimes (especially for drawers) we need to ensure an element is on top of everything else. To do so,
     * we need to move the element to the body. We do this on open, and then restore the initial DOM position on
     * close.
     */
    get shouldAppendToBody() {
      return false;
    }

    // We can set an element to have focus on open
    get initialFocus() {
      return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
    }

    get preventScrollWhenTrapped() {
      return true;
    }

    // Uses the FocusTrap library in vendor.min.js. This handles a lot of accessibility needs.
    get focusTrap() {
      return this._focusTrap = this._focusTrap || new FocusTrap.createFocusTrap(this, {
        onDeactivate: this.hide.bind(this),
        allowOutsideClick: (event) => this._isDelegatedAriaControlsTrigger(event),
        clickOutsideDeactivates: (event) => !this._isDelegatedAriaControlsTrigger(event),
        initialFocus: window.matchMedia("screen and (pointer: fine)").matches ? this.initialFocus : false,
        fallbackFocus: this,
        preventScroll: this.preventScrollWhenTrapped
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "id":
          // If id changes, we reset our main event listener
          if (newValue) {
            this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
          }
          break;
        case "open":
          // Set our aria on our controlling elements
          this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));

          if (oldValue === null && (newValue === "" || newValue === "immediate")) {
            // If we open

            // Remove inert so the element can be interacted with
            this.removeAttribute("inert");

            // If we append to body, we run this code to get the parent so we can return the element there on close.
            this._originalParentBeforeAppend = null;

            if (this.shouldAppendToBody && this.parentElement !== document.body) {
              this._originalParentBeforeAppend = this.parentElement;
              document.body.append(this);
            }

            // If we have an animation we dispatch an event
            const showTransitionPromise = this._showTransition(newValue !== "immediate") || Promise.resolve();

            // We ensure to dispatch the event once any animation is complete
            showTransitionPromise.then(() => {
              this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
            });

            // Ensure that any transition is complete before trapping focus
            if (this.shouldTrapFocus) {
              this.focusTrap.activate({
                checkCanFocusTrap: () => showTransitionPromise
              });
            }

            // Locks the body element from scrolling
            if (this.shouldLock) {
              DialogElement.#lockLayerCount += 1;
              this.#isLocked = true;
              document.documentElement.classList.add("lock");
            }
          } else if (oldValue !== null && newValue === null) {
            // If we close

            // Ensure the element cannot be interacted with
            this.setAttribute("inert", "");

            // Run hide transition
            const hideTransitionPromise = this._hideTransition() || Promise.resolve();

            hideTransitionPromise.then(() => {
              // Return element back to it's parent if it was appended
              if (this.parentElement === document.body && this._originalParentBeforeAppend) {
                this._originalParentBeforeAppend.appendChild(this);
                this._originalParentBeforeAppend = null;
              }

              // Dispatch event after transition ends
              this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
            });

            // Deactivates focus once transition has ended
            this.focusTrap?.deactivate({
              checkCanReturnFocus: () => hideTransitionPromise
            });

            // Removes scroll locking from body
            if (this.shouldLock) {
              this.#isLocked = false;
              document.documentElement.classList.toggle("lock", --DialogElement.#lockLayerCount > 0);
            }
          }
          this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
          break;
      }
    }

    /* Those methods are used to perform custom show/hide transition, and must return a promise */
    _showTransition(animate11 = true) {
    }

    _hideTransition() {
    }

    /**
     * Matches the delegated toggle selector in connectedCallback (`[aria-controls="${this.id}"]`).
     * Those clicks must bypass focus-trap's outside-pointer path: it deactivates on mousedown, so the
     * dialog closes before click; the same gesture then hits _onToggleClicked and opens again.
     */
    _isDelegatedAriaControlsTrigger(event) {
      if (!this.id || !event.target?.closest) {
        return false;
      }
      const control = event.target.closest("[aria-controls]");
      return control?.getAttribute("aria-controls") === this.id;
    }

    _onToggleClicked(event) {
      event?.preventDefault();
      this.open ? this.hide() : this.show();
    }

  };

  if (!window.customElements.get("dialog-element")) {
    window.customElements.define("dialog-element", DialogElement);
  }


  /**
   * A modal is an impactful popup that traditionally opens in the centre of the screen. We use this for our product page tooltips.
   */
  var Modal = class extends DialogElement {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();
      this.setAttribute("aria-modal", "true");
    }

    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    _showTransition(animate11 = true) {
      let animationControls;
      animationControls = Motion_timeline([[this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }]])

      animate11 ? animationControls.play() : animationControls.finish();
    }

    _hideTransition() {
      let animationControls;

      animationControls = Motion_timeline([[this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]])

      return animationControls.finished;
    }
  }

  if (!window.customElements.get("x-modal")) {
    window.customElements.define("x-modal", Modal);
  }


  /**
   * A drawer element traditionally slides in or out of view. Used for our mobile menu, search drawer, and cart drawer.
   */
  var reduceDrawerAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches || JSON.parse("false");

  var Drawer = class extends DialogElement {
    constructor() {
      super();
    }

    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    get openFrom() {
      if (window.matchMedia("(max-width: 999px)").matches) {
        return this.getAttribute("mobile-open-from") || this.getAttribute("open-from") || "right";
      }
      return this.getAttribute("open-from") || "right";
    }

    connectedCallback() {
      super.connectedCallback?.();
      this.addEventListener("click", this._handleAnchorClick.bind(this));
    }
  
    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.removeEventListener("click", this._handleAnchorClick.bind(this));
    }
  
    _handleAnchorClick(event) {
      const anchor = event.target.closest("a");
      if (anchor && anchor.getAttribute("href")?.startsWith("#")) {
        this.hide();
      }
    }

    _getClipPathProperties() {
      switch (this.openFrom) {
        case "left":
          return ["inset(0 100% 0 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
        case "right":
          return ["inset(0 0 0 100% round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md)"];
        case "bottom":
          return ["inset(100% 0 0 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
        case "top":
          return ["inset(0 0 100% 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
      }
    }

    _setInitialPosition() {
      this.style.left = this.openFrom === "left" ? "0px" : null;
      this.style.right = this.style.left === "" ? "0px" : null;
      this.style.bottom = this.openFrom === "bottom" ? "0px" : null;
      this.style.top = this.style.bottom === "" ? "0px" : null;
    }

    _showTransition(animate11 = true) {
      this._setInitialPosition();
    
      const content = this.querySelector(".drawer__content");
      const closeButton = this.querySelector(".outside-close-button");
      const returnButton = this.querySelector(".mobile-nav__return");
    
      let animationControls;
    
      if (reduceDrawerAnimation) {
        animationControls = Motion_animate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 });
      } else {
        // Timeline keeps the older sequence:
        animationControls = Motion_timeline([
          // 1. Drawer fades in
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
    
          // 2. Drawer content slides in
          [content, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
    
          // 3. Content children fade in
          [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
    
          // 4. Close button fades in
          [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }],
    
          // 5. Return button fades in only if not hidden
          returnButton && !returnButton.hidden ? [
            returnButton,
            { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] },
            { at: "<", duration: 0.25, easing: "ease" }
          ] : null
        ].filter(Boolean));
      }
    
      animate11 ? animationControls.play() : animationControls.finish();
    
      return animationControls.finished.then(() => this.classList.add("show-close-cursor"));
    }
    
    _hideTransition() {
      let animationControls;
    
      if (reduceDrawerAnimation) {
        animationControls = Motion_animate(this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.2 });
      } else {
        const content = this.querySelector(".drawer__content");
        const closeButton = this.querySelector(".outside-close-button");
        const returnButton = this.querySelector(".mobile-nav__return"); // add the return button
    
        animationControls = Motion_timeline([
          // Fade out return button immediately
          returnButton ? [
            returnButton,
            { opacity: [null, 0], transform: ["translateY(0px)", "translateY(10px)"] },
            { duration: 0.15 }
          ] : null,
    
          // Fade out close button
          closeButton ? [
            closeButton,
            { opacity: [null, 0] },
            { duration: 0.15, at: "<" } // run at the same time as return button
          ] : null,
    
          // Fade out panel content
          [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
    
          // Animate the drawer clip path
          [content, { clipPath: this._getClipPathProperties().reverse() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
    
          // Fade out drawer itself
          [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
        ].filter(Boolean)); // filter out null entries
      }
    
      return animationControls.finished.then(() => this.classList.remove("show-close-cursor"));
    }
  };

  if (!window.customElements.get("x-drawer")) {
    window.customElements.define("x-drawer", Drawer);
  }

  /**
   * The Popover and Listbox elements work together. These power a less intrusive dialog element. We use this for sort by and variant selectors.
   */
  var Popover = class extends DialogElement {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));

      if (this.hasAttribute("close-on-listbox-select")) {
        this.addEventListener("listbox:select", this.hide, { signal: this._abortController.signal });
      }
    }

    get shouldLock() {
      return window.matchMedia("screen and (max-width: 999px)").matches;
    }

    get shouldAppendToBody() {
      return window.matchMedia("screen and (max-width: 999px)").matches;
    }

    get anchor() {
      return {
        vertical: this.getAttribute("anchor-vertical") || "start",
        horizontal: this.getAttribute("anchor-horizontal") || "end"
      };
    }

    _showTransition(animate11 = true) {
      let animationControls; 
      let content = this.querySelector(".popover__content");
      let closeButton = this.querySelector("outside-close-button");

      this.style.display = "block";

      if (window.matchMedia("screen and (max-width: 999px)").matches) {
        this.style.left = "0px";
        this.style.right = null;
        this.style.bottom = "0px";
        this.style.top = null;
        animationControls = Motion_timeline([
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
          [content, { clipPath: ["inset(100% 0 0 0 round 8px)", "inset(0 0 0 0 round 8px"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
        ]);
      } else {
        let spacingBlockValue = "var(--popover-anchor-block-spacing)", spacingInlineValue = "var(--popover-anchor-inline-spacing)";
        this.style.left = this.anchor.horizontal === "start" ? spacingInlineValue : null;
        this.style.right = this.anchor.horizontal === "end" ? spacingInlineValue : null;

        if (this.anchor.vertical === "center") {
          this.style.top = `calc(50% - ${parseInt(this.clientHeight / 2)}px)`;
          this.style.bottom = null;
        } else {
          this.style.top = this.anchor.vertical === "end" ? `calc(100% + ${spacingBlockValue})` : null;
          this.style.bottom = this.anchor.vertical === "start" ? `calc(100% + ${spacingBlockValue})` : null;
        }

        animationControls = Motion_timeline([
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
          [content, { clipPath: "none" }, { at: "<", duration: 0 }]
        ]);
      }

      animate11 ? animationControls.play() : animationControls.finish();
      return animationControls.finished;
    }

    _hideTransition() {
      let animationControls;
      if (window.matchMedia("screen and (max-width: 999px)").matches) {
        let content = this.querySelector(".popover__content");
        let closeButton = this.querySelector("outside-close-button");

        animationControls = Motion_timeline([
          [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
          [content, { clipPath: [null, "inset(100% 0 0 0 round 8px)"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
        ]);
      } else {
        animationControls = Motion_timeline(this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 });
      }

      return animationControls.finished.then(() => this.style.display = "none");
    }
  };

  if (!window.customElements.get("x-popover")) {
    window.customElements.define("x-popover", Popover);
  }

  var Listbox = class extends HTMLElement {
    constructor() {
      super();
    }

    static get observedAttributes() {
      return ["aria-activedescendant"];
    }

    connectedCallback() {
      this._accessibilityInitialized = false;
      this._delegate = new Delegate();
      this._hiddenInput = this.querySelector('input[type="hidden"]');
      this.addEventListener("keydown", this._onKeyDown.bind(this));

      if (!this._accessibilityInitialized) {
        this.setAttribute("role", "listbox");
        this._hiddenInput?.addEventListener("change", this._onInputChanged.bind(this));
        Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
          option.addEventListener("click", this._onOptionClicked.bind(this));
          option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));

          if (option.getAttribute("aria-selected") === "true") {
            this.setAttribute("aria-activedescendant", option.id);
          }
        });

        this._accessibilityInitialized = true;
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
        Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
          if (option.id === newValue) {
            option.setAttribute("aria-selected", "true");

            if (this._hiddenInput && this._hiddenInput.value !== option.value) {
              this._hiddenInput = option.value;
              this._hiddenInput.dispatchEvent(new Event("change"), { bubbles: true });
            }

            if (this.hasAttribute("aria-owns")) {
              this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
                document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
              });
            }

            option.dispatchEvent(new CustomEvent("listbox:change", {
              bubbles: true,
              detail: {
                value: option.value
              }
            }));
          } else {
            option.setAttribute("aria-selected", "false");
          }
        });
      }
    }

    _onOptionClicked(event) {
      this.setAttribute("aria-activedescendant", event.currentTarget.id);
      event.currentTarget.dispatchEvent(new CustomEvent("listbox:select", {
        bubbles: true,
        detail: {
          value: event.currentTarget.value
        }
      }));
    }

    _onInputChanged(event) {
      this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
    }

    _onKeyDown(event) {
      if (event.key === "ArrowUp") {
        event.target.previousElementSibling?.focus();
        event.preventDefault();
      } else if (event.key === "ArrowDown") {
        event.target.nextElementSibling?.focus();
        event.preventDefault();
      }
    }
  }

  if (!window.customElements.get("x-listbox")) {
    window.customElements.define("x-listbox", Listbox);
  }


  /**
   * Mobile menu drawer. This has some complex functionality opening various panels.
   */
  var NavigationDrawer = class extends Drawer {
    constructor() {
      super();

      const delegate = new Delegate(this);

      delegate.on("click", "[data-target-panel]", this._onForwardNav.bind(this));
      delegate.on("click", ".mobile-nav__return", this._onBackNav.bind(this));

      // Panel map and history
      this.panels = [];
      this.panelMap = new Map();
      this.panelHistory = ["root"];
      this._currentPanelId = "root"; // keeps track of which panel is currently visible

      this.addEventListener("dialog:after-hide", () => {
        this.reinitializeDrawer();
        this.panelHistory = ["root"];
        this._currentPanelId = "root";
        this.updateReturnButton();
      });
      
    }

    connectedCallback() {
      super.connectedCallback();

      // build map of panel-id => index
      this.panels = Array.from(this.querySelectorAll(".panel"));
      this.panelMap = new Map(
        this.panels.map((panel, index) => [panel.dataset.panelId, index])
      );

      // show/hide return button appropriately
      this.updateReturnButton();
    }

    _onForwardNav(event, target) {
      const panelId = target.dataset.targetPanel;
      if (!panelId || !this.panelMap.has(panelId)) return;

      const previousPanelId = this._currentPanelId;
      const panelIndex = this.panelMap.get(panelId);
      const panelToHideIndex = this.panelMap.get(previousPanelId);

      this.switchToPanel(panelIndex, parseInt(target.dataset.linkIndex), panelToHideIndex, "forward");

      this.panelHistory.push(previousPanelId);
      this._currentPanelId = panelId;

      this.updateReturnButton(previousPanelId);
    }

    _onBackNav() {
      if (this.panelHistory.length === 0) return; 

      const previousPanelId = this.panelHistory.pop();
      const panelIndex = this.panelMap.get(previousPanelId);
      const panelToHideIndex = this.panelMap.get(this._currentPanelId);

      this.switchToPanel(panelIndex, null, panelToHideIndex, "back");

      const oldPanelId = this._currentPanelId;
      this._currentPanelId = previousPanelId;

      this.updateReturnButton(oldPanelId);
    }

    updateReturnButton(previousPanelId = null) {
      const returnButton = this.querySelector(".mobile-nav__return");
      const closeButton = this.querySelector(".outside-close-button");
      if (!returnButton || !closeButton) return;

      if (!this.open) {
        returnButton.style.opacity = "0";
        returnButton.style.transform = "translateY(10px)";
        returnButton.hidden = true;
        return;
      }

      const atRoot = this._currentPanelId === "root";
      const fromRoot = previousPanelId === "root";

      // Animate only when moving from/to root
      if (fromRoot || atRoot) {
        if (!atRoot) returnButton.hidden = false;

        Motion_timeline([
          [
            returnButton,
            {
              opacity: atRoot ? [1, 0] : [0, 1],
              transform: atRoot
                ? ["translateY(0px)", "translateY(10px)"]
                : ["translateY(10px)", "translateY(0px)"],
            },
            { duration: 0.25, easing: "ease" },
          ],
          [
            closeButton,
            {
              left: atRoot ? "50%" : "auto",
              right: atRoot ? "auto" : "24px",
              transform: atRoot ? "translateX(-50%)" : "none",
            },
            { duration: 0.25, easing: "ease", at: "<" },
          ],
        ]).finished.then(() => {
          if (atRoot) returnButton.hidden = true;
        });
      } else {
        // Deep levels: keep static
        returnButton.style.opacity = "1";
        returnButton.style.transform = "translateY(0px)";
        returnButton.hidden = false;

        closeButton.style.left = "auto";
        closeButton.style.right = "40px";
        closeButton.style.transform = "none";
      }
    }
    
    switchToPanel(panelIndex, linkListIndex = null, panelToHideIndex = null, direction = "forward") {
      const panels = this.panels; // already cached in connectedCallback
      const panelToShow = panels[panelIndex];
      const panelToHide = panelToHideIndex !== null ? panels[panelToHideIndex] : null;
    
      // Determine panel movement
      const offset = direction === "forward" ? 100 : -100;
      const panelHideAnim = panelToHide
        ? { transform: [`translateX(0%)`, `translateX(${ -offset }%)`], opacity: [1, 0], visibility: ["visible", "hidden"] }
        : null;
    
      const panelShowAnim = {
        transform: [`translateX(${ offset }%)`, "translateX(0%)"],
        opacity: [0, 1],
        visibility: ["hidden", "visible"]
      };
    
      const timeline = [];
    
      // Hide previous panel
      if (panelToHide) timeline.push([panelToHide, panelHideAnim, { duration: 0.3 }]);
    
      // Show new panel
      timeline.push([panelToShow, panelShowAnim, { at: "<", duration: 0.3 }]);
    
      // Handle link list inside panel (if any)
      if (linkListIndex !== null) {
        const linkLists = panelToShow.querySelectorAll(".panel__wrapper");
        // hide all lists first
        Array.from(linkLists).forEach(list => list.setAttribute("hidden", ""));
        // show the selected list
        linkLists[linkListIndex]?.removeAttribute("hidden");
    
        timeline.push([
          linkLists[linkListIndex]?.querySelectorAll("li"),
          { opacity: [0, 1], visibility: ["hidden", "visible"] },
          { easing: "ease", duration: 0.2, at: "-0.15", delay: 0.025 }
        ]);
      }
    
      Motion_timeline(timeline);
    }
    

    // Reset navigation drawer to initial state
    reinitializeDrawer() {
      const firstPanel = this.querySelector(".panel:first-child");
      firstPanel.style.opacity = "1";
      firstPanel.style.visibility = "visible";
      firstPanel.style.transform = "translateX(0%)";

      Array.from(this.querySelectorAll(".panel:not(:first-child)")).forEach((item) => {
        item.style.opacity = "0";
        item.style.visibility = "hidden";
        Array.from(item.querySelectorAll(".panel__wrapper")).forEach((list) => list.setAttribute("hidden", ""));
      });
    }

    setExpanded(target) {
      Array.from(this.querySelectorAll("button[data-panel]")).forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });

      if (target) {
        target.setAttribute("aria-expanded", "true");
      }
    }
  }

  if (!window.customElements.get("navigation-drawer")) {
    window.customElements.define("navigation-drawer", NavigationDrawer);
  }



  /*------------------------------------*\
    #SEARCH
  \*------------------------------------*/

  var PredictiveSearch = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="idle"></slot>`));
    }
  
    connectedCallback() {
      this._searchForm = this.closest("form");
      this._queryInput = this._searchForm.elements["q"];
      this._searchForm.addEventListener("submit", this._onFormSubmitted.bind(this));
      this._searchForm.addEventListener("reset", this._onSearchCleared.bind(this));
      this._queryInput.addEventListener("input", debounce(this._onInputChanged.bind(this), this.autoCompleteDelay));
    }
  
    /**
     * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
     * refresh too often, so we recommend to keep the default one
     */
    get autoCompleteDelay() {
      return 280;
    }

    /**
     * On the Support and Help centre blogs, we need an internal search. Shopify doesn't allow for searching within a specific blog, so we have to search via tag e.g. `tag:blog_Support` or `tag:blog_Help`.
     */
    get limitSearchTag() {
      return this.getAttribute("limit-search-tag");
    }

    get requestedResourceTypes() {
      return this.getAttribute("resources-types");
    }

    get fallbackDisabled() {
      return this.hasAttribute("fallback-disabled");
    }
  
    /**
     * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
     * search is simply disabled and only the standard search is used
     */
    supportsPredictiveApi() {
      return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
    }
  
    /**
     * Check if the input is not empty, and if so, trigger the predictive search
     */
    _onInputChanged() {
      if (this._queryInput.value === "") {
        return this._onSearchCleared();
      }
  
      this._abortController?.abort();
      this._abortController = new AbortController();
  
      try {
        if (!this.supportsPredictiveApi() && this.fallbackDisabled) {
          return this._transitionToSlot("idle");
        }
        return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }
  
    /**
     * Prevent the form submission if the query is empty
     */
    _onFormSubmitted(event) {
      if (this._queryInput.value === "") {
        return event.preventDefault();
      }
    }
  
    /**
     * Do the actual predictive search
     */
    async _doPredictiveSearch() {
      await this._transitionToSlot("loading");

      let queryValue = this._queryInput.value;

      if (this.limitSearchTag) {
        queryValue = `${this.limitSearchTag}` + " " + `${this._queryInput.value}`;
      }

      const resourceTypesParam = this.requestedResourceTypes ? `&resources[type]=${encodeURIComponent(this.requestedResourceTypes)}` : "";
      const queryParams = `q=${encodeURIComponent(queryValue)}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each${resourceTypesParam}`;
  
      const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search/suggest?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
  
      this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
  
      return this._transitionToSlot("results");
    }
  
    /**
     * For merchants using store that do not support the predictive search, we have to fallback to standard search. Unfortunately,
     * the standard search is less convenient, and we have to simulate one request for each desired resource type
     */
    async _doFallbackSearch() {
      await this._transitionToSlot("loading");

      if (this.fallbackDisabled) {
        return this._transitionToSlot("idle");
      }
      
      let queryValue = this._queryInput.value;

      if (this.limitSearchTag) {
        queryValue = `${this.limitSearchTag}` + " " + `${this._queryInput.value}`;
      }

      const queryParams = `q=${queryValue}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
      const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
      this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
      return this._transitionToSlot("results");
    }
  
    /**
     * If any search is pending, we abort them, and transition to the idle slot
     */
    _onSearchCleared() {
      this._abortController?.abort();
      this._queryInput.focus();
      return this._transitionToSlot("idle");
    }
  
    /**
     * Transition between different slot. To prevent useless animation, the slot does not transition if we go to the same slot
     */
    async _transitionToSlot(toSlotName) {
      if (this.shadowRoot.firstElementChild.name === toSlotName) {
        return;
      }
  
      let assignedNodes = this.shadowRoot.firstElementChild.assignedNodes();
  
      if (assignedNodes.length === 0) {
        this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
        return;
      } else {
        await Motion_animate(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [1, 0] }, { duration: 0.1 }).finished;
        this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
        return Motion_animate(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [0, 1], transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.1 }).finished;
      }
    }
  };
  
  if (!window.customElements.get("predictive-search")) {
    window.customElements.define("predictive-search", PredictiveSearch);
  }
  

  var SearchDrawer = class extends Drawer {
    get shouldAppendToBody() {
      return false;
    }
  };

  if (!window.customElements.get("search-drawer")) {
    window.customElements.define("search-drawer", SearchDrawer);
  }

  var SearchPopup = class extends DialogElement {
    connectedCallback() {
      super.connectedCallback();
      this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearch = this.querySelector("predictive-search");
      this.addEventListener("dialog:after-hide", this._resetState.bind(this), { signal: this._abortController.signal });
    }

    _showTransition(animate11 = true) {
      const panel = this.querySelector(".search-popup__panel");
      const formChildren = panel?.children || [];
      let animationControls = Motion_timeline([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [panel, { clipPath: ["inset(0 0 0 100% round 0px)", "inset(0 0 0 0 round 0px)"] }, { at: "<", duration: 0.35, easing: [0.86, 0, 0.07, 1] }],
        [formChildren, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);

      animate11 ? animationControls.play() : animationControls.finish();

      return animationControls.finished.then(() => this.input?.focus());
    }

    _hideTransition() {
      const panel = this.querySelector(".search-popup__panel");

      if (!panel) {
        return Motion_animate(this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }).finished;
      }

      return Motion_timeline([
        [panel, { clipPath: ["inset(0 0 0 0 round 0px)", "inset(0 0 0 100% round 0px)"] }, { duration: 0.35, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.12 }]
      ]).finished;
    }

    _resetState() {
      if (this.input) {
        this.input.value = "";
      }

      this.predictiveSearch?._abortController?.abort();
      this.predictiveSearch?._transitionToSlot("idle");
    }
  };

  if (!window.customElements.get("search-popup")) {
    window.customElements.define("search-popup", SearchPopup);
  }

  var MainSearch = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.searchForm = this.querySelector(".main-search__form");
      if (!this.searchForm) {
        return;
      }

      this.searchInput = this.searchForm.querySelector('input[name="search-term"]');
      this.searchTypeInput = this.searchForm.querySelector('input[name="type"]');
      this.queryInput = this.searchForm.querySelector('input[name="q"]');
      this.searchTag = this.searchForm.dataset.searchTag || "";
      this.sectionId = this.getAttribute("section-id");
      this.shopifySection = this.closest(".shopify-section");

      document.addEventListener("search:switch", this.searchSwitch.bind(this));
      this.searchForm.addEventListener("submit", this._onFormSubmit.bind(this));
    }

    async searchSwitch(event) {
      let url;
      this.shopifySection.classList.add("is-loading");
      const queryValue = this._buildScopedQuery(this.searchInput.value);

      if (event.detail.resource == "article") {
        url = `q=${queryValue}&type=article`;
      } else {
        url = `q=${queryValue}&type=product`;
      }

      window.location = `${window.Shopify.routes.root}search?${url}`;
    }

    _onFormSubmit() {
      if (!this.queryInput || !this.searchInput) {
        return;
      }

      this.queryInput.value = this._buildScopedQuery(this.searchInput.value);

      if (this.searchTypeInput) {
        this.searchTypeInput.value = this.searchTag ? "article" : "product";
      }
    }

    _buildScopedQuery(queryValue) {
      const trimmedQuery = queryValue.trim();

      if (!this.searchTag) {
        return trimmedQuery;
      }

      return [trimmedQuery, this.searchTag].filter(Boolean).join(" ");
    }
  }

  if (!window.customElements.get("main-search")) {
    window.customElements.define("main-search", MainSearch);
  }




  /*------------------------------------*\
    #CART
  \*------------------------------------*/

  var createCartPromise = () => {
    return new Promise(async (resolve) => {
      resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
    });
  };

  var fetchCart = createCartPromise();
  document.addEventListener("cart:change", (event) => {
    fetchCart = event.detail["cart"];
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      fetchCart = createCartPromise();
    }
  });
  
  var CartCount = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<span><slot></slot></span>"));
    }

    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: this._abortController.signal });

      document.addEventListener("cart:refresh", this._updateFromServer.bind(this), { signal: this._abortController.signal });
      window.addEventListener("pageshow", this._updateFromServer.bind(this));
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    async _updateFromServer() {
      this.itemCount = (await fetchCart)["item_count"];
    }

    get itemCount() {
      return parseInt(this.innerText);
    }

    set itemCount(count) {
      if (this.itemCount === count) {
        return;
      }

      if (count === 0) {
        Motion_animate(this, { opacity: 0 }, { duration: 0.1 });
        this.innerText = count;
      } else if (this.itemCount === 0) {
        Motion_animate(this, { opacity: 1 }, { duration: 0.1 });
        this.innerText = count;
      } else {
        (async () => {
          await Motion_animate(this.shadowRoot.firstElementChild, { transform: ["translateY(-50%)"], opacity: 0 }, { duration: 0.25, easing: [1, 0, 0, 1] }).finished;
          this.innerText = count;
          Motion_animate(this.shadowRoot.firstElementChild, { transform: ["translateY(50%)", "translateY(0)"], opacity: 1 }, { duration: 0.25, easing: [1, 0, 0, 1] });
        })();
      }
    }
  };
  
  if (!window.customElements.get("cart-count")) {
    window.customElements.define("cart-count", CartCount);
  }

  var CartDrawer = class extends Drawer {
    constructor() {
      super();
      this._onPrepareBundledSectionsListener = this._onPrepareBundledSections.bind(this);
      this._onCartChangedListener = this._onCartChanged.bind(this);
      this._onCartRefreshListener = this._onCartRefresh.bind(this);
      this._onVariantAddedListener = this._onVariantAdded.bind(this);
      window.addEventListener("pageshow", this._onPageShow.bind(this));
    }

    connectedCallback() {
      super.connectedCallback();
      document.addEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
      document.addEventListener("cart:change", this._onCartChangedListener);
      document.addEventListener("cart:refresh", this._onCartRefreshListener);
      document.addEventListener("variant:add", this._onVariantAddedListener);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
      document.removeEventListener("cart:change", this._onCartChangedListener);
      document.removeEventListener("cart:refresh", this._onCartRefreshListener);
      document.removeEventListener("variant:add", this._onVariantAddedListener);
    }

    get shouldAppendToBody() {
      return false;
    }

    _onPrepareBundledSections(event) {
      event.detail.sections.push(extractSectionId(this));
    }

    /**
     * Update the cart drawer content when cart content changes.
     */
    async _onCartChanged(event) {
      const updatedDrawerContent = new DOMParser().parseFromString(event.detail.cart["sections"][extractSectionId(this)], "text/html");

      if (event.detail.cart["item_count"] > 0) {
        const currentInner = this.querySelector(".cart-drawer__inner"); 
        const updatedInner = updatedDrawerContent.querySelector(".cart-drawer__inner");

        if (!currentInner) {
          this.replaceChildren(document.createRange().createContextualFragment(updatedDrawerContent.querySelector(".cart-drawer").innerHTML));
        } else {
          setTimeout(() => {
            currentInner.innerHTML = updatedInner.innerHTML;
          }, event.detail.baseEvent === "variant:add" ? 0 : 1250);

          this.querySelector('[slot="footer"]').replaceChildren(...updatedDrawerContent.querySelector('[slot="footer"]').childNodes);
        }
      } else {
        await Motion_animate(this.children, { opacity: 0 }, { duration: 0.15 }).finished;

        this.replaceChildren(...updatedDrawerContent.querySelector(".cart-drawer").childNodes);

        Motion_animate(this.querySelector(".empty-state"), { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { duration: 0.15 });
      }
    }

    /**
     * Handle the case when the page is served from BF cache
     */
    _onPageShow(event) {
      if (!event.persisted) {
        return;
      }
      this._onCartRefresh();
    }

    /**
     * Listeners called when a new variant has been added
     */
    _onVariantAdded(event) {
      if (window.themeVariables.settings.cartType !== "drawer" || event.detail?.blockCartDrawerOpening) {
        return;
      }
      this.show();
    }

    /**
     * Force a complete refresh of the cart drawer (this is called by dispatching the 'cart:refresh' on the document)
     */
    async _onCartRefresh() {
      const tempDiv = document.createElement("div");

      tempDiv.innerHTML = await (await fetch(`${window.Shopify.routes.root}?section_id=${extractSectionId(this)}`)).text();

      this.replaceChildren(...tempDiv.querySelector("#cart-drawer").children);
    }
  };

  if (!window.customElements.get("cart-drawer")) {
    window.customElements.define("cart-drawer", CartDrawer);
  }

  var LineItem = class extends HTMLElement {
    connectedCallback() {
      this.pillLoaderElement = this.querySelector("pill-loader");
      this.addEventListener("line-item:will-change", this._onWillChange.bind(this));
      this.addEventListener("line-item:change", this._onChanged.bind(this));
    }

    _onWillChange() {
      this.pillLoaderElement.setAttribute("aria-busy", "true");
    }

    async _onChanged(event) {
      this.pillLoaderElement.removeAttribute("aria-busy");
      if (event.detail.cart["item_count"] === 0 || event.detail.quantity !== 0) {
        return;
      }

      let marginCompensation = 0;
      if (this.nextElementSibling) {
        marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
      }

      await Motion_animate(this, { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: "hidden", opacity: [1, 0] }, { duration: 0.2, easing: "ease" }).finished;
      this.remove();
    }
  };

  if (!window.customElements.get("line-item")) {
    window.customElements.define("line-item", LineItem);
  }

  var LineItemQuantity = class extends HTMLElement {
    constructor() {
      super();
      this.delegate = new Delegate(this);
      this.delegate.on("change", "[data-line-key]", this._onQuantityChanged.bind(this));
      this.delegate.on("click", '[href*="/cart/change"]', this._onChangeLinkClicked.bind(this));
    }

    _onQuantityChanged(event, target) {
      if (parseInt(target.value) > parseInt(target.getAttribute("max"))) {
        target.value = parseInt(target.getAttribute("value"));
        alert(window.themeVariables.strings.cartQuantityCannotAddMore);
        return;
      } else {
        this._changeLineItemQuantity(target.getAttribute("data-line-key"), parseInt(target.value));
      }
    }

    _onChangeLinkClicked(event, target) {
      event.preventDefault();
      const url = new URL(target.href);
      this._changeLineItemQuantity(url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
    }

    async _changeLineItemQuantity(lineKey, targetQuantity) {
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=${targetQuantity}`;
      } else {
        const lineItem = this.closest("line-item");

        lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

        const cartContent = await (await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: targetQuantity,
            sections: sectionsToBundle
          })
        })).json();

        const updatedLineItem = cartContent["items"].find(item => item["key"] === lineKey);

        lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
          bubbles: true,
          detail: {
            quantity: updatedLineItem ? updatedLineItem["quantity"] : 0,
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
      }
    }
  };

  if (!window.customElements.get("line-item-quantity")) {
    window.customElements.define("line-item-quantity", LineItemQuantity);
  }

  var ShippingEstimator = class extends HTMLElement {
    constructor() {
      super();
      this._estimateShippingListener = this._estimateShipping.bind(this);
    }

    connectedCallback() {
      this.submitButton = this.querySelector('[type="submit"]');
      this.resultsElement = this.lastElementChild;
      this.submitButton.addEventListener("click", this._estimateShippingListener);
    }

    disconnectedCallback() {
      this.submitButton.removeEventListener("click", this._estimateShippingListener);
    }

    /**
     * @doc https://shopify.dev/docs/themes/ajax-api/reference/cart#generate-shipping-rates
     */
    async _estimateShipping(event) {
      event.preventDefault();
      const zip = this.querySelector('[name="address[zip]"]').value; 
      const country = this.querySelector('[name="address[country]"]').value; const province = this.querySelector('[name="address[province]"]').value;

      this.submitButton.setAttribute("aria-busy", "true");

      const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });

      if (prepareResponse.ok) {
        const shippingRates = await this._getAsyncShippingRates(zip, country, province);
        this._formatShippingRates(shippingRates);
      } else {
        const jsonError = await prepareResponse.json();
        this._formatError(jsonError);
      }

      this.resultsElement.hidden = false;
      this.submitButton.removeAttribute("aria-busy");
    }

    async _getAsyncShippingRates(zip, country, province) {
      const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
      const responseAsText = await response.text();

      if (responseAsText === "null") {
        return this._getAsyncShippingRates(zip, country, province);
      } else {
        return JSON.parse(responseAsText)["shipping_rates"];
      }
    }

    _formatShippingRates(shippingRates) {
      let formattedShippingRates = shippingRates.map((shippingRate) => {
        return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
      });

      this.resultsElement.innerHTML = `
        <div class="v-stack gap-2">
          <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
          ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>`}
        </div>
      `;
    }

    _formatError(errors) {
      let formattedShippingRates = Object.keys(errors).map((errorKey) => {
        return `<li>${errors[errorKey]}</li>`;
      });
      this.resultsElement.innerHTML = `
        <div class="v-stack gap-2">
          <p>${window.themeVariables.strings.shippingEstimatorError}</p>
          <ul class="list-disc" role="list">${formattedShippingRates}</ul>
        </div>
      `;
    }
  };

  if (!window.customElements.get("shipping-estimator")) {
    window.customElements.define("shipping-estimator", ShippingEstimator);
  }
  
  var CartNote = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("change", this._onNoteChanged);
    }

    _onNoteChanged(event) {
      if (event.target.getAttribute("name") !== "note") {
        return;
      }

      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: event.target.value }),
        keepalive: true
        // Allows to make sure the request is fired even when submitting the form
      });
    }
  };

  var FreeShippingBar = class extends HTMLElement {
    static get observedAttributes() {
      return ["threshold", "total-price"];
    }

    constructor() {
      super();
      this._onCartChangedListener = this._onCartChanged.bind(this);
    }

    async connectedCallback() {
      this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
      document.addEventListener("cart:change", this._onCartChangedListener);
    }

    disconnectedCallback() {
      document.removeEventListener("cart:change", this._onCartChangedListener);
    }

    get threshold() {
      return parseFloat(this.getAttribute("threshold"));
    }

    set threshold(value) {
      this.setAttribute("threshold", value);
    }

    get totalPrice() {
      return parseFloat(this.getAttribute("total-price"));
    }

    set totalPrice(value) {
      this.setAttribute("total-price", value);
    }

    async attributeChangedCallback(attribute, oldValue, newValue) {
      await window.customElements.whenDefined("progress-bar");
      const progressBarElement = this.querySelector("progress-bar");
      switch (attribute) {
        case "threshold":
          progressBarElement.valueMax = newValue;
          break;
        case "total-price":
          progressBarElement.valueNow = newValue;
          break;
      }
      this._updateMessage();
    }

    _updateMessage() {
      const messageElement = this.querySelector("span");
      if (this.totalPrice >= this.threshold) {
        messageElement.innerHTML = this.getAttribute("reached-message");
      } else {
        const replacement = `<span class="bold text-accent">${formatMoney(this.threshold - this.totalPrice).replace(/\$/g, "$$$$")}</span>`;
        messageElement.innerHTML = this.getAttribute("unreached-message").replace(new RegExp("({{.*}})", "g"), replacement);
      }
    }

    _onCartChanged(event) {
      const priceForItems = event.detail["cart"]["items"].filter((item) => item["requires_shipping"]).reduce((sum, item) => sum + item["final_line_price"], 0); 
      const cartDiscount = event.detail["cart"]["cart_level_discount_applications"].reduce((sum, discountAllocation) => sum + discountAllocation["total_allocated_amount"], 0);
      this.totalPrice = priceForItems - cartDiscount;
    }
  };

  if (!window.customElements.get("free-shipping-bar")) {
    window.customElements.define("free-shipping-bar", FreeShippingBar);
  }

  var FreeShippingMessage = class extends FreeShippingBar {
    constructor() {
      super();
    }

    get threshold() {
      return parseFloat(this.getAttribute("threshold"));
    }

    set threshold(value) {
      this.setAttribute("threshold", value);
    }

    get totalPrice() {
      return parseFloat(this.getAttribute("total-price"));
    }

    set totalPrice(value) {
      this.setAttribute("total-price", value);
    }

    async attributeChangedCallback(attribute, oldValue, newValue) {
      this._updateMessage();
    }
  };

  if (!window.customElements.get("free-shipping-message")) {
    window.customElements.define("free-shipping-message", FreeShippingMessage);
  }

  var ProgressBar = class extends HTMLElement {
    static get observedAttributes() {
      return ["aria-valuenow", "aria-valuemax"];
    }

    set valueMax(value) {
      this.setAttribute("aria-valuemax", value);
    }

    set valueNow(value) {
      this.setAttribute("aria-valuenow", value);
    }

    attributeChangedCallback() {
      this.style.setProperty("--progress", `${Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"))}`);
    }
  };

  if (!window.customElements.get("progress-bar")) {
    window.customElements.define("progress-bar", ProgressBar);
  }

  var PillLoader = class extends HTMLElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    connectedCallback() {
      this.innerHTML = `
        <div class="loader-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        
        <svg class="loader-checkmark" fill="none" width="9" height="8" viewBox="0 0 9 8">
          <path d="M1 3.5 3.3 6 8 1" stroke="currentColor" stroke-width="2"/>
        </svg>
      `;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === "true") {
        Motion_timeline([
          [this, { opacity: [0, 1], visibility: "visible", transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.15 }],
          [this.firstElementChild, { opacity: 1, transform: ["translateY(0)"] }, { duration: 0.15, at: "<" }],
          [this.lastElementChild, { opacity: 0 }, { duration: 0, at: "<" }]
        ]);

        Motion_animate(this.firstElementChild.querySelectorAll("span"), { opacity: [1, 0.1] }, { duration: 0.35, delay: Motion_stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
      } else {
        Motion_timeline([
          [this.firstElementChild, { opacity: 0, transform: ["translateY(0)", "translateY(-2px)"] }, { duration: 0.15 }],
          [this.lastElementChild, { opacity: 1, transform: ["translateY(2px)", "translateY(0)"] }, { duration: 0.15 }],
          [this, { opacity: 0, transform: ["translateY(0)", "translateY(-5px)"], visibility: "hidden" }, { duration: 0.15, at: "+0.8" }]
        ]);
      }
    }
  };

  if (!window.customElements.get("pill-loader")) {
    window.customElements.define("pill-loader", PillLoader);
  }






  /*------------------------------------*\
    #FACETS
  \*------------------------------------*/

  var FacetApplyButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._closeDrawer);
      this.form.addEventListener("change", this._updateCount.bind(this));
      this.filterCountElement = document.createElement("span");
      this.appendChild(this.filterCountElement);
    }

    connectedCallback() {
      this._updateCount();
    }

    _updateCount() {
      const form = new FormData(this.form);
      const count = Array.from(form.entries()).filter(([key, value]) => key !== "sort_by" && value !== "").length;
      this.filterCountElement.innerText = ` (${count})`;
    }

    async _closeDrawer() {
      this.closest("facet-drawer").hide();
    }
  };

  if (!window.customElements.get("facet-apply-button")) {
    window.customElements.define("facet-apply-button", FacetApplyButton, { extends: "button" });
  }


  var FacetDrawer = class extends Drawer {
    constructor() {
      super();
      this.addEventListener("dialog:after-hide", this._submitForm);
    }

    _submitForm() {
      const form = this.querySelector("#facet-form");
      if (HTMLFormElement.prototype.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    }
  };

  if (!window.customElements.get("facet-drawer")) {
    window.customElements.define("facet-drawer", FacetDrawer);
  }

  var abortController = null;
  var openElements = new Set();

  document.addEventListener("facet:update", async (event) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const url = event.detail.url;
    const shopifySection = document.getElementById(`shopify-section-${event.detail.sectionId}`);
    const updateHistory = event.detail.updateHistory !== false; // Default to true if not provided

    shopifySection.classList.add("is-loading");
    
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete("section_id");

    // Update history only if updateHistory is true and check if the new URL is different from the current one
    if (updateHistory && clonedUrl.toString() !== window.location.href) {
      history.pushState({ url: clonedUrl.toString() }, "", clonedUrl.toString());
    }

    try {
      url.searchParams.set("section_id", event.detail.sectionId);

      const tempContent = new DOMParser().parseFromString(await (await cachedFetch(url.toString(), { signal: abortController.signal })).text(), "text/html");

      Array.from(tempContent.querySelectorAll("details, facet-dialog")).forEach((item) => {
        if (openElements.has(item.id)) {
          item.setAttribute("open", "");
        }
      });

      shopifySection.replaceChildren(...document.importNode(tempContent.querySelector(".shopify-section"), true).childNodes);

      shopifySection.classList.remove("is-loading");

      var trustPilotWidgets = document.getElementsByClassName("trustpilot-widget");
      for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
      }

      const scrollToElement = shopifySection.querySelector(".collection__results");
      const scrollToBoundingRect = scrollToElement.getBoundingClientRect();

      if (scrollToBoundingRect.top < parseInt(getComputedStyle(scrollToElement).scrollPaddingTop || 0)) {
        scrollToElement.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    } catch (e) {
    }
  });

  /**
   * We use `popstate` to allow for triggering filter changes based on back/forward navigation.
   * `popstate` fires when the active history entry changes, but it doesn't fire for initial page load.
   * Therefore, we store the initial state (unfiltered collection page). This ensures if someone selects a filter and clicks back, they will return to the unfiltered collection.
   */
  window.addEventListener('DOMContentLoaded', (event) => {
    const initialUrl = window.location.href;
    history.replaceState({ url: initialUrl }, '', initialUrl);
  });
  
  /**
   * The `popstate` event will re-fetch data when the user navigates with back/forward buttons
   */
  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.url) {
      document.dispatchEvent(new CustomEvent("facet:update", { 
        detail: { 
          url: new URL(event.state.url), 
          sectionId: document.querySelector("#facet-form").getAttribute("section-id")
        }
      }));
    }
  });
  
  

  var FacetForm = class extends HTMLFormElement {
    constructor() {
      super();

      // Check if the form has been changed since load. By default, an unchanged form is clean.
      this._isDirty = false;

      this.addEventListener("change", this._onFormChanged);
      this.addEventListener("submit", this._onFormSubmitted);
    }

    connectedCallback() {
      Array.from(this.querySelectorAll("details, facet-dialog")).forEach((disclosureElement) => {
        if (disclosureElement.open) {
          openElements.add(disclosureElement.id);
        }

        disclosureElement.addEventListener("toggle", () => {
          if (disclosureElement.open) {
            openElements.add(disclosureElement.id);
          } else {
            openElements.delete(disclosureElement.id);
          }
        });
      });
    }

    _buildUrl() {
      // 1. Gather all form data and create a URLSearchParams object.
      const searchParams = new URLSearchParams(new FormData(this));
    
      // 2. Create a new URL object based on the form's action attribute.
      const url = new URL(this.action);
    
      // 3. Clear any existing search parameters in the URL.
      url.search = "";
    
      // 4. Append each key-value pair from the searchParams to the URL.
      searchParams.forEach((value, key) => url.searchParams.append(key, value));
    
      // 5. Clear certain parameters if their value is an empty string.
      ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
        if (url.searchParams.get(optionToClear) === "") {
          url.searchParams.delete(optionToClear);
        }
      });
    
      // 6. Add or update a "section_id" parameter in the URL.
      url.searchParams.set("section_id", this.getAttribute("section-id"));
    
      // 7. Return the final constructed URL.
      return url;
    }
    

    _onFormChanged() {
      this._isDirty = true;

      /**
       * Our facet form on desktop has this attribute which means clicking filters will trigger form submission automatically.
       * In contrast, the facet drawer will only trigger the submission once the drawer is closed.
       */
      if (this.hasAttribute("update-on-change")) {
        /**
         * requestSubmit is a modern method which will run a full form validation. In contrast, the older submit method doesn't.
         */
        if (HTMLFormElement.prototype.requestSubmit) {
          this.requestSubmit();
        } else {
          this.dispatchEvent(new Event("submit", { cancelable: true }));
        }
      } else {
        cachedFetch(this._buildUrl().toString());
      }
    }

    _onFormSubmitted(event) {
      event.preventDefault();

      if (!this._isDirty) {
        return;
      }

      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url: this._buildUrl(),
          sectionId: this.getAttribute("section-id")
        }
      }));

      this._isDirty = false;
    }
  };

  if (!window.customElements.get("facet-form")) {
    window.customElements.define("facet-form", FacetForm, { extends: "form" });
  }


  var FacetLink = class extends HTMLAnchorElement {
    constructor() {
      super();
      this.addEventListener("click", this._onFacetUpdate);
    }

    _onFacetUpdate(event) {
      event.preventDefault();

      const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", ""); 
      const url = new URL(this.href);

      url.searchParams.set("section_id", sectionId);
      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url,
          sectionId: sectionId
        }
      }));
    }
  };

  if (!window.customElements.get("facet-link")) {
    window.customElements.define("facet-link", FacetLink, { extends: "a" });
  }


  var FacetSortBy = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("listbox:change", this._onValueChanged);
    }

    _onValueChanged(event) {
      const url = new URL(location.href); 
      const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", "");
      
      url.searchParams.set("sort_by", event.detail.value);
      url.searchParams.set("section_id", sectionId);
      url.searchParams.delete("page");

      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url,
          sectionId: sectionId
        }
      }));
    }
  };

  if (!window.customElements.get("facet-sort-by")) {
    window.customElements.define("facet-sort-by", FacetSortBy);
  }




  /*------------------------------------*\
    #PRODUCT FORM
  \*------------------------------------*/

  var ProductForm = class extends HTMLFormElement {
    constructor() {
      super();
      this.addEventListener("submit", this._onSubmit);
    }

    connectedCallback() {
      this.id.disabled = false;
    }

    async _onSubmit(event) {
      event.preventDefault();

      if (!this.checkValidity()) {
        this.reportValidity();
        return;
      }

      const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");

      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      });

      let sectionsToBundle = ["variant-added"];

      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

      const formData = new FormData(this);
      formData.set("sections", sectionsToBundle.join(","));
      formData.set("sections_url", `${Shopify.routes.root}variants/${this.id.value}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
          // Needed for Shopify to check inventory
        }
      });

      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      });

      const responseJson = await response.json();

      if (response.ok) {
        if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
          return window.location.href = `${Shopify.routes.root}cart`;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent["sections"] = responseJson["sections"];

        this.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent
          }
        }));
      } else {
        this.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"]
          }
        }));

        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    }
  };

  if (!window.customElements.get("product-form")) {
    window.customElements.define("product-form", ProductForm, { extends: "form" });
  }

  var BuyButtons = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this._onCartErrorListener = this._onCartError.bind(this);
    }

    connectedCallback() {
      this._productForm = document.forms[this.getAttribute("form")];
      this._productForm?.addEventListener("variant:change", this._onVariantChangedListener);
      this._productForm?.addEventListener("cart:error", this._onCartErrorListener);
    }

    disconnectedCallback() {
      this._productForm?.removeEventListener("variant:change", this._onVariantChangedListener);
      this._productForm?.removeEventListener("cart:error", this._onCartErrorListener);
    }

    _onVariantChanged(event) {
      const addToCartButton = this.querySelector('button[type="submit"]'); 
      const paymentButton = this.querySelector(".shopify-payment-button");
      addToCartButton.classList.remove("button--subdued");
      addToCartButton.disabled = !event.detail.variant || !event.detail.variant["available"];

      const addToCartButtonText = addToCartButton.getAttribute("is") === "custom-button" ? addToCartButton.firstElementChild : addToCartButton;
      const addToCartButtonLabel = addToCartButton.querySelector(".button__label") || addToCartButtonText?.querySelector?.(".button__label");

      const setAddToCartText = (text) => {
        if (addToCartButtonLabel) {
          addToCartButtonLabel.textContent = text;
        } else if (addToCartButtonText) {
          addToCartButtonText.textContent = text;
        }
      };

      if (!event.detail.variant) {
        setAddToCartText(window.themeVariables.strings.unavailableButton);
        addToCartButton.classList.add("button--subdued");

        if (paymentButton) {
          paymentButton.style.display = "none";
        }
      } else {
        addToCartButton.classList.add(event.detail.variant["available"] ? paymentButton || this.hasAttribute("force-secondary-button") ? "button" : "button" : "button--subdued");

        setAddToCartText(event.detail.variant["available"] ? window.themeVariables.strings.addToCartButton : window.themeVariables.strings.soldOutButton);

        if (paymentButton) {
          paymentButton.style.display = event.detail.variant["available"] ? "block" : "none";
        }
      }
    }

    _onCartError(event) {
      const errorBanner = document.createElement("div");
      errorBanner.classList.add("banner", "banner--error", "justify-center");
      errorBanner.setAttribute("role", "alert");
      errorBanner.style.gridColumn = "1/-1";
      errorBanner.style.marginBottom = "1rem";

      errorBanner.innerHTML = `
        <svg role="presentation" focusable="false" width="18" height="18" class="icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
          <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
          <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
        </svg>
        
        <p>${event.detail.error}</p>
      `;
      this.before(errorBanner);

      setTimeout(async () => {
        await errorBanner.animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished;
        errorBanner.remove();
      }, 5e3);
    }
  };

  if (!window.customElements.get("buy-buttons")) {
    window.customElements.define("buy-buttons", BuyButtons);
  }


  /*------------------------------------*\
    #PRODUCT PRICES
  \*------------------------------------*/

  var currencyFormat = window.themeVariables.settings.moneyFormat;

  var SalePrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(variant["price"], currencyFormat)));
      this.classList.toggle("text-on-sale", variant["compare_at_price"] > variant["price"]);
    }
  };


  var CompareAtPrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(variant["compare_at_price"], currencyFormat)));
      this.hidden = !(variant["compare_at_price"] > variant["price"]);
    }
  };


  var UnitPrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant["unit_price"]) {
        return this.hidden = true;
      }
      const referenceValue = variant["unit_price_measurement"]["reference_value"] !== 1 ? variant["unit_price_measurement"]["reference_value"] : "";
      const node = document.createRange().createContextualFragment(
        `${formatMoney(variant["unit_price"])}/${referenceValue}${variant["unit_price_measurement"]["reference_unit"]}`
      );
      this.lastChild.replaceWith(node);
      this.hidden = false;
    }
  };

  if (!window.customElements.get("sale-price")) {
    window.customElements.define("sale-price", SalePrice);
  }

  if (!window.customElements.get("compare-at-price")) {
    window.customElements.define("compare-at-price", CompareAtPrice);
  }

  if (!window.customElements.get("unit-price")) {
    window.customElements.define("unit-price", UnitPrice);
  }


  /*------------------------------------*\
    #PRODUCT BADGES
  \*------------------------------------*/

  var SoldOutBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.hidden = event.detail.variant["available"];
    }
  };

  var InStockBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.hidden = !event.detail.variant["available"];
    }
  };

  var OnSaleBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (!variant["available"]) {
        this.hidden = true;
        return;
      }

      if (variant["compare_at_price"] > variant["price"]) {
        this.hidden = false;
        if (this.hasAttribute("discount-mode")) {
          const savings = this.getAttribute("discount-mode") === "percentage" ? `${Math.round((variant["compare_at_price"] - variant["price"]) * 100 / variant["compare_at_price"])}%` : formatMoney(variant["compare_at_price"] - variant["price"]);
          this.innerHTML = `${window.themeVariables.strings.discountBadge.replace("@@", savings)}`;
        }
      } else {
        this.hidden = true;
      }
    }
  };

  if (!window.customElements.get("sold-out-badge")) {
    window.customElements.define("sold-out-badge", SoldOutBadge);
  }

  if (!window.customElements.get("in-stock-badge")) {
    window.customElements.define("in-stock-badge", InStockBadge);
  }

  if (!window.customElements.get("on-sale-badge")) {
    window.customElements.define("on-sale-badge", OnSaleBadge);
  }



  /*------------------------------------*\
    #PRODUCT VARIANTS
  \*------------------------------------*/

  var VariantPicker = class extends HTMLElement {
    async connectedCallback() {
      this._abortController = new AbortController();
      this.masterSelector = document.forms[this.getAttribute("form")].id;
      this.optionSelectors = Array.from(this.querySelectorAll("[data-option-selector]"));

      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }

      this.product = await ProductLoader.load(this.productHandle);

      this.optionSelectors.forEach((optionSelector) => {
        optionSelector.addEventListener("change", this._onOptionChanged.bind(this), { signal: this._abortController.signal });
      });

      this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this), { signal: this._abortController.signal });
      this._updateDisableSelectors();
      this.selectVariant(this.selectedVariant["id"]);
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    get hideSoldOutVariants() {
      return this.hasAttribute("hide-sold-out-variants");
    }

    get updateUrl() {
      return this.hasAttribute("update-url");
    }

    /**
     * Select a new variant by its ID
     */
    selectVariant(id) {
      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableOrSelectableVariant()["id"];
      }

      const previousVariant = this.selectedVariant;

      if (previousVariant && previousVariant.id === id) {
        return;
      }

      this.masterSelector.value = id;
      this.masterSelector.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set("variant", id);
        } else {
          newUrl.searchParams.delete("variant");
        }
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }

      this._updateDisableSelectors();

      this.masterSelector.form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          product: this.product,
          variant: this.selectedVariant,
          previousVariant
        }
      }));
    }

    _onOptionChanged(event) {
      if (!event.target.name.startsWith("option")) {
        return;
      }

      this.selectVariant(this._getVariantFromOptions()?.id);
    }

    _onMasterSelectorChanged() {
      const options = this.selectedVariant?.options || [];

      options.forEach((value, index) => {
        let input = this.optionSelectors[index].querySelector(`input[type="radio"][name="option${index + 1}"][value="${CSS.escape(value)}"], input[type="hidden"][name="option${index + 1}"], select[name="option${index + 1}"]`);
        let triggerChangeEvent = false;

        if (input.tagName === "SELECT" || input.tagName === "INPUT" && input.type === "hidden") {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === "INPUT" && input.type === "radio") {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    /**
     * Get the product variant by its ID
     */
    _getVariantById(id) {
      return this.product["variants"].find((variant) => variant["id"] === id);
    }

    /**
     * Get the variant based on the options
     */
    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();
      return this.product["variants"].find((variant) => {
        return variant["options"].every((value, index) => value === options[index]);
      });
    }

    /**
     * Detect if a specific variant is selectable. This is used when the "hide sold out variant" option is enabled, to allow
     * returning true only if the variant is actually available
     */
    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant["available"] || !this.hideSoldOutVariants && !variant["available"];
      }
    }

    /**
     * This method is used internally to select an available or selectable variant, when the current choice does not
     * match the requirements. For instance, if sold out variants are configured to be hidden, but that the choices end
     * up being a non-valid variant, the theme automatically changes the variant to match the requirements. In the case
     * the customer end up on variant combinations that do not exist, it also switches to a valid combination.
     *
     * The algorithm is as follows: if we have for instance three options "Color", "Size" and "Material", we pop the last
     * option (Material) and try to find the first available variant for the given Color and Size. If none is found we
     * remove the second option (Size) and try to find the first available variant for the selected color. Finally, if none
     * is found we return the first available variant independently of any choice.
     */
    _getFirstMatchingAvailableOrSelectableVariant() {
      let options = this._getSelectedOptionValues(); 
      let matchedVariant = null; 
      let slicedCount = 0;

      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product["variants"].find((variant) => {
          if (this.hideSoldOutVariants) {
            return variant["available"] && variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
          } else {
            return variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
          }
        });
      } while (!matchedVariant && options.length > 0);
      return matchedVariant;
    }

    _getSelectedOptionValues() {
      return this.optionSelectors.map((optionSelector) => {
        return optionSelector.querySelector('input[name^="option"][type="hidden"], input[name^="option"]:checked, select[name^="option"]').value;
      });
    }

    /**
     * We add specific class to sold out variants based on the selectors
     */
    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;

      if (!selectedVariant) {
        return;
      }

      this._updateDisableSelectorsForOptionLevel(0, selectedVariant);
    }

    _updateDisableSelectorsForOptionLevel(level, selectedVariant) {
      if (!this.optionSelectors[level]) {
        return;
      }

      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        const optionValue = Array.from(selector.querySelectorAll("[data-option-value]"))[valueIndex];
        /* Uncomment the below in order to hide unavailable variant options */
        // optionValue.toggleAttribute("hidden", !hasAtLeastOneCombination);

        // This will disable unavailable variant options
        optionValue.classList.toggle("is-disabled", !hasAtLeastOneCombination);

        if (this.hideSoldOutVariants) {
          optionValue.toggleAttribute("hidden", !available);
        } else {
          // This will not disable sold out options but add a CSS class which we can potentially style in future
          optionValue.classList.toggle("is-sold-out", !available);
        }
      };

      const hasCombination = (variant, level2, value, selectedVariant2) => {
        return Array.from({ length: level2 + 1 }, (_, i) => {
          if (i === level2) {
            return variant[`option${level2 + 1}`] === value;
          } else {
            return variant[`option${i + 1}`] === selectedVariant2[`option${i + 1}`];
          }
        }).every((condition) => condition);
      };

      this.product["options"][level]["values"].forEach((value, valueIndex) => {
        const hasAtLeastOneCombination = this.product["variants"].some(
          (variant) => hasCombination(variant, level, value, selectedVariant) && variant
        );

        const hasAvailableVariant = this.product["variants"].some(
          (variant) => hasCombination(variant, level, value, selectedVariant) && variant["available"]
        );

        applyClassToSelector(this.optionSelectors[level], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
        this._updateDisableSelectorsForOptionLevel(level + 1, selectedVariant);
      });
    }
  };

  if (!window.customElements.get("variant-picker")) {
    window.customElements.define("variant-picker", VariantPicker);
  }


  var VariantOptionValue = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.innerHTML = event.detail.variant[this.getAttribute("for")];
    }
  };

  if (!window.customElements.get("variant-option-value")) {
    window.customElements.define("variant-option-value", VariantOptionValue);
  }


  var VariantSku = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      if (!event.detail.variant) {
        this.hidden = true;
      } else {
        this.innerText = `${window.themeVariables.strings.sku} ${event.detail.variant["sku"]}`;
        this.hidden = !event.detail.variant["sku"];
      }
    }
  };

  if (!window.customElements.get("variant-sku")) {
    window.customElements.define("variant-sku", VariantSku);
  }





  /*------------------------------------*\
    #QUANTITY
  \*------------------------------------*/

  var QuantitySelector = class extends HTMLElement {
    constructor() {
      super();
      this._decreaseQuantity;
      this._increaseQuantity;
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this._updateUI;
      this._abortController = undefined;
      this._decreaseButton = undefined;
      this._increaseButton = undefined;
      this._inputElement = undefined;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this._decreaseButton = this.querySelector("button:first-of-type");
      this._increaseButton = this.querySelector("button:last-of-type");
      this._inputElement = this.querySelector("input");
      
      this._decreaseButton?.addEventListener("click", this._onDecreaseQuantity.bind(this), { signal: this._abortController.signal });
      this._increaseButton?.addEventListener("click", this._onIncreaseQuantity.bind(this), { signal: this._abortController.signal });

      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _onDecreaseQuantity(event) {
      this._inputElement.stepDown();
      this._updateUI();
    }

    _onIncreaseQuantity(event) {
      this._inputElement.stepUp();
      this._updateUI();
    }

    _onVariantChanged(event) {
      this._inputElement.updateVariantRule(event.detail.variant);
    }

    _updateUI() {
      this._decreaseButton.disabled = this._inputElement.quantity <= 1;
    }
  };

  var QuantityInput = class extends HTMLInputElement {
    constructor() {
      super();
      this._onValueInputListener = this._onValueInput.bind(this);
      this.addEventListener("input", this._onValueInput);
      this.addEventListener("focus", this.select);
    }

    connectedCallback() {
      this.style.setProperty("--quantity-selector-character-count", `${this.value.length}ch`);
    }

    get quantity() {
      return parseInt(this.value);
    }

    updateVariantRule(variant) {
    }

    _onValueInput() {
      if (this.value === "") {
        this.value = this.min || 1;
      }

      this.style.setProperty("--quantity-selector-character-count", `${this.value.length}ch`);
    }
  };

  if (!window.customElements.get("quantity-selector")) {
    window.customElements.define("quantity-selector", QuantitySelector);
  }
  if (!window.customElements.get("quantity-input")) {
    window.customElements.define("quantity-input", QuantityInput, { extends: "input" });
  }




  /*------------------------------------*\
    #QUICK BUY
  \*------------------------------------*/

  var ProductQuickAdd = class extends HTMLElement {
    #scopeFromPassed = false;
    #scopeToReached = false;
    #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));

    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this.buttonScroller = Array.from(this.querySelectorAll(".button--scroller"));
      this.buttonScroller.forEach((element) => {
        element.addEventListener("click", this.buttonScrollerClicked.bind(this));
      });
    }

    buttonScrollerClicked(event) {
      const scrollToId = event.currentTarget.getAttribute("scroll-to");
      const scrollToElement = document.querySelector(`#${scrollToId}`);
      scrollToElement.scrollIntoView({ behavior: "smooth" });
    }

    connectedCallback() {
      this._scopeFrom = document.getElementById(this.getAttribute("form"));
      this._scopeTo = document.querySelector(".footer");
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);

      if (!this._scopeFrom || !this._scopeTo) {
        return;
      }

      this.#intersectionObserver.observe(this._scopeFrom);
      this.#intersectionObserver.observe(this._scopeTo);
    }

    disconnectedCallback() {
      this.#intersectionObserver.disconnect();
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (variant["available"]) {
        this.hidden = false;
      } else {
        this.hidden = true;
      }
    }

    _onObserved(entries) {
      entries.forEach((entry) => {
        if (entry.target === this._scopeFrom) {
          this.#scopeFromPassed = entry.boundingClientRect.bottom < 0;
        }

        if (entry.target === this._scopeTo) {
          this.#scopeToReached = entry.isIntersecting;
        }
      });

      this.classList.toggle("is-visible", this.#scopeFromPassed && !this.#scopeToReached);
    }
  };

  if (!window.customElements.get("product-quick-add")) {
    window.customElements.define("product-quick-add", ProductQuickAdd);
  }


  var QuickBuyDrawer = class extends Drawer {
    constructor() {
      super();
      this._hasLoaded = false;
      this.addEventListener("variant:add", this._onVariantAdded.bind(this));
    }

    async show() {
      this.style.display = "block";

      if (!this._hasLoaded) {
        [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "true"));

        const responseContent = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();

        [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "false"));

        const quickBuyContent = new DOMParser().parseFromString(responseContent, "text/html").getElementById("quick-buy-content").content;

        Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());

        this.replaceChildren(quickBuyContent);
        Shopify.PaymentButton?.init();
        this._hasLoaded = true;
      }

      var trustPilotWidget = this.querySelector(".trustpilot-widget");
      window.Trustpilot.loadFromElement(trustPilotWidget);

      return super.show();
    }

    async hide() {
      return super.hide()?.then(() => {
        this.style.display = "none";
      });
    }

    _onVariantAdded(event) {
      this.hide();
      this._hasLoaded = false;
    }
  };

  if (!window.customElements.get("quick-buy-drawer")) {
    window.customElements.define("quick-buy-drawer", QuickBuyDrawer);
  }






  /*------------------------------------*\
    #MEDIA
  \*------------------------------------*/

  var BaseMedia = class extends HTMLElement {
    static get observedAttributes() {
      return ["playing"];
    }

    connectedCallback() {
      this._abortController = new AbortController();
      if (this.hasAttribute("autoplay")) {
        Motion_inView(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get playing() {
      return this.hasAttribute("playing");
    }

    get player() {
      return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this._playerHandler(target, prop);
          };
        }
      });
    }

    play() {
      if (!this.playing) {
        this.player.play();
      }
    }

    pause() {
      if (this.playing) {
        this.player.pause();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "playing") {
        if (oldValue === null && newValue === "") {
          this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));

          if (this.hasAttribute("group")) {
            Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
              itemToPause.pause();
            });
          }
        } else if (newValue === null) {
          this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
        }
      }
    }
  };


  var onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
  });

  var VideoMedia = class extends BaseMedia {
    connectedCallback() {
      super.connectedCallback();
      if (!this.hasAttribute("autoplay")) {
        this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
      }

      if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
        this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
          <slot></slot>
          
          <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
            <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="#f0f0f0"/>
            <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="#1a1a1a"/>
            <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="#1a1a1a" stroke-opacity=".12"/>
          </svg>
        `));
      }
    }

    play({ restart = false } = {}) {
      if (restart && !this.hasAttribute("host")) {
        this.querySelector("video").currentTime = 0;
      }
      super.play();
    }

    _playerTarget() {
      if (this.hasAttribute("host")) {
        this.setAttribute("loaded", "");
        return new Promise(async (resolve) => {
          const templateElement = this.querySelector("template");

          if (templateElement) {
            templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
          }

          const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
          const script = document.createElement("script");
          script.type = "text/javascript";

          if (this.getAttribute("host") === "youtube") {
            if (!window.YT || !window.YT.Player) {
              script.src = "https://www.youtube.com/iframe_api";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            await onYouTubePromise;
            this.setAttribute("can-play", "");
            const player = new YT.Player(this.querySelector("iframe"), {
              events: {
                "onReady": () => {
                  if (muteVideo) {
                    player.mute();
                  }
                  resolve(player);
                },
                "onStateChange": (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute("playing", "");
                  } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                    this.removeAttribute("playing");
                  }
                }
              }
            });
          }

          if (this.getAttribute("host") === "vimeo") {
            if (!window.Vimeo || !window.Vimeo.Player) {
              script.src = "https://player.vimeo.com/api/player.js";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            const player = new Vimeo.Player(this.querySelector("iframe"));
            if (muteVideo) {
              player.setMuted(true);
            }

            this.setAttribute("can-play", "");
            player.on("play", () => {
              this.setAttribute("playing", "");
            });

            player.on("pause", () => this.removeAttribute("playing"));
            player.on("ended", () => this.removeAttribute("playing"));
            resolve(player);
          }
        });
      } else {
        const videoElement = this.querySelector("video");
        this.setAttribute("loaded", "");
        this.setAttribute("can-play", "");

        videoElement.addEventListener("play", () => {
          this.setAttribute("playing", "");
          this.removeAttribute("suspended");
        });

        videoElement.addEventListener("pause", () => {
          if (!videoElement.seeking && videoElement.paused) {
            this.removeAttribute("playing");
          }
        });
        return videoElement;
      }
    }

    _playerHandler(target, prop) {
      if (this.getAttribute("host") === "youtube") {
        prop === "play" ? target.playVideo() : target.pauseVideo();
      } else {
        if (prop === "play" && !this.hasAttribute("host")) {
          target.play().catch((error) => {
            if (error.name === "NotAllowedError") {
              this.setAttribute("suspended", "");
              target.controls = true;
              const replacementImageSrc = target.previousElementSibling?.currentSrc;
              
              if (replacementImageSrc) {
                target.poster = replacementImageSrc;
              }
            }
          });
        } else {
          target[prop]();
        }
      }
    }
  };

  if (!window.customElements.get("video-media")) {
    window.customElements.define("video-media", VideoMedia);
  }





  /*------------------------------------*\
    #PRODUCT SECTIONS
  \*------------------------------------*/

  var ProductRecommendations = class extends HTMLElement {
    constructor() {
      super();
      this._isLoaded = false;
    }

    connectedCallback() {
      this._loadRecommendations();
    }

    async _loadRecommendations() {
      if (this._isLoaded) {
        return;
      }

      this._isLoaded = true;
      const section = this.closest(".shopify-section"); 
      const intent = this.getAttribute("intent") || "related";
      const url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${section.id.replace("shopify-section-", "")}&intent=${intent}`; 
      const response = await fetch(url, { priority: "low" });

      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");

      if (productRecommendationsElement.childElementCount > 0) {
        this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);

        var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
        for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
        }
      } else {
        if (intent === "related") {
          section.remove();
        } else {
          this.remove();
        }
      }
    }
  };

  if (!window.customElements.get("product-recommendations")) {
    window.customElements.define("product-recommendations", ProductRecommendations);
  }


  var RecentlyViewedProducts = class extends HTMLElement {
    constructor() {
      super();
      this._isLoaded = false;
    }

    connectedCallback() {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(this._loadProducts.bind(this), { timeout: 1500 });
      } else {
        this._loadProducts();
      }
    }

    removeAllItems(event) {
      localStorage.removeItem('theme:recently-viewed-products');

      this._isLoaded = false;
      this._loadProducts();
    }

    removeItem(event) {
      const recentlyViewedProducts = new Set(JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]'));

      const productId = parseInt(event.currentTarget.getAttribute("product-id"));
      
      recentlyViewedProducts.delete(productId);

      // Update localStorage
      localStorage.setItem('theme:recently-viewed-products', JSON.stringify(Array.from(recentlyViewedProducts.values())));

      // Reload the section
      this._isLoaded = false;
      this._loadProducts();
    }
  
    get _searchQueryString() {
      const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));

      if (this.hasAttribute("exclude-id")) {
        items.delete(parseInt(this.getAttribute("exclude-id")));
      }

      return Array.from(items.values(), item => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
    }
  
    async _loadProducts() {
      if (this._isLoaded) {
        return;
      }
      
      this._isLoaded = true;
  
      const section = this.closest(".shopify-section");
      const url = `${Shopify.routes.root}search?type=product&q=${this._searchQueryString}&section_id=${extractSectionId(section)}`;
      const response = await fetch(url, { priority: "low" });
      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  
      if (recentlyViewedProductsElement.childElementCount > 0) {
        this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);

        var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
        for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
        }

        // Now that the content is loaded, query the remove buttons add the event listeners.
        this.removeButtons = Array.from(this.querySelectorAll(".remove-button"));
        this.removeButtons.forEach((button) => button.addEventListener("click", this.removeItem.bind(this)));
        this.removeAllButton = this.querySelector(".remove-all-button");
        this.removeAllButton.addEventListener("click", this.removeAllItems.bind(this))
      } else {
        section.remove();
      }
    }
  }
  
  if (!window.customElements.get("recently-viewed-products")) {
    window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
  }

  /**
   * Product page Back in Stock functionality integrated with Klaviyo.
   * We run two separate fetches as Klaviyo has separate APIs for back in stock and subscription.
   */
  var BackInStock = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this.form = this.querySelector(".back-in-stock__form");
      this.form.addEventListener("submit", this._onFormSubmitted.bind(this));
      this.formInput = this.querySelector('input[type="email"]');
      this.formCheckbox = this.querySelector('input[type="checkbox"]');
      this.resultsElement = this.querySelector(".back-in-stock__results");
      this.submitButton = this.querySelector('button[type="submit"]');
      this.apiKey = this.getAttribute("api-key");
      this.listId = this.getAttribute("list-id");
      this.successText = this.getAttribute("success-text");
    }

    connectedCallback() {
      this.productOffers = document.querySelector(".product-info__offers");
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    get checkboxChecked() {
      return this.formCheckbox.checked;
    }

    async _onFormSubmitted(event) {
      event.preventDefault();
      this.resultsElement.hidden = true;
      this.resultsElement.innerHTML = "";

      const email = this.formInput.value;
      const variant = this.form.getAttribute("data-variant-id");

      this.submitButton.setAttribute("aria-busy", "true");

      const backInStockUrl = "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" + this.apiKey;

      const payload = {
        "data": {
          "type": "back-in-stock-subscription",
          "attributes": {
            "profile": {
              "data": {
                  "type": "profile",
                  "attributes": {
                      "email": email,
                  }
              }
            },
            "channels": ["EMAIL"],
          },
          "relationships": {
            "variant": {
              "data": {
                "type": "catalog-variant",
                "id": "$shopify:::$default:::" + variant,
              }
            }
          }
        }
      }

      var requestOptions = {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2025-01-15',
          'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(backInStockUrl, requestOptions);

      if (response.ok) {
        this.resultsElement.innerHTML = `<span class="text-success">${this.successText}</span>`;
        this.resultsElement.hidden = false;
      } else {
        const errors = await response.json();
        this.resultsElement.innerHTML = `<span class="text-error">${errors["errors"][0]["detail"]}</span>`;
        this.resultsElement.hidden = false;
      }

      if (this.checkboxChecked) {
        const newsletterUrl = "https://a.klaviyo.com/client/subscriptions/?company_id=" + this.apiKey;

        const secondPayload = { 
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in stock form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  },
                }
              }
            },
            relationships: {list: {data: {type: 'list', id: `${this.listId}`}}}
          }
        }

        var secondRequestOptions = {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "revision": "2024-06-15"
          },
          body: JSON.stringify(secondPayload),
        };

        const secondResponse = await fetch(newsletterUrl, secondRequestOptions);

        if (secondResponse.ok) {
          console.log(secondResponse);
        } else {
          const secondErrors = await secondResponse.json();
          console.log(secondErrors);
        }
      }

      this.submitButton.setAttribute("aria-busy", "false");
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (variant["available"]) {
        this.hidden = true;
        this.productOffers.hidden = false;
        this.form.setAttribute("data-variant-id", "");
      } else {
        this.form.setAttribute("data-variant-id", variant["id"]);
        this.productOffers.hidden = true;
        this.hidden = false;
      }

    }
  }

  if (!window.customElements.get("back-in-stock")) {
    window.customElements.define("back-in-stock", BackInStock);
  }


  /**
   * Product page alternative variants. This checks for the selected variant, and will show alternative variants. The primary logic is within Liquid. The JS is primarily controlling showing/hiding the alternatives.
   */
  var AlternativeVariants = class extends HTMLElement {
    constructor() {
      super();

      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      this._productForm = document.forms[this.getAttribute("form")];
      this._productForm?.addEventListener("variant:change", this._onVariantChangedListener);
      this.json = this.querySelector(".alternative-variants__json");
      this.badges = this.querySelector(".alternative-variants__badge-list");
      this.button = this.querySelector(".alternative-variants__button");
      this._onLoad();
    }

    get avLink() {
      return this.getAttribute("av-link");
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    _onLoad() {
      if (this.avLink) {
        this._fetchAlternativeVariants(this.avLink);
      }
    }

    _fetchAlternativeVariants(url) {
      try {
        return this._loadAlternativeVariants(url);
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }

    async _loadAlternativeVariants(url) {
      const newElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}${url}&view=fetch&section_id=fetch-collection`)).text(), "text/html");
  
      const altVariantsResults = this.querySelector(".alternative-variants__list");
      const collectionResultsChildren = newElement.querySelector(".collection-results").children;
  
      altVariantsResults.innerHTML = "";

      /**
       * We want a maximum of 3 alternative variants to show. However, this may include our existing product which is why set maxImported to 4. We also need to check if any products are available to display which determines whether we show the widget.
       */

      let importedCount = 0;
      let displayedCount = 0;
      const maxImported = 4;
      const maxDisplayed = 3;
      
      for (const child of collectionResultsChildren) {
        if (importedCount >= maxImported) break;

        const handle = child.getAttribute("handle");

        if (handle === this.productHandle) {
          importedCount++;
          continue;
        }

        if (displayedCount < maxDisplayed) {
          altVariantsResults.appendChild(document.importNode(child, true));
          displayedCount++;
        }
    
        importedCount++;
      }

      if (displayedCount > 0) {
        this.button.href = `${window.Shopify.routes.root}${url}`;
        this.removeAttribute("hidden");
      }

      var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
      for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
      }
    }

    _onVariantChanged(event) {
      this.setAttribute("hidden", "");

      if (!event.detail.variant.available) {
        const jsonData = JSON.parse(this.json.textContent);
        const newurl = jsonData[event.detail.variant.id];
        this._fetchAlternativeVariants(newurl);
        let newBadgesContent = ``
        
        event.detail.variant.options.forEach((element) => {
          newBadgesContent += `<div class="badge badge--secondary">${element}</div>`
        });

        this.badges.innerHTML = "";
        this.badges.innerHTML = newBadgesContent;
      }
    }
  }

  if (!window.customElements.get("alternative-variants")) {
    window.customElements.define("alternative-variants", AlternativeVariants);
  }


  var ProductZoomModal = class extends Modal {
    constructor() {
      super();
      this.addEventListener("dialog:after-hide", () => {
        this.querySelector("video-media").pause();
        this.querySelector("product-zoom-media-carousel").select(0)
      });
    }

    connectedCallback() {
      super.connectedCallback();
    }

    get shouldAppendToBody() {
      return false;
    }
  }

  if (!window.customElements.get("product-zoom-modal")) {
    window.customElements.define("product-zoom-modal", ProductZoomModal);
  }

  var ProductGallery = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);

      this._carousels = Array.from(this.querySelectorAll("media-carousel"));
      this._pageDots = Array.from(this.querySelectorAll("page-dots"));
      this.productGalleryInfo = this.querySelector(".product-gallery__info");

      if (this.productGalleryInfo) {
        this.addEventListener("carousel:change", this._updateCarouselInformation);
      }

      this.addEventListener("lightbox:open", (event) => this.openZoom(event.detail.index));
    }

    _updateCarouselInformation(event) {
      if (event.detail.slide.getAttribute("data-media-type") === "image") {
        this.productGalleryInfo.classList.remove("is-hidden");
      } else {
        this.productGalleryInfo.classList.add("is-hidden");
      }
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const filteredIndexes = this._getFilteredMediaIndexes(event.detail.product, event.detail.variant);

      this._carousels.forEach((carousel) => carousel.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes } })));

      this._pageDots.forEach((pageDots) => pageDots.dispatchEvent(new CustomEvent("control:filter", { detail: { filteredIndexes } })));

      if (event.detail.variant["featured_media"] && event.detail.previousVariant["featured_media"] && event.detail.previousVariant["featured_media"]["id"] !== event.detail.variant["featured_media"]["id"] || !event.detail.previousVariant["featured_media"] && event.detail.variant["featured_media"]) {
        this._carousels.forEach((carousel) => carousel.select(event.detail.variant["featured_media"]["position"] - 1, { animate: false }));
      }
    }

    _getFilteredMediaIndexes(product, variant) {
      const filteredMediaIds = [];

      product["media"].forEach((media) => {
        let matchMedia = variant["featured_media"] && media["position"] === variant["featured_media"]["position"];

        if (media["alt"]?.includes("#") && media["alt"] !== product["title"]) {
          if (!matchMedia) {
            const altParts = media["alt"].split("#"), mediaGroupParts = altParts.pop().split("_");

            product["options"].forEach((option) => {
              if (option["name"].toLowerCase() === mediaGroupParts[0].toLowerCase()) {
                if (variant["options"][option["position"] - 1].toLowerCase() !== mediaGroupParts[1].trim().toLowerCase()) {
                  filteredMediaIds.push(media["position"] - 1);
                }
              }
            });
          }
        }
      });

      return filteredMediaIds;
    }

    openZoom(index = 0) {
      this.querySelector("product-zoom-media-carousel").select(index);
      this.querySelector("product-zoom-modal").show();
    } 
  }

  if (!window.customElements.get("product-gallery")) {
    window.customElements.define("product-gallery", ProductGallery);
  }

  var ProductZoomGallery = class extends ProductGallery {
    connectedCallback() {
      super.connectedCallback();
      this.activeSlideCount = document.querySelector("product-zoom-modal .pagination__active-count");
      this.productInfoText = Array.from(document.querySelectorAll(".zoom-modal__info"));
      this.addEventListener("carousel:change", this._updateCarouselInformation);
    }

    _updateCarouselInformation(event) {
      this.activeSlideCount.innerText = event.detail.index + 1;
      if (event.detail.slide.getAttribute("data-media-type") === "image") {
        this.productInfoText.forEach((element) => {
          element.classList.remove("is-hidden");
        });
      } else {
        this.productInfoText.forEach((element) => {
          element.classList.add("is-hidden");
        });
      }
    }
  }

  if (!window.customElements.get("product-zoom-gallery")) {
    window.customElements.define("product-zoom-gallery", ProductZoomGallery);
  }

  var MediaCarousel = class extends EffectCarousel {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener("carousel:settle", this._onMediaSettled, { signal: this._abortController.signal });
    }

    get loop() {
      return false;
    }

    _onMediaSettled(event) {
      const media = event.detail.slide;
      this.items.filter((item) => ["video", "external_video", "model"].includes(item.getAttribute("data-media-type"))).forEach((item) => item.firstElementChild.pause());
      switch (media.getAttribute("data-media-type")) {
        case "external_video":
        case "video":
          if (this.hasAttribute("media-autoplay")) {
            media.firstElementChild.play();
          }
          break;
      }
    }
  }

  if (!window.customElements.get("media-carousel")) {
    window.customElements.define("media-carousel", MediaCarousel);
  }

  var ProductZoomMediaCarousel = class extends MediaCarousel {
    connectedCallback() {
      super.connectedCallback();
    }
  }

  if (!window.customElements.get("product-zoom-media-carousel")) {
    window.customElements.define("product-zoom-media-carousel", ProductZoomMediaCarousel);
  }

  var ProductZoomButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._onButtonClicked);
    }

    _onButtonClicked() {
      let media = this.closest(".product-gallery__media");
      let openIndex;

      if (media) {
        const visibleImages = this.closest("media-carousel").visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
        openIndex = visibleImages.indexOf(media);
      } else {
        const carousel = this.closest(".product-gallery__media-list-wrapper").querySelector("media-carousel");
        const visibleImages = carousel.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
        openIndex = visibleImages.indexOf(carousel.selectedSlide);
      }

      this.dispatchEvent(new CustomEvent("lightbox:open", {
        bubbles: true,
        detail: {
          index: openIndex
        }
      }));      
    }
  }

  if (!window.customElements.get("product-zoom-button")) {
    window.customElements.define("product-zoom-button", ProductZoomButton, { extends: "button" });
  }

  // create a container and set the full-size image as its background
  function createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    overlay = document.createElement('div');
    prepareOverlay(overlay, overlayImage);

    image.style.opacity = '50%';
    toggleLoadingSpinner(image);

    overlayImage.onload = () => {
      toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
      image.style.opacity = '100%';
    };

    return overlay;
  }

  function prepareOverlay(container, image) {
    container.setAttribute('class', 'image-magnify-full-size');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
  }

  function toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement.parentElement.querySelector(`.loading__spinner`);
    loadingSpinner.classList.toggle('hidden');
  }

  function moveWithHover(image, event, zoomRatio) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }

  function magnify(image, zoomRatio) {
    const overlay = createOverlay(image);
    overlay.onclick = () => overlay.remove();
    overlay.onmousemove = (event) => moveWithHover(image, event, zoomRatio);
    overlay.onmouseleave = () => overlay.remove();
  }

  function enableZoomOnHover(zoomRatio) {
    const images = document.querySelectorAll('.image-magnify-hover');
    images.forEach((image) => {
      image.onclick = (event) => {
        magnify(image, zoomRatio);
        moveWithHover(image, event, zoomRatio);
      };
    });
  }

  enableZoomOnHover(2);

  /*------------------------------------*\
    #IMAGE SECTIONS
  \*------------------------------------*/

  var MultipleImagesWithText = class extends HTMLElement {
    constructor() {
      super();
      this._imageCarousel = this.querySelector("multiple-images-with-text-image-list");
      this._contentCarousel = this.querySelector("multiple-images-with-text-content-list");

      this.addEventListener("control:prev", () => {
        this._imageCarousel.previous();
        this._contentCarousel.previous();
      });

      this.addEventListener("control:next", () => {
        this._imageCarousel.next();
        this._contentCarousel.next();
      });

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          if (event.target.hasAttribute("image-id")) {
            this._imageCarousel.select(this._imageCarousel.items.findIndex((item) => item.id === event.target.getAttribute("image-id")));
          }
        });
      }
    }
  };

  var MultipleImagesWithTextImageList = class extends EffectCarousel {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
      }
    }

    _transitionTo(fromSlide, toSlide, { direction, animate: animate11 }) {
      if (this.getAttribute("layout") !== "stacked") {
        return;
      }

      const transitionSpeed = 0.2;

      if (direction === "next") {
        const fromSlideTransform = getComputedStyle(fromSlide).getPropertyValue("transform");

        return Motion_timeline([
          [fromSlide, { opacity: 0 }, { duration: transitionSpeed }],
          [toSlide, { zIndex: 1 }, { duration: transitionSpeed, zIndex: { easing: "step-start" } }],
          [fromSlide, { opacity: 1, zIndex: -1 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }],
          [toSlide.previousElementSibling, { zIndex: 0 }, { at: "<", easing: "step-start" }]
        ], { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
      } else {
        const toSlideTransform = getComputedStyle(toSlide).getPropertyValue("transform");

        return Motion_timeline([
          [toSlide, { opacity: 0 }, { duration: transitionSpeed }],
          this.items.length >= 3 && [fromSlide.previousElementSibling || this.lastElementChild, { zIndex: -1 }, { easing: "step-start" }],
          [toSlide, { opacity: 1, zIndex: 1 }, { duration: transitionSpeed, at: this.items.length >= 3 ? "<" : "+0", zIndex: { easing: "step-start" } }],
          [fromSlide, { zIndex: 0 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }]
        ].filter(Boolean), { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
      }
    }
  };

  var MultipleImagesWithTextContentList = class extends EffectCarousel {
    constructor() {
      super();
    }

    _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      return Motion_timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }]
      ], { duration: animate11 ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0 }).finished;
    }
  };

  if (!window.customElements.get("multiple-images-with-text")) {
    window.customElements.define("multiple-images-with-text", MultipleImagesWithText);
  }

  if (!window.customElements.get("multiple-images-with-text-image-list")) {
    window.customElements.define("multiple-images-with-text-image-list", MultipleImagesWithTextImageList);
  }

  if (!window.customElements.get("multiple-images-with-text-content-list")) {
    window.customElements.define("multiple-images-with-text-content-list", MultipleImagesWithTextContentList);
  }

  function isImagesWithTextMeasurable(host) {
    if (!host || !host.offsetParent) {
      return false;
    }

    const section = host.closest(".shopify-section");
    return !section || !section.hasAttribute("hidden");
  }

  var ImagesWithTextSwiper = class extends HTMLElement {
    constructor() {
      super();
      this._swiperComponent = null;
      this._textItems = [];
      this._currentIndex = 0;
      this._swiperInstance = null;
      this._contentContainer = null;
      this._resizeHandler = null;
      this._layoutObserver = null;
      this._promoTabsListener = null;
    }

    connectedCallback() {
      this._swiperComponent = this.querySelector("swiper-component");
      this._textItems = Array.from(this.querySelectorAll("[data-images-with-text-text]"));
      this._contentContainer = this.querySelector(".images-with-text-swiper__content-list");

      if (!this._swiperComponent || this._textItems.length === 0 || !this._contentContainer) {
        return;
      }

      this._setupInitialState();
      this._initFakeTabs();
      this._updateContainerHeight();
      this._attachToSwiper();
      this._bindResize();
      this._bindLayoutObserver();

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const targetText = event.target.closest("[data-images-with-text-text]");
          if (!targetText) {
            return;
          }

          const index = this._textItems.indexOf(targetText);

          if (index < 0 || Number.isNaN(index)) {
            return;
          }

          // Ensure text content updates immediately in the editor
          this._showIndex(index, false);

          // Sync the image swiper if it has been initialized
          const swiper = this._swiperComponent && this._swiperComponent.swiper;
          if (swiper) {
            if (typeof swiper.slideToLoop === "function") {
              swiper.slideToLoop(index);
            } else if (typeof swiper.slideTo === "function") {
              swiper.slideTo(index);
            }
          }
        });
      }
    }

    disconnectedCallback() {
      if (this._resizeHandler) {
        window.removeEventListener("resize", this._resizeHandler);
      }

      if (this._layoutObserver) {
        this._layoutObserver.disconnect();
        this._layoutObserver = null;
      }

      if (this._promoTabsListener) {
        const section = this.closest("section");
        const promoTabs = section && section.querySelector("promo-tabs");
        if (promoTabs) {
          promoTabs.removeEventListener("promo-tabs:change", this._promoTabsListener);
        }
        this._promoTabsListener = null;
      }
    }

    refreshLayout() {
      if (!isImagesWithTextMeasurable(this) || !this._textItems.length) {
        return;
      }

      const index = Math.min(Math.max(0, this._currentIndex), this._textItems.length - 1);
      const hasSelectedText = this._textItems.some((item) => item.classList.contains("is-selected"));

      if (!hasSelectedText) {
        this._showIndex(index, false);
      } else {
        const selected = this._textItems[this._currentIndex];
        if (selected) {
          selected.style.opacity = "";
          selected.style.visibility = "";
          this._updateContainerHeight(selected);
        }
      }

      const swiper = this._swiperComponent?.swiper;
      if (swiper) {
        if (typeof swiper.slideToLoop === "function") {
          swiper.slideToLoop(index, 0);
        } else if (typeof swiper.slideTo === "function") {
          swiper.slideTo(index, 0);
        }
      }
    }

    _setupInitialState() {
      this._textItems.forEach((item, index) => {
        if (index === 0) {
          item.classList.add("is-selected");
          item.classList.remove("reveal-invisible");
          item.style.opacity = "";
          item.style.visibility = "";
          item.style.pointerEvents = "auto";
          item.setAttribute("aria-hidden", "false");
        } else {
          if (!item.classList.contains("reveal-invisible")) {
            item.classList.add("reveal-invisible");
          }
          item.style.pointerEvents = "none";
          item.setAttribute("aria-hidden", "true");
        }
      });

      this._currentIndex = 0;
    }

    _attachToSwiper() {
      const tryAttach = () => {
        const swiperInstance = this._swiperComponent.swiper;

        if (!swiperInstance) {
          setTimeout(tryAttach, 100);
          return;
        }

        if (this._swiperInstance === swiperInstance) {
          return;
        }

        this._swiperInstance = swiperInstance;

        const initialIndex = typeof swiperInstance.realIndex === "number" ? swiperInstance.realIndex : swiperInstance.activeIndex || 0;
        this._currentIndex = initialIndex;
        this._showIndex(initialIndex, false);

        swiperInstance.on("slideChange", () => {
          const newIndex = typeof swiperInstance.realIndex === "number" ? swiperInstance.realIndex : swiperInstance.activeIndex || 0;
          this._onSlideChange(newIndex);
        });
      };

      tryAttach();
    }

    _onSlideChange(newIndex) {
      if (newIndex === this._currentIndex) {
        return;
      }

      const fromSlide = this._textItems[this._currentIndex];
      const toSlide = this._textItems[newIndex];

      this._currentIndex = newIndex;
      this._transitionText(fromSlide, toSlide, true);
    }

    _showIndex(index, animate = true) {
      const fromSlide = this._textItems[this._currentIndex];
      const toSlide = this._textItems[index];

      this._currentIndex = index;
      this._transitionText(fromSlide, toSlide, animate);
    }

    _transitionText(fromSlide, toSlide, animate = true) {
      if (!fromSlide || !toSlide || fromSlide === toSlide) {
        return;
      }

      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      fromSlide.classList.remove("reveal-invisible");
      toSlide.classList.remove("reveal-invisible");

      fromSlide.style.pointerEvents = "none";
      toSlide.style.pointerEvents = "auto";

      fromSlide.setAttribute("aria-hidden", "true");
      toSlide.setAttribute("aria-hidden", "false");

      this._updateContainerHeight(toSlide);

      return Motion_timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }]
      ], {
        duration: animate ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0
      }).finished;
    }

    _updateContainerHeight(target) {
      if (!this._contentContainer || !isImagesWithTextMeasurable(this)) {
        return;
      }

      const slide = target || this._textItems[this._currentIndex];
      if (!slide) {
        return;
      }

      const targetHeight = slide.offsetHeight;
      if (!targetHeight) {
        return;
      }

      this._contentContainer.style.height = `${targetHeight}px`;
    }

    _bindResize() {
      this._resizeHandler = debounce(() => {
        this._updateContainerHeight();
      }, 150);

      window.addEventListener("resize", this._resizeHandler);
    }

    _bindLayoutObserver() {
      if (!window.ResizeObserver) {
        return;
      }

      this._layoutObserver = new ResizeObserver(
        debounce(() => {
          if (isImagesWithTextMeasurable(this)) {
            this._updateContainerHeight();
          }
        }, 150)
      );
      this._layoutObserver.observe(this);
    }

    _initFakeTabs() {
      const section = this.closest("section");
      if (!section) {
        return;
      }

      const promoTabs = section.querySelector("promo-tabs");
      if (!promoTabs) {
        return;
      }

      const controlsId = promoTabs.getAttribute("controls-id");
      const controlsRoot = controlsId ? document.getElementById(controlsId) || null : promoTabs;

      if (!controlsRoot) {
        return;
      }

      const realTabs = Array.from(controlsRoot.querySelectorAll(".tab-scroller__nav-item"));
      if (!realTabs.length) {
        return;
      }

      this._realTabs = realTabs;

      const fakeContainers = Array.from(this.querySelectorAll("[data-images-with-text-fake-tabs]"));
      if (!fakeContainers.length) {
        return;
      }

      fakeContainers.forEach((container) => {
        const nav = container.querySelector(".tab-scroller__nav");
        if (!nav) {
          return;
        }

        nav.innerHTML = "";

        realTabs.forEach((realButton, index) => {
          const fakeButton = realButton.cloneNode(true);

          fakeButton.removeAttribute("id");
          fakeButton.setAttribute("type", "button");
          fakeButton.setAttribute("data-images-with-text-fake-tab-index", String(index));
          fakeButton.addEventListener("click", () => {
            realButton.click();
          });

          nav.appendChild(fakeButton);
        });
      });

      let selectedIndex = realTabs.findIndex(
        (button) => button.getAttribute("aria-selected") === "true"
      );
      if (selectedIndex === -1) {
        selectedIndex = 0;
      }

      this._updateFakeTabsSelection(selectedIndex);

      this._promoTabsListener = (event) => {
        if (!event.detail || typeof event.detail.index !== "number") {
          return;
        }
        this._updateFakeTabsSelection(event.detail.index);
        requestAnimationFrame(() => this.refreshLayout());
      };

      promoTabs.addEventListener("promo-tabs:change", this._promoTabsListener);
    }

    _updateFakeTabsSelection(selectedIndex) {
      const fakeButtons = Array.from(
        this.querySelectorAll("[data-images-with-text-fake-tabs] .tab-scroller__nav-item")
      );

      if (!fakeButtons.length) {
        return;
      }

      fakeButtons.forEach((button) => {
        const indexAttr = button.getAttribute("data-images-with-text-fake-tab-index");
        const index = indexAttr === null ? -1 : parseInt(indexAttr, 10);
        const isSelected = index === selectedIndex;

        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
      });
    }
  };

  if (!window.customElements.get("images-with-text-swiper")) {
    window.customElements.define("images-with-text-swiper", ImagesWithTextSwiper);
  }

  var ImagesWithTextCarousel = class extends HTMLElement {
    constructor() {
      super();
      this._scrollCarousel = null;
      this._textItems = [];
      this._currentIndex = 0;
      this._contentContainer = null;
      this._resizeHandler = null;
      this._layoutObserver = null;
      this._promoTabsListener = null;
      this._carouselChangeHandler = null;
    }

    connectedCallback() {
      this._scrollCarousel = this.querySelector("scroll-carousel");
      this._textItems = Array.from(this.querySelectorAll("[data-images-with-text-text]"));
      this._contentContainer = this.querySelector(".images-with-text-swiper__content-list");

      if (!this._scrollCarousel || this._textItems.length === 0 || !this._contentContainer) {
        return;
      }

      this._setupInitialState();
      this._initFakeTabs();
      this._updateContainerHeight();
      this._bindLayoutObserver();

      this._carouselChangeHandler = (event) => {
        if (typeof event.detail?.index !== "number") {
          return;
        }
        this._onSlideChange(event.detail.index);
      };
      this._scrollCarousel.addEventListener("carousel:change", this._carouselChangeHandler);

      const initialIndex = this._scrollCarousel.selectedIndex ?? 0;
      this._showIndex(initialIndex, false);

      this._bindResize();

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const targetText = event.target.closest("[data-images-with-text-text]");
          if (!targetText) {
            return;
          }

          const index = this._textItems.indexOf(targetText);

          if (index < 0 || Number.isNaN(index)) {
            return;
          }

          this._showIndex(index, false);

          if (this._scrollCarousel && typeof this._scrollCarousel.select === "function") {
            this._scrollCarousel.select(index, { animate: false });
          }
        });
      }
    }

    disconnectedCallback() {
      if (this._carouselChangeHandler && this._scrollCarousel) {
        this._scrollCarousel.removeEventListener("carousel:change", this._carouselChangeHandler);
        this._carouselChangeHandler = null;
      }

      if (this._resizeHandler) {
        window.removeEventListener("resize", this._resizeHandler);
      }

      if (this._layoutObserver) {
        this._layoutObserver.disconnect();
        this._layoutObserver = null;
      }

      if (this._promoTabsListener) {
        const section = this.closest("section");
        const promoTabs = section && section.querySelector("promo-tabs");
        if (promoTabs) {
          promoTabs.removeEventListener("promo-tabs:change", this._promoTabsListener);
        }
        this._promoTabsListener = null;
      }
    }

    refreshLayout() {
      if (!isImagesWithTextMeasurable(this) || !this._textItems.length) {
        return;
      }

      const index = Math.min(Math.max(0, this._currentIndex), this._textItems.length - 1);
      const hasSelectedText = this._textItems.some((item) => item.classList.contains("is-selected"));

      if (!hasSelectedText) {
        this._showIndex(index, false);
      } else {
        const selected = this._textItems[this._currentIndex];
        if (selected) {
          selected.style.opacity = "";
          selected.style.visibility = "";
          this._updateContainerHeight(selected);
        }
      }

      if (this._scrollCarousel && typeof this._scrollCarousel.select === "function") {
        this._scrollCarousel.select(index, { animate: false, force: true });
      }
    }

    _setupInitialState() {
      this._textItems.forEach((item, index) => {
        if (index === 0) {
          item.classList.add("is-selected");
          item.classList.remove("reveal-invisible");
          item.style.opacity = "";
          item.style.visibility = "";
          item.style.pointerEvents = "auto";
          item.setAttribute("aria-hidden", "false");
        } else {
          if (!item.classList.contains("reveal-invisible")) {
            item.classList.add("reveal-invisible");
          }
          item.style.pointerEvents = "none";
          item.setAttribute("aria-hidden", "true");
        }
      });

      this._currentIndex = 0;
    }

    _onSlideChange(newIndex) {
      if (newIndex === this._currentIndex) {
        return;
      }

      const fromSlide = this._textItems[this._currentIndex];
      const toSlide = this._textItems[newIndex];

      this._currentIndex = newIndex;
      this._transitionText(fromSlide, toSlide, true);
    }

    _showIndex(index, animate = true) {
      const fromSlide = this._textItems[this._currentIndex];
      const toSlide = this._textItems[index];

      this._currentIndex = index;
      this._transitionText(fromSlide, toSlide, animate);
    }

    _transitionText(fromSlide, toSlide, animate = true) {
      if (!fromSlide || !toSlide || fromSlide === toSlide) {
        return;
      }

      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      fromSlide.classList.remove("reveal-invisible");
      toSlide.classList.remove("reveal-invisible");

      fromSlide.style.pointerEvents = "none";
      toSlide.style.pointerEvents = "auto";

      fromSlide.setAttribute("aria-hidden", "true");
      toSlide.setAttribute("aria-hidden", "false");

      this._updateContainerHeight(toSlide);

      return Motion_timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }]
      ], {
        duration: animate ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0
      }).finished;
    }

    _updateContainerHeight(target) {
      if (!this._contentContainer || !isImagesWithTextMeasurable(this)) {
        return;
      }

      const slide = target || this._textItems[this._currentIndex];
      if (!slide) {
        return;
      }

      const targetHeight = slide.offsetHeight;
      if (!targetHeight) {
        return;
      }

      this._contentContainer.style.height = `${targetHeight}px`;
    }

    _bindResize() {
      this._resizeHandler = debounce(() => {
        this._updateContainerHeight();
      }, 150);

      window.addEventListener("resize", this._resizeHandler);
    }

    _bindLayoutObserver() {
      if (!window.ResizeObserver) {
        return;
      }

      this._layoutObserver = new ResizeObserver(
        debounce(() => {
          if (isImagesWithTextMeasurable(this)) {
            this._updateContainerHeight();
          }
        }, 150)
      );
      this._layoutObserver.observe(this);
    }

    _initFakeTabs() {
      const section = this.closest("section");
      if (!section) {
        return;
      }

      const promoTabs = section.querySelector("promo-tabs");
      if (!promoTabs) {
        return;
      }

      const controlsId = promoTabs.getAttribute("controls-id");
      const controlsRoot = controlsId ? document.getElementById(controlsId) || null : promoTabs;

      if (!controlsRoot) {
        return;
      }

      const realTabs = Array.from(controlsRoot.querySelectorAll(".tab-scroller__nav-item"));
      if (!realTabs.length) {
        return;
      }

      this._realTabs = realTabs;

      const fakeContainers = Array.from(this.querySelectorAll("[data-images-with-text-fake-tabs]"));
      if (!fakeContainers.length) {
        return;
      }

      fakeContainers.forEach((container) => {
        const nav = container.querySelector(".tab-scroller__nav");
        if (!nav) {
          return;
        }

        nav.innerHTML = "";

        realTabs.forEach((realButton, index) => {
          const fakeButton = realButton.cloneNode(true);

          fakeButton.removeAttribute("id");
          fakeButton.setAttribute("type", "button");
          fakeButton.setAttribute("data-images-with-text-fake-tab-index", String(index));
          fakeButton.addEventListener("click", () => {
            realButton.click();
          });

          nav.appendChild(fakeButton);
        });
      });

      let selectedIndex = realTabs.findIndex(
        (button) => button.getAttribute("aria-selected") === "true"
      );
      if (selectedIndex === -1) {
        selectedIndex = 0;
      }

      this._updateFakeTabsSelection(selectedIndex);

      this._promoTabsListener = (event) => {
        if (!event.detail || typeof event.detail.index !== "number") {
          return;
        }
        this._updateFakeTabsSelection(event.detail.index);
        requestAnimationFrame(() => this.refreshLayout());
      };

      promoTabs.addEventListener("promo-tabs:change", this._promoTabsListener);
    }

    _updateFakeTabsSelection(selectedIndex) {
      const fakeButtons = Array.from(
        this.querySelectorAll("[data-images-with-text-fake-tabs] .tab-scroller__nav-item")
      );

      if (!fakeButtons.length) {
        return;
      }

      fakeButtons.forEach((button) => {
        const indexAttr = button.getAttribute("data-images-with-text-fake-tab-index");
        const index = indexAttr === null ? -1 : parseInt(indexAttr, 10);
        const isSelected = index === selectedIndex;

        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
      });
    }
  };

  if (!window.customElements.get("images-with-text-carousel")) {
    window.customElements.define("images-with-text-carousel", ImagesWithTextCarousel);
  }

  var ImageLinkBlocks = class extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      new ScrollArea(this);
      this.addEventListener("control:prev", this._prev);
      this.addEventListener("control:next", this._next);
    }

    _prev() {
      this.scrollBy({ left: (document.dir === "rtl" ? 1 : -1) * this.items[0].clientWidth, behavior: "smooth" });
    }
    _next() {
      this.scrollBy({ left: (document.dir === "rtl" ? -1 : 1) * this.items[0].clientWidth, behavior: "smooth" });
    }
  };
  
  if (!window.customElements.get("image-link-blocks")) {
    window.customElements.define("image-link-blocks", ImageLinkBlocks);
  }

  /**
   * IMPORTANT: This is no longer being used for the collection SEO block as it depended on truncation within Liquid which caused issues with HTML content.
   * 
   * A custom element for "read more"/"read less" functionality. We essentially have duplicate elements (a truncated and full version). On clicking "read more" we swap in the full element.
   */
  var ContentSwap = class extends HTMLElement {
    constructor() {
      super();

      this.shortContent = this.querySelector(".content-swap__short");
      this.fullContent = this.querySelector(".content-swap__full");
      this.moreButton = this.querySelector('[data-action="show-more"]');
      this.lessButton = this.querySelector('[data-action="show-less"]');

      this.lessButton.addEventListener("click", this.showLess.bind(this));
      this.moreButton.addEventListener("click", this.showMore.bind(this));
    }

    showLess(event) {
      event.preventDefault();
      this.fullContent.style.display = 'none';
      this.shortContent.style.display = 'block';
    }

    showMore(event) {
      event.preventDefault();
      this.shortContent.style.display = 'none';
      this.fullContent.style.display = 'block';
    }
  };

  if (!window.customElements.get("content-swap")) {
    window.customElements.define("content-swap", ContentSwap);
  }

  /**
   * A new component used for "read more"/"read less" Collection SEO functionality. This is dependent on a fix height and javascript so no truncation is required meaning any HTML content is unaffected.
   */
  class ExpandableContent extends HTMLElement {
    constructor() {
      super();
  
      // Get the toggle button and main content
      this.expandableToggleButton = this.querySelector(".expandable-content__toggle");
      this.mainContent = this.querySelector(".expandable-content__main");
      this.expanded = false; // Track the current state (collapsed by default)
      this._resizeObserver = null;
      
      // Set initial ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "false");
      this.expandableToggleButton.setAttribute("aria-controls", this.id || this.mainContent.id || "expandable-content-main");
      this.mainContent.setAttribute("aria-hidden", "true");
  
      // Bind the click handler to the toggle button
      this.expandableToggleButton.addEventListener("click", () => this.toggleContent());
    }

    _getCollapsedMaxHeight() {
      const maxHeight = parseFloat(getComputedStyle(this.mainContent).maxHeight);
      return Number.isFinite(maxHeight) ? maxHeight : 320;
    }

    _updateExpandability() {
      if (this.expanded) {
        return;
      }

      this._collapsedMaxHeight = this._getCollapsedMaxHeight();

      if (this.mainContent.scrollHeight > this._collapsedMaxHeight) {
        this.classList.add("expandable-content--expandable");
      } else {
        this.classList.remove("expandable-content--expandable");
      }
    }
  
    connectedCallback() {
      this._updateExpandability();

      this._resizeObserver = new ResizeObserver(() => this._updateExpandability());
      this._resizeObserver.observe(this.mainContent);
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
    }
  
    toggleContent() {
      if (this.expanded) {
        this.collapseContent();
      } else {
        this.expandContent();
      }
    }
  
    expandContent() {
      this.mainContent.style.height = `${this.mainContent.scrollHeight}px`; // Set height to scrollHeight temporarily for smooth transition
      this.mainContent.style.height = 'auto'; // Set height to auto after transition
      this.classList.add("expandable-content--expanded");
      this.expanded = true;
  
      // Update ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "true");
      this.mainContent.setAttribute("aria-hidden", "false");
    }
  
    collapseContent() {
      const collapsedHeight = this._collapsedMaxHeight ?? this._getCollapsedMaxHeight();
      this.mainContent.style.height = `${collapsedHeight}px`;
      this.classList.remove("expandable-content--expanded");
      this.expanded = false;
  
      // Update ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "false");
      this.mainContent.setAttribute("aria-hidden", "true");
      this.mainContent.scrollIntoView({ behavior: "smooth"});
    }
  }
  
  if (!window.customElements.get("expandable-content")) {
    window.customElements.define("expandable-content", ExpandableContent);
  }

  /**
   * Info blocks section: reveal cards in batches with optional sessionStorage restore (back/forward).
   */
  class InfoBlocksLoadMore extends HTMLElement {
    constructor() {
      super();
      this._onLoadMore = this._onLoadMore.bind(this);
      this._onShowAll = this._onShowAll.bind(this);
      this._onPageShow = this._onPageShow.bind(this);
    }

    connectedCallback() {
      this.perPage = parseInt(this.dataset.perPage, 10) || 9;
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.maxBatches = parseInt(this.dataset.maxBatches, 10) || 1;
      this.remember = this.dataset.remember === "true";
      this.showStatus = this.dataset.showStatus === "true";
      const storagePrefix = this.dataset.storagePrefix || "info-blocks";
      this.storageKey = `${storagePrefix}:${this.dataset.sectionId || ""}`;
      this.statusTemplate = this.dataset.statusTemplate || "";
      const cardSelector = this.dataset.cardSelector || ".info-blocks-card";
      this.cards = Array.from(this.querySelectorAll(cardSelector));
      this.loadMoreBtn = this.querySelector(".info-blocks-load-more__btn:not(.info-blocks-load-more__btn--all)");
      this.showAllBtn = this.querySelector(".info-blocks-load-more__btn--all");
      this.statusEl = this.querySelector("[data-info-blocks-status]");
      this.buttonsRow = this.querySelector(".info-blocks-load-more__buttons");
      this.footerEl = this.querySelector(".info-blocks-load-more__footer");

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        this.cards.forEach((card) => card.removeAttribute("hidden"));
        return;
      }

      const restored = this.restoreState();
      this.visibleBatches =
        restored != null ? Math.min(Math.max(1, restored), this.maxBatches) : 1;

      this.applyVisibility();
      this.updateButtons();
      this.updateStatus();

      this.loadMoreBtn?.addEventListener("click", this._onLoadMore);
      this.showAllBtn?.addEventListener("click", this._onShowAll);
      window.addEventListener("pageshow", this._onPageShow);
    }

    disconnectedCallback() {
      window.removeEventListener("pageshow", this._onPageShow);
      this.loadMoreBtn?.removeEventListener("click", this._onLoadMore);
      this.showAllBtn?.removeEventListener("click", this._onShowAll);
    }

    restoreState() {
      if (!this.remember) {
        return null;
      }
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) {
          return null;
        }
        const data = JSON.parse(raw);
        if (typeof data.visibleBatches === "number") {
          return data.visibleBatches;
        }
      } catch (_) {}
      return null;
    }

    persistState() {
      if (!this.remember) {
        return;
      }
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({ visibleBatches: this.visibleBatches })
        );
      } catch (_) {}
    }

    applyVisibility() {
      const maxVisible = this.visibleBatches * this.perPage;
      this.cards.forEach((card) => {
        const idx = parseInt(card.dataset.infoBlocksIndex, 10);
        if (Number.isFinite(idx) && idx <= maxVisible) {
          card.removeAttribute("hidden");
        } else {
          card.setAttribute("hidden", "");
        }
      });
    }

    visibleCount() {
      return Math.min(this.total, this.visibleBatches * this.perPage);
    }

    updateStatus() {
      if (!this.statusEl || !this.showStatus) {
        return;
      }
      const visible = this.visibleCount();
      const text = this.statusTemplate.replace("__V__", String(visible)).replace("__T__", String(this.total));
      this.statusEl.textContent = text;
      this.statusEl.removeAttribute("hidden");
    }

    updateButtons() {
      const hasMore = this.visibleBatches < this.maxBatches;
      if (this.loadMoreBtn) {
        this.loadMoreBtn.hidden = !hasMore;
        this.loadMoreBtn.setAttribute("aria-expanded", hasMore ? "false" : "true");
      }
      if (this.showAllBtn) {
        this.showAllBtn.hidden = !hasMore;
      }
      if (this.buttonsRow) {
        this.buttonsRow.hidden = !hasMore;
      }
      if (this.footerEl) {
        const showFooter = hasMore || (this.showStatus && this.statusEl);
        this.footerEl.hidden = !showFooter;
      }
    }

    _onLoadMore() {
      if (this.visibleBatches >= this.maxBatches) {
        return;
      }
      this.visibleBatches += 1;
      this.applyVisibility();
      this.updateButtons();
      this.updateStatus();
      this.persistState();
    }

    _onShowAll() {
      this.visibleBatches = this.maxBatches;
      this.applyVisibility();
      this.updateButtons();
      this.updateStatus();
      this.persistState();
    }

    _onPageShow() {
      if (!this.remember) {
        return;
      }
      const restored = this.restoreState();
      if (restored == null) {
        return;
      }
      this.visibleBatches = Math.min(Math.max(1, restored), this.maxBatches);
      this.applyVisibility();
      this.updateButtons();
      this.updateStatus();
    }
  }

  if (!window.customElements.get("info-blocks-load-more")) {
    window.customElements.define("info-blocks-load-more", InfoBlocksLoadMore);
  }

  /**
   * Accordion-like elements for the Shop The Look section.
   */
  var ShopTheLookAccordion = class extends HTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this.summaryElement = this.querySelector('.shop-the-look-accordion__toggle');
      this.contentElement = this.querySelector('.shop-the-look-accordion__content');
      this._open = this.hasAttribute("open");
      this.summaryElement.addEventListener("click", (event) => this.toggleElement({ event }));
      this.setAttribute("aria-expanded", this._open ? "true" : "false");
      this.summaryElement.setAttribute("aria-expanded", this._open ? "true" : "false");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }

      this.setAttribute("aria-expanded", value ? "true" : "false");
      this.summaryElement?.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return this._open;
    }

    get collapseNonActiveElements() {
      return this.hasAttribute("collapse-non-active-elements");
    }

    get scrollToActive() {
      return this.hasAttribute("scroll-to-active");
    }

    scrollToActiveElement(element) {
      if (window.matchMedia("(max-width: 999px)").matches) {
        element.scrollIntoView({ behavior: "smooth"});
      }
    }

    closeSiblingAccordions() {
      if (!this.collapseNonActiveElements || !this.parentElement) {
        return;
      }

      Array.from(this.parentElement.querySelectorAll("shop-the-look-accordion")).forEach((accordionElement) => {
        if (accordionElement !== this) {
          accordionElement.open = false;
        }
      });
    }

    openElement({ event } = {}) {
      event?.preventDefault();
      this.closeSiblingAccordions();
      this.open = true;
    }

    closeElement({ event } = {}) {
      event?.preventDefault();
      this.open = false;
    }

    toggleElement({ event } = {}) {
      event?.preventDefault();

      if (this.open) {
        this.closeElement();
      } else {
        this.openElement();
      }
    }

    async _transition(value) {
      this.contentElement.style.overflow = "hidden";
      if (value) {
        this.setAttribute("open", "");
        await Motion_timeline([
          [this.contentElement, { height: [0, `${this.contentElement.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
          [this.contentElement, { opacity: [0, 1], transform: ["translateY(0)", "translateY(-4px)"] }, { duration: 0.15, at: "-0.1" }]
        ]).finished;

        this.contentElement.style.height = "auto";
        this.contentElement.style.overflow = "visible";

        if (this.scrollToActive) {
          this.scrollToActiveElement(this);
        }

        this.dispatchEvent(new CustomEvent("openable-element:open", {
          bubbles: true
        }));
      } else {
        await Motion_timeline([
          [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
          [this.contentElement, { height: [`${this.contentElement.clientHeight}px`, 0] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;

        this.contentElement.style.height = "0";
        this.contentElement.style.overflow = "hidden";

        this.dispatchEvent(new CustomEvent("openable-element:close", {
          bubbles: true
        }));
        
        this.removeAttribute("open");
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open") {
        this.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
        this.summaryElement?.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
      }
    }
  }

  if (!window.customElements.get("shop-the-look-accordion")) {
    window.customElements.define("shop-the-look-accordion", ShopTheLookAccordion);
  }

  var ImageTextSlideshow = class extends EffectCarousel {
    _pendingAnimationControls = [];

    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      if (this._player && this.hasAttribute("autoplay") && window.matchMedia("screen and (min-width: 1000px)").matches) {
        this._player.addEventListener("player:start", (event) => {
          const circles = Array.from(this.querySelectorAll(".numbered-dots__item"));

          circles.forEach((item) => {
            const circle = item.querySelector("circle:last-child");

            let circleAnimationControl = null;

            if (item.parentElement.parentElement.getAttribute("aria-current") === "true") {
              circleAnimationControl = Motion_animate(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: event.detail.duration, easing: "linear" });
            } else {
              circleAnimationControl = Motion_animate(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
            }

            this._pendingAnimationControls.push(circleAnimationControl);
          });

          this._player.addEventListener("player:pause", () => {
            this._pendingAnimationControls.forEach((control) => control.pause());
          });

          this._player.addEventListener("player:resume", () => {
            this._pendingAnimationControls.forEach((control) => control.play());
          });
        })
      }
    }
  }

  if (!window.customElements.get("image-text-slideshow")) {
    window.customElements.define("image-text-slideshow", ImageTextSlideshow);
  }

  var VyneOnlineSwitcher = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.slideshows = Array.from(this.querySelectorAll(".image-text-slideshow"));
      this.select = this.querySelector("#vyne-online-switcher");
      this.select.addEventListener("change", this._onOptionChanged.bind(this));
      this.currentElement = this.querySelector(".image-text-slideshow:not(.hidden)");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          this.currentElement.querySelector("effect-carousel").player?.pause();
          const optionSelected = event.target.parentElement.getAttribute("group");

          this.slideshows.forEach((element) => {
            element.classList.add("hidden");
    
            if (element.id == optionSelected) {
              element.classList.remove("hidden");
    
              if (window.matchMedia("screen and (min-width: 1000px)").matches) {
                element.querySelector("effect-carousel").player?.resume();
              }
              this.currentElement = element;
            }
          });
        });
      }
    }

    _onOptionChanged(event) {
      this.currentElement.querySelector("effect-carousel").player?.pause();
      const optionSelected = event.target.value;

      this.slideshows.forEach((element) => {
        element.classList.add("hidden");

        if (element.id == optionSelected) {
          element.classList.remove("hidden");

          if (window.matchMedia("screen and (min-width: 1000px)").matches) {
            element.querySelector("effect-carousel").player?.resume();
          }
          this.currentElement = element;
        }
      });
    }
  }

  if (!window.customElements.get("vyne-online-switcher")) {
    window.customElements.define("vyne-online-switcher", VyneOnlineSwitcher);
  }

  var ImageWithDownload = class extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector(".form");
      this.form.addEventListener("submit", this._onFormSubmitted.bind(this));
      this.formInput = this.querySelector('input[type="email"]');
      this.formCheckbox = this.querySelector('input[type="checkbox"]');
      this.resultsElement = this.querySelector(".form__results");
      this.submitButton = this.querySelector('button[type="submit"]');
      this.apiKey = this.getAttribute("api-key");
      this.listId = this.getAttribute("list-id");
      this.successText = this.getAttribute("success-text");
      this.fileUrl = this.getAttribute("file-url"); // Attribute for file download URL
    }

    get checkboxChecked() {
      return this.formCheckbox.checked;
    }

    async _onFormSubmitted(event) {
      event.preventDefault();
      this.resultsElement.hidden = true;
      this.resultsElement.innerHTML = "";

      const email = this.formInput.value;

      this.submitButton.setAttribute("aria-busy", "true");

      if (this.checkboxChecked) {
        const newsletterUrl = "https://a.klaviyo.com/client/subscriptions/?company_id=" + this.apiKey;

        const payload = { 
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Image with download form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  },
                }
              }
            },
            relationships: {list: {data: {type: 'list', id: `${this.listId}`}}}
          }
        }

        var requestOptions = {
          method: 'POST',
          headers: {
            revision: '2025-01-15', 
            'content-type': 'application/vnd.api+json'
          },
          body: JSON.stringify(payload),
        };

        const response = await fetch(newsletterUrl, requestOptions);

        if (response.ok) {
          console.log(response);
          this.resultsElement.innerHTML = `<span class="text-success">${this.successText}</span>`;
          this.resultsElement.hidden = false;
        } else {
          const errors = await response.json();
          console.log(errors);
          this.resultsElement.innerHTML = `<span class="text-error">${errors["errors"][0]["detail"]}</span>`;
          this.resultsElement.hidden = false;
        }
      }

      // Trigger file download irrespective of the Klaviyo API response
      this._downloadFile();

      this.submitButton.setAttribute("aria-busy", "false");
    }
    
    _downloadFile() {
      // Create an XMLHttpRequest to fetch the file
      fetch(this.fileUrl)
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.fileUrl.split('/').pop(); // Use the file name from the URL for download
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url); // Clean up the URL object
        })
        .catch(error => console.error('File download failed:', error));
    }
  }

  if (!window.customElements.get("image-with-download")) {
    window.customElements.define("image-with-download", ImageWithDownload);
  }


  /*------------------------------------*\
    #BLOG SECTIONS
  \*------------------------------------*/

  var BlogEffectCarousel = class extends EffectCarousel {
    connectedCallback() {
      super.connectedCallback();

      let blogHandle = this.selectedSlide.getAttribute("blog-handle") || "support-advice";
      let tagHandle = this.selectedSlide.getAttribute("tag-handle");
      let element = this.selectedSlide;
      let numberOfPosts = parseInt(this.selectedSlide.getAttribute("number-of-posts"));

      _fetchBlogPosts(blogHandle, tagHandle, element, numberOfPosts);

      this.addEventListener("carousel:select", () => {
        blogHandle = this.selectedSlide.getAttribute("blog-handle") || "support-advice";
        tagHandle = this.selectedSlide.getAttribute("tag-handle");
        element = this.selectedSlide;
        numberOfPosts = parseInt(this.selectedSlide.getAttribute("number-of-posts"));
        _fetchBlogPosts(blogHandle, tagHandle, element, numberOfPosts)
      }, { signal: this._abortController.signal });
    }

    _fetchBlogPosts() {
      if (this.selectedSlide.hasAttribute("blog-posts-loaded")) {
        return;
      }

      if (!this.selectedSlide.getAttribute("blog-handle")) {
        return;
      }

      const blogHandle = this.selectedSlide.getAttribute("blog-handle");
      const tagHandle = this.selectedSlide.getAttribute("tag-handle");

      try {
        return this._loadBlog(blogHandle, tagHandle);
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }

    async _loadBlog(blogHandle, tagHandle) {
      const newElement = new DOMParser().parseFromString(await (await cachedFetch(_blogFetchUrl(blogHandle, tagHandle), { signal: this._abortController.signal })).text(), "text/html");

      const blogPostsResults = this.selectedSlide.querySelector(".blog-posts-list");
      const blogResultsChildren = newElement.querySelector(".blog-results").children;
      const buttonWrapper = this.selectedSlide.querySelector(".blog-posts__button-wrapper");

      blogPostsResults.innerHTML = "";

      for (const child of blogResultsChildren) {
        blogPostsResults.appendChild(document.importNode(child, true));
      }

      buttonWrapper.style.opacity = "1";

      this.selectedSlide.setAttribute("blog-posts-loaded","");
    }
  }

  if (!window.customElements.get("blog-effect-carousel")) {
    window.customElements.define("blog-effect-carousel", BlogEffectCarousel);
  }

  class BlogTabs extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._selectedIndex = 0;
    }

    connectedCallback() {
      this._abortController = new AbortController();

      this.navButtons = Array.from(this.querySelectorAll(".tab-scroller__nav-item"));
      this.panels = Array.from(this.querySelectorAll("[data-blog-panel]"));

      if (!this.navButtons.length || !this.panels.length) {
        return;
      }

      this.navButtons.forEach((button, index) => {
        button.addEventListener("click", () => this._onTabClick(index), { signal: this._abortController.signal });
      });

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const index = this.panels.findIndex((panel) => panel.contains(event.target));

          if (index !== -1) {
            this._onTabClick(index, { animate: false });
          }
        }, { signal: this._abortController.signal });
      }

      const initiallySelectedIndex = this.navButtons.findIndex((button) => button.getAttribute("aria-selected") === "true");
      this._selectedIndex = initiallySelectedIndex !== -1 ? initiallySelectedIndex : 0;

      this.navButtons.forEach((button, index) => {
        button.setAttribute("aria-selected", index === this._selectedIndex ? "true" : "false");
        button.setAttribute("tabindex", index === this._selectedIndex ? "0" : "-1");
      });

      this.panels.forEach((panel, index) => {
        panel.hidden = index !== this._selectedIndex;
        if (index === this._selectedIndex) {
          panel.classList.add("is-selected");
          panel.classList.remove("reveal-invisible");
        } else {
          panel.classList.remove("is-selected");
        }
      });

      this._scrollSelectedIntoView();
      this._loadBlogForPanel(this.panels[this._selectedIndex]);
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
    }

    _onTabClick(index, { animate = true } = {}) {
      if (index === this._selectedIndex || index < 0 || index >= this.panels.length) {
        return;
      }

      const previousIndex = this._selectedIndex;
      this._selectedIndex = index;

      this.navButtons.forEach((button, buttonIndex) => {
        button.setAttribute("aria-selected", buttonIndex === this._selectedIndex ? "true" : "false");
        button.setAttribute("tabindex", buttonIndex === this._selectedIndex ? "0" : "-1");
      });

      this._scrollSelectedIntoView();

      const fromPanel = this.panels[previousIndex];
      const toPanel = this.panels[this._selectedIndex];

      if (animate) {
        this._transitionPanels(fromPanel, toPanel);
      } else {
        if (fromPanel) {
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");
      }

      this._loadBlogForPanel(toPanel);
    }

    _scrollSelectedIntoView() {
      if (!this.hasAttribute("scrolling-tabs")) {
        return;
      }

      const scrollElement = this.querySelector(".tab-scroller__controls") || this.querySelector(".tab-scroller__nav") || this;
      const selectedButton = this.navButtons[this._selectedIndex];

      if (!scrollElement || !selectedButton) {
        return;
      }

      scrollElement.scrollTo({
        left: selectedButton.offsetLeft - scrollElement.clientWidth / 2 + selectedButton.clientWidth / 2,
        top: selectedButton.offsetTop - scrollElement.clientHeight / 2 - selectedButton.clientHeight / 2,
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
      });
    }

    async _transitionPanels(fromPanel, toPanel) {
      if (fromPanel === toPanel) {
        return;
      }

      try {
        if (fromPanel) {
          await Motion_animate(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");

        await Motion_animate(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
      } catch (e) {
        if (fromPanel) {
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");
      }
    }

    async _loadBlogForPanel(panel) {
      if (!panel) {
        return;
      }

      if (!panel.getAttribute("data-blog-handle") || panel.hasAttribute("data-blog-posts-loaded")) {
        if (panel.hasAttribute("data-blog-posts-loaded")) {
          const blogPostsResults = panel.querySelector(".blog-posts-list");
          const scrollCarousel = blogPostsResults?.closest("scroll-carousel");

          if (scrollCarousel && typeof scrollCarousel.refreshItems === "function") {
            queueMicrotask(() => scrollCarousel.refreshItems());
          }
        }

        return;
      }

      const blogHandle = panel.getAttribute("data-blog-handle");
      const tagHandle = panel.getAttribute("data-tag-handle");
      const numberOfPosts = parseInt(panel.getAttribute("data-number-of-posts") || "3", 10);

      try {
        const response = await cachedFetch(_blogFetchUrl(blogHandle, tagHandle), { signal: this._abortController.signal });
        const html = await response.text();
        const newElement = new DOMParser().parseFromString(html, "text/html");

        const blogPostsResults = panel.querySelector(".blog-posts-list");
        const blogResults = newElement.querySelector(".blog-results");
        const buttonWrapper = panel.querySelector(".blog-posts__button-wrapper");
        const scrollCarousel = blogPostsResults?.closest("scroll-carousel");

        if (blogPostsResults && blogResults) {
          blogPostsResults.innerHTML = "";

          let count = 0;

          for (const child of blogResults.children) {
            const importedNode = document.importNode(child, true);

            blogPostsResults.appendChild(importedNode);

            count += 1;

            if (count >= numberOfPosts) {
              break;
            }
          }

          if (scrollCarousel && typeof scrollCarousel.refreshItems === "function") {
            scrollCarousel.refreshItems();
          }
        }

        if (buttonWrapper) {
          buttonWrapper.style.opacity = "1";
        }

        panel.setAttribute("data-blog-posts-loaded", "");
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }
  }

  if (!window.customElements.get("blog-tabs")) {
    window.customElements.define("blog-tabs", BlogTabs);
  }

  class PromoTabs extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._selectedIndex = 0;
      this._controlsRoot = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();

      const controlsId = this.getAttribute("controls-id");

      if (controlsId) {
        this._controlsRoot = document.getElementById(controlsId) || null;
      }

      if (this._controlsRoot) {
        this.navButtons = Array.from(
          this._controlsRoot.querySelectorAll(".tab-scroller__nav-item")
        );
      } else {
        this.navButtons = Array.from(this.querySelectorAll(".tab-scroller__nav-item"));
      }

      const panelSelector = this.getAttribute("panel-selector") || "[data-promo-panel]";
      this.panels = Array.from(this.querySelectorAll(panelSelector));

      if (!this.navButtons.length || !this.panels.length) {
        return;
      }

      this.navButtons.forEach((button, index) => {
        button.addEventListener(
          "click",
          () => this._onTabClick(index),
          { signal: this._abortController.signal }
        );
      });

      if (Shopify.designMode) {
        this.addEventListener(
          "shopify:block:select",
          (event) => {
            const index = this.panels.findIndex((panel) => panel.contains(event.target));

            if (index !== -1) {
              this._onTabClick(index, { animate: false });
            }
          },
          { signal: this._abortController.signal }
        );
      }

      const initiallySelectedIndex = this.navButtons.findIndex(
        (button) => button.getAttribute("aria-selected") === "true"
      );
      this._selectedIndex = initiallySelectedIndex !== -1 ? initiallySelectedIndex : 0;

      this.navButtons.forEach((button, index) => {
        const isSelected = index === this._selectedIndex;
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
        button.setAttribute("tabindex", isSelected ? "0" : "-1");
        button.classList.toggle("is-selected", isSelected);
      });

      this.panels.forEach((panel, index) => {
        const isSelected = index === this._selectedIndex;
        panel.hidden = !isSelected;
        panel.classList.toggle("is-selected", isSelected);

        if (isSelected) {
          panel.classList.remove("reveal-invisible");
        }
      });

      this._scrollSelectedIntoView();
      this._dispatchTabChange();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration")
        ? parseFloat(this.getAttribute("animation-duration"))
        : 0.15;
    }

    _onTabClick(index, { animate = true } = {}) {
      if (index === this._selectedIndex || index < 0 || index >= this.panels.length) {
        return;
      }

      const previousIndex = this._selectedIndex;
      this._selectedIndex = index;

      this.navButtons.forEach((button, buttonIndex) => {
        const isSelected = buttonIndex === this._selectedIndex;
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
        button.setAttribute("tabindex", isSelected ? "0" : "-1");
        button.classList.toggle("is-selected", isSelected);
      });

      this._scrollSelectedIntoView();

      const fromPanel = this.panels[previousIndex];
      const toPanel = this.panels[this._selectedIndex];

      if (animate) {
        this._transitionPanels(fromPanel, toPanel);
      } else {
        if (fromPanel) {
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");
      }

      this._dispatchTabChange();
    }

    _dispatchTabChange() {
      this.dispatchEvent(
        new CustomEvent("promo-tabs:change", {
          detail: { index: this._selectedIndex },
          bubbles: true
        })
      );
    }

    _scrollSelectedIntoView() {
      if (!this.hasAttribute("scrolling-tabs")) {
        return;
      }

      const scrollElement =
        (this._controlsRoot &&
          (this._controlsRoot.querySelector(".tab-scroller__controls") ||
            this._controlsRoot.querySelector(".tab-scroller__nav") ||
            this._controlsRoot)) ||
        this.querySelector(".tab-scroller__controls") ||
        this.querySelector(".tab-scroller__nav") ||
        this;
      const selectedButton = this.navButtons[this._selectedIndex];

      if (!scrollElement || !selectedButton) {
        return;
      }

      scrollElement.scrollTo({
        left:
          selectedButton.offsetLeft -
          scrollElement.clientWidth / 2 +
          selectedButton.clientWidth / 2,
        top:
          selectedButton.offsetTop -
          scrollElement.clientHeight / 2 -
          selectedButton.clientHeight / 2,
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches
          ? "smooth"
          : "auto"
      });
    }

    async _transitionPanels(fromPanel, toPanel) {
      if (fromPanel === toPanel) {
        return;
      }

      try {
        if (fromPanel) {
          await Motion_animate(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration })
            .finished;
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");

        await Motion_animate(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration })
          .finished;
      } catch (e) {
        if (fromPanel) {
          fromPanel.hidden = true;
          fromPanel.classList.remove("is-selected");
        }

        toPanel.hidden = false;
        toPanel.classList.add("is-selected");
        toPanel.classList.remove("reveal-invisible");
      }
    }
  }

  if (!window.customElements.get("promo-tabs")) {
    window.customElements.define("promo-tabs", PromoTabs);
  }

  class VyneOnlineVideos extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._currentIndex = 0;
      this._isInView = false;
      this._hasAutoplayedFirst = false;
      this._intersectionObserver = null;
      this.mediaItems = [];
      this.videoMedias = [];
      this.cards = [];
    }

    connectedCallback() {
      this._abortController = new AbortController();

      this.mediaItems = Array.from(this.querySelectorAll(".vyne-online-videos__media-item"));

      if (!this.mediaItems.length) {
        return;
      }

      this.videoMedias = this.mediaItems.map((item) => item.querySelector("video-media") || null);

      const section = this.closest(".shopify-section");

      if (section) {
        this.cards = Array.from(
          section.querySelectorAll(".vyne-online-card[data-video-index]")
        );
      }

      this.cards.forEach((card) => {
        const index = parseInt(card.getAttribute("data-video-index"), 10);

        if (Number.isNaN(index)) {
          return;
        }

        card.addEventListener(
          "click",
          () => this._onCardClick(index),
          { signal: this._abortController.signal }
        );
      });

      const initiallyActiveCard = this.cards.find((card) =>
        card.classList.contains("is-active")
      );

      if (initiallyActiveCard) {
        const index = parseInt(initiallyActiveCard.getAttribute("data-video-index"), 10);

        if (!Number.isNaN(index)) {
          this._currentIndex = index;
        }
      } else {
        this._currentIndex = 0;
      }

      this._syncMediaVisibility();
      this._updateCards();

      this._intersectionObserver = new IntersectionObserver(
        (entries) => this._onIntersection(entries),
        { threshold: 0.3 }
      );

      this._intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }

      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
      }
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration")
        ? parseFloat(this.getAttribute("animation-duration"))
        : 0.15;
    }

    _onCardClick(index) {
      if (index === this._currentIndex || index < 0 || index >= this.mediaItems.length) {
        return;
      }

      this._selectIndex(index, { restart: true });
    }

    async _selectIndex(index, { restart = false } = {}) {
      const previousIndex = this._currentIndex;
      this._currentIndex = index;

      this._updateCards();

      const fromItem = this.mediaItems[previousIndex];
      const toItem = this.mediaItems[this._currentIndex];

      await this._transitionMedia(fromItem, toItem);
      this._updatePlayback({ restart });
    }

    async _transitionMedia(fromItem, toItem) {
      if (fromItem === toItem) {
        return;
      }

      try {
        if (fromItem) {
          if (typeof Motion_animate === "function") {
            await Motion_animate(
              fromItem,
              { opacity: [1, 0] },
              { duration: this.animationDuration }
            ).finished;
          }

          fromItem.hidden = true;
          fromItem.classList.remove("is-selected");
        }

        toItem.hidden = false;
        toItem.classList.add("is-selected");
        toItem.classList.remove("reveal-invisible");

        if (typeof Motion_animate === "function") {
          await Motion_animate(
            toItem,
            { opacity: [0, 1] },
            { duration: this.animationDuration }
          ).finished;
        }
      } catch (e) {
        if (fromItem) {
          fromItem.hidden = true;
          fromItem.classList.remove("is-selected");
        }

        toItem.hidden = false;
        toItem.classList.add("is-selected");
        toItem.classList.remove("reveal-invisible");
      }
    }

    _syncMediaVisibility() {
      this.mediaItems.forEach((item, index) => {
        const isSelected = index === this._currentIndex;
        item.hidden = !isSelected;
        item.classList.toggle("is-selected", isSelected);

        if (isSelected) {
          item.classList.remove("reveal-invisible");
        }
      });
    }

    _updateCards() {
      if (!this.cards || !this.cards.length) {
        return;
      }

      this.cards.forEach((card) => {
        const index = parseInt(card.getAttribute("data-video-index"), 10);
        const isActive = index === this._currentIndex;

        card.classList.toggle("is-active", isActive);
      });
    }

    _updatePlayback({ restart = false } = {}) {
      this.videoMedias.forEach((videoMedia, index) => {
        if (!videoMedia) {
          return;
        }

        if (!this._isInView) {
          videoMedia.pause();
          return;
        }

        if (index === this._currentIndex) {
          if (restart) {
            videoMedia.play({ restart: true });
          } else {
            videoMedia.play();
          }
        } else {
          videoMedia.pause();
        }
      });
    }

    _onIntersection(entries) {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);

      this._isInView = isIntersecting;

      if (!isIntersecting) {
        this.videoMedias.forEach((videoMedia) => {
          if (videoMedia) {
            videoMedia.pause();
          }
        });

        return;
      }

      if (!this._hasAutoplayedFirst) {
        this._hasAutoplayedFirst = true;
        this._updatePlayback({ restart: true });
      } else {
        this._updatePlayback();
      }
    }
  }

  if (!window.customElements.get("vyne-online-videos")) {
    window.customElements.define("vyne-online-videos", VyneOnlineVideos);
  }

  class VideoTabs extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._currentIndex = 0;
      this._isInView = false;
      this._hasAutoplayedFirst = false;
      this._intersectionObserver = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();

      this.mediaItems = Array.from(this.querySelectorAll(".video-tabs__media-item"));
      this.tabs = Array.from(this.querySelectorAll(".video-tabs__tab"));

      if (!this.mediaItems.length || !this.tabs.length) {
        return;
      }

      this.videoMedias = [];
      this.videoElements = [];

      this.mediaItems.forEach((item) => {
        const videoMedia = item.querySelector("video-media");
        const video = videoMedia ? videoMedia.querySelector("video") || videoMedia : item.querySelector("video");

        this.videoMedias.push(videoMedia || null);
        this.videoElements.push(video || null);
      });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener(
          "click",
          () => this._onTabClick(index),
          { signal: this._abortController.signal }
        );
      });

      this.videoElements.forEach((video, index) => {
        if (!video) {
          return;
        }

        video.addEventListener(
          "ended",
          () => this._onVideoEnded(index),
          { signal: this._abortController.signal }
        );

        video.addEventListener(
          "timeupdate",
          () => this._onTimeUpdate(index),
          { signal: this._abortController.signal }
        );
      });

      const initiallySelectedIndex = this.tabs.findIndex(
        (tab) => tab.classList.contains("is-selected") || tab.getAttribute("aria-selected") === "true"
      );

      this._currentIndex = initiallySelectedIndex !== -1 ? initiallySelectedIndex : 0;

      this.mediaItems.forEach((item, index) => {
        const isSelected = index === this._currentIndex;
        item.hidden = !isSelected;
        item.classList.toggle("is-selected", isSelected);

        if (isSelected) {
          item.classList.remove("reveal-invisible");
        }
      });

      this.tabs.forEach((tab, index) => {
        const isSelected = index === this._currentIndex;
        tab.setAttribute("aria-selected", isSelected ? "true" : "false");
        tab.setAttribute("tabindex", isSelected ? "0" : "-1");
        tab.classList.toggle("is-selected", isSelected);
      });

      this._intersectionObserver = new IntersectionObserver(
        (entries) => this._onIntersection(entries),
        { threshold: 0.3 }
      );

      this._intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }

      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
      }
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration")
        ? parseFloat(this.getAttribute("animation-duration"))
        : 0.15;
    }

    _onTabClick(index) {
      if (index === this._currentIndex || index < 0 || index >= this.mediaItems.length) {
        return;
      }

      this._selectIndex(index, { restart: true });
    }

    _resetProgress(index) {
      console.log("resetProgress", index);
      if (index < 0 || index >= this.tabs.length) {
        return;
      }

      const tab = this.tabs[index];
      console.log("tab", tab);

      if (!tab) {
        return;
      }

      const bar = tab.querySelector(".video-tabs__tab-progress-bar");

      console.log("bar", bar);
      if (!bar) {
        return;
      }
      console.log("setting progress to 0");
      bar.style.setProperty("--progress", "0");
    }

    async _selectIndex(index, { restart = false } = {}) {
      const previousIndex = this._currentIndex;
      if (previousIndex !== index) {
        this._resetProgress(previousIndex);
      }
      this._currentIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const isSelected = tabIndex === this._currentIndex;
        tab.setAttribute("aria-selected", isSelected ? "true" : "false");
        tab.setAttribute("tabindex", isSelected ? "0" : "-1");
        tab.classList.toggle("is-selected", isSelected);
      });

      const fromItem = this.mediaItems[previousIndex];
      const toItem = this.mediaItems[this._currentIndex];

      await this._transitionMedia(fromItem, toItem);
      this._updatePlayback({ restart });
    }

    async _transitionMedia(fromItem, toItem) {
      if (fromItem === toItem) {
        return;
      }

      try {
        if (fromItem) {
          if (typeof Motion_animate === "function") {
            await Motion_animate(
              fromItem,
              { opacity: [1, 0] },
              { duration: this.animationDuration }
            ).finished;
          }

          fromItem.hidden = true;
          fromItem.classList.remove("is-selected");
        }

        toItem.hidden = false;
        toItem.classList.add("is-selected");
        toItem.classList.remove("reveal-invisible");

        if (typeof Motion_animate === "function") {
          await Motion_animate(
            toItem,
            { opacity: [0, 1] },
            { duration: this.animationDuration }
          ).finished;
        }
      } catch (e) {
        if (fromItem) {
          fromItem.hidden = true;
          fromItem.classList.remove("is-selected");
        }

        toItem.hidden = false;
        toItem.classList.add("is-selected");
        toItem.classList.remove("reveal-invisible");
      }
    }

    _updatePlayback({ restart = false } = {}) {
      this.videoMedias.forEach((videoMedia, index) => {
        if (!videoMedia) {
          return;
        }

        if (!this._isInView) {
          videoMedia.pause();
          return;
        }

        if (index === this._currentIndex) {
          if (restart) {
            videoMedia.play({ restart: true });
          } else {
            videoMedia.play();
          }
        } else {
          videoMedia.pause();
        }
      });
    }

    _onVideoEnded(index) {
      if (index !== this._currentIndex) {
        return;
      }

      const nextIndex = this._currentIndex + 1;

      if (nextIndex < this.mediaItems.length) {
        this._selectIndex(nextIndex, { restart: true });
      } else if (this.mediaItems.length > 1) {
        // Loop back to the first video
        this._selectIndex(0, { restart: true });
      } else {
        // Single video: reset its progress when it ends
        this._resetProgress(this._currentIndex);
      }
    }

    _onTimeUpdate(index) {
      const video = this.videoElements[index];

      if (index !== this._currentIndex) {
        // Ignore time updates for videos that are not currently selected
        // so they don't override reset progress values.
        return;
      }

      if (!video || !video.duration || isNaN(video.duration)) {
        return;
      }

      const progress = video.currentTime / video.duration;
      const tab = this.tabs[index];

      if (!tab) {
        return;
      }

      const bar = tab.querySelector(".video-tabs__tab-progress-bar");

      if (!bar) {
        return;
      }

      const clampedProgress = Math.max(0, Math.min(1, progress));

      bar.style.setProperty("--progress", clampedProgress.toString());
    }

    _onIntersection(entries) {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);

      this._isInView = isIntersecting;

      if (!isIntersecting) {
        this.videoMedias.forEach((videoMedia) => {
          if (videoMedia) {
            videoMedia.pause();
          }
        });

        return;
      }

      if (!this._hasAutoplayedFirst) {
        this._hasAutoplayedFirst = true;
        this._selectIndex(this._currentIndex, { restart: true });
      } else {
        this._updatePlayback();
      }
    }
  }

  if (!window.customElements.get("video-tabs")) {
    window.customElements.define("video-tabs", VideoTabs);
  }

  /*------------------------------------*\
    #SLIDESHOW CAROUSEL
  \*------------------------------------*/

  class SlideshowCarousel extends EffectCarousel {
    _slideshowTabProgressControls = [];
    _slideshowAutoplayListenersAttached = false;

    constructor() {
      super();
      this.addEventListener("carousel:select", this._onSlideSelected);
    }

    connectedCallback() {
      super.connectedCallback();

      // EffectCarousel only wires autoplay at min-width 1000px; slideshow should autoplay on all breakpoints.
      if (this.items.length > 1 && this.hasAttribute("autoplay") && !this._player) {
        this._player = new Player(this.getAttribute("autoplay"));
        this._player.addEventListener("player:end", this.next.bind(this));
        Motion_inView(this, () => {
          this._player.resume(true);
          return () => this._player.pause();
        });

        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", () => this._player.stop());
          this.addEventListener("shopify:block:deselect", () => this._player.start());
        }
      }

      if (!this._player || !this.hasAttribute("autoplay")) {
        return;
      }

      if (this._slideshowAutoplayListenersAttached) {
        return;
      }

      this._slideshowAutoplayListenersAttached = true;
      this._player.addEventListener("player:start", this._onSlideshowAutoplayStart);
      this._player.addEventListener("player:pause", this._onSlideshowAutoplayPause);
      this._player.addEventListener("player:resume", this._onSlideshowAutoplayResume);
    }

    /**
     * Manual tab clicks pass `externalClick: true`, which would normally stop autoplay and skip
     * `player.resume(true)` on EffectCarousel. For slideshow we always restart the full interval
     * so the previous tab’s progress clears and the new tab animates from 0 for the full duration.
     */
    async select(index, secondArg = false, thirdArg = {}) {
      let externalClick = false;
      let options = thirdArg;

      if (typeof secondArg === "object" && secondArg !== null) {
        options = secondArg;
      } else {
        externalClick = secondArg;
      }

      const keepAutoplayRunning =
        this.hasAttribute("autoplay") && this.items.length > 1;

      return super.select(
        index,
        keepAutoplayRunning ? false : externalClick,
        options
      );
    }

    _onSlideshowAutoplayStart = (event) => {
      this._slideshowTabProgressControls.forEach((control) => control.stop());
      this._slideshowTabProgressControls = [];

      const tabs = this.querySelectorAll(".slideshow__controls .tab-scroller__nav-item");

      tabs.forEach((tab) => {
        const bar = tab.querySelector(".slideshow__tab-progress-bar");

        if (!bar) {
          return;
        }

        const isCurrent = tab.getAttribute("aria-current") === "true";
        const control = Motion_animate(
          bar,
          { transform: isCurrent ? ["scaleX(0)", "scaleX(1)"] : "scaleX(0)" },
          { duration: isCurrent ? event.detail.duration : 0, easing: "linear" }
        );

        this._slideshowTabProgressControls.push(control);
      });
    };

    _onSlideshowAutoplayPause = () => {
      this._slideshowTabProgressControls.forEach((control) => control.pause());
    };

    _onSlideshowAutoplayResume = () => {
      this._slideshowTabProgressControls.forEach((control) => control.play());
    };
  
    get transitionType() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "fade" : this.getAttribute("transition");
    }
  
    async _reveal() {
      const selectedSlide = this.selectedSlide;
      await imageLoaded(selectedSlide.querySelectorAll("img"));
      this.style.opacity = "1";
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      if (!this._reloaded) {
        Motion_timeline([
          [selectedSlide, { zIndex: 1 }, { duration: 0 }],
          [selectedSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3 }],
          "content",
          [selectedSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
          [...getHeadingKeyframe(selectedSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })],
          [selectedSlide.querySelector(".button"), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
          [this.querySelector(".slideshow__controls"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
        ]);
      }
    }
  
    _onSlideshowTap(event) {
      if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
        return;
      }
      event.detail.originalEvent.clientX > window.innerWidth / 2 ? this.next() : this.previous();
    }
    /**
     * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
     * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
     * that resolves when the animation is finished
     */
    async _transitionTo(fromSlide, toSlide, { direction, animate: animate19 = true } = {}) {
      if (!fromSlide || !toSlide) {
        return;
      }

      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");
      let timelineControls = null;
      switch (this.transitionType) {
        case "fade":
          timelineControls = this._fade(fromSlide, toSlide, { animate: animate19 });
          break;
        case "fade_with_text":
          timelineControls = this._fadeWithText(fromSlide, toSlide, { animate: animate19 });
          break;
      }
      if (!timelineControls) {
        return;
      }
      if (!animate19) {
        timelineControls.finish();
      }
      return timelineControls.finished;
    }
    /**
     * Perform a simple fade transition
     */
    _fade(fromSlide, toSlide) {
      return Motion_timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0.3, at: "<", easing: "ease-out", zIndex: { easing: "step-end" } }]
      ]);
    }
    /**
     * Perform a transition with fade images and image transition
     */
    async _fadeWithText(fromSlide, toSlide) {
      Motion_animate(fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } });
      await imageLoaded(toSlide.querySelectorAll("img"));
      Motion_animate(toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0, zIndex: { easing: "step-end" } });
      return Motion_timeline([
        [toSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, easing: "ease-out" }],
        "content",
        [toSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
        [...getHeadingKeyframe(toSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })]
      ]);
    }
    /**
     * Perform the synchronization with video
     */
    async _onSlideSelected(event) {
      Array.from(this.querySelectorAll("video-media")).forEach((video) => video.pause());
      if (!event.detail.slide) {
        return;
      }

      if (event.detail.slide.getAttribute("data-slide-type") === "video") {
        const visibleVideo = Array.from(event.detail.slide.querySelectorAll("video-media")).filter((video) => video.offsetParent !== null).pop();
        visibleVideo.play({ restart: true });
        if (this.player) {
          const video = visibleVideo.querySelector("video");
          if (isNaN(video.duration)) {
            await new Promise((resolve) => {
              video.onloadedmetadata = () => resolve();
            });
          }
          this._player.setDuration(video.duration);
          this._player.resume({ restart: true });
        }
      } else {
        this._player?.setDuration(parseInt(this.getAttribute("autoplay")));
      }
    }
  };

  if (!window.customElements.get("slideshow-carousel")) {
    window.customElements.define("slideshow-carousel", SlideshowCarousel);
  }

  /*------------------------------------*\
    #TESTIMONIALS CAROUSEL
  \*------------------------------------*/

  var reduceMotion2 = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
  class TestimonialsCarousel extends EffectCarousel {
    constructor() {
      super();
      if (reduceMotion2) {
        Motion_inView(this, this._reveal.bind(this));
      }
    }
  
    _reveal() {
      Motion_animate(this.selectedSlide.querySelector(".blockquote"), { opacity: 1, transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.2 });
    }
  };
  if (!window.customElements.get("testimonials-carousel")) {
    window.customElements.define("testimonials-carousel", TestimonialsCarousel);
  }

  /*------------------------------------*\
    #SWITCH ELEMENTS
  \*------------------------------------*/

  class CollectionSwitch extends HTMLElement {
    constructor() {
      super();
    }
  
    connectedCallback() {
      this.productsButton = this.querySelector('[data-view="products"]');
      this.articlesButton = this.querySelector('[data-view="articles"]');
  
      this.productsButton.addEventListener('click', () => {
        this.showProducts();
      });
  
      this.articlesButton.addEventListener('click', () => {
        this.showArticles();
      });

      void this._prefetchSupportArticleCount();
    }

    async _prefetchSupportArticleCount() {
      const blogHandle = this.getAttribute("blog-handle");
      const tagHandle = this.getAttribute("tag-handle");
      const countEl = this.articlesButton?.querySelector(".count");
      if (!blogHandle || !tagHandle?.trim() || !countEl) {
        return;
      }
      try {
        const response = await cachedFetch(_blogFetchUrl(blogHandle, tagHandle));
        if (!response.ok) {
          return;
        }
        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const total = doc.querySelector(".blog-results")?.getAttribute("data-articles-count");
        if (total == null || total === "") {
          return;
        }
        countEl.textContent = total;
      } catch {
        /* leave count empty */
      }
    }
  
    showArticles() {
      const element = document.querySelector(".collection-blog");
      const blogHandle = element.getAttribute("blog-handle") || "support-advice";
      const tagHandle = this.getAttribute("tag-handle");
      let numberOfPosts;
  
      _fetchBlogPosts(blogHandle, tagHandle, element, numberOfPosts);
  
      // Hide sections between header and main collection
      const sections = document.querySelectorAll('.shopify-section');
      let hide = false;
  
      sections.forEach(section => {
        if (section.classList.contains('shopify-section--collection-header')) {
          hide = true;
        } else if (section.classList.contains('shopify-section--main-collection')) {
          hide = false;
        } else if (hide) {
          section.classList.add('hidden');
        }
      });
  
      document.querySelector(".collection").style.display = "none";
      document.querySelector(".collection-blog").style.display = "block";
  
      this.setActive('articles');
    }
  
    showProducts() {
      document.querySelector(".collection").style.display = "grid";
      document.querySelector(".collection-blog").style.display = "none";
  
      const hiddenSections = document.querySelectorAll('.shopify-section.hidden');
      hiddenSections.forEach(section => {
        section.classList.remove('hidden');
      });
  
      this.setActive('products');
    }
  
    setActive(view) {
      this.productsButton.classList.toggle('is-active', view === 'products');
      this.articlesButton.classList.toggle('is-active', view === 'articles');
    }
  }
  
  if (!window.customElements.get("collection-switch")) {
    window.customElements.define("collection-switch", CollectionSwitch);
  }
  


  var SearchSwitch = class extends HTMLElement {
    constructor() {
      super();
    }
  
    connectedCallback() {
      // Find the input checkbox with the class 'my-input'
      this.inputCheckbox = this.querySelector(".text-switch");
  
      // Listen for changes to the input checkbox
      this.inputCheckbox.addEventListener("change", this._onCheckboxChanged.bind(this));
    }
  
    _onCheckboxChanged(event) {  
      // If the checkbox is checked
      if (event.target.checked) {
        // We search articles
        // Get search query
        this.dispatchEvent(new CustomEvent("search:switch", {
          bubbles: true,
          detail: {
            resource: "article"
          }
        }));
      } else {
        this.dispatchEvent(new CustomEvent("search:switch", {
          bubbles: true,
          detail: {
            resource: "product"
          }
        }));
      }
    }
  }
  
  if (!window.customElements.get("search-switch")) {
    window.customElements.define("search-switch", SearchSwitch);
  }

  var NewSearchSwitch = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.links = Array.from(this.querySelectorAll("[data-view]"));
      if (this.links.length === 0) {
        return;
      }

      void this._prefetchCounts();
    }

    async _prefetchCounts() {
      await Promise.all(this.links.map((link) => this._prefetchCount(link)));
    }

    async _prefetchCount(link) {
      const countEl = link.querySelector(".count");
      if (!countEl || countEl.textContent.trim() !== "" || !link.href) {
        return;
      }

      try {
        const response = await cachedFetch(link.href);
        if (!response.ok) {
          return;
        }

        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const fetchedSwitch = doc.querySelector("new-search-switch");
        const total = fetchedSwitch?.dataset.activeCount;

        if (!total?.trim()) {
          return;
        }

        countEl.textContent = total;
      } catch {
        /* leave count empty */
      }
    }
  };

  if (!window.customElements.get("new-search-switch")) {
    window.customElements.define("new-search-switch", NewSearchSwitch);
  }

  var BlogFilterLink = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener('click', this._onRemoveFilter);
    }

    _onRemoveFilter(event) {
      event.preventDefault();
      
      const currentUrl = new URL(window.location.href);
      const currentTags = currentUrl.pathname.split('/tagged/')[1]?.split('+') || [];
      const filterToRemove = `${this.dataset.filterGroup}_${this.dataset.filterValue.replace(/\s+/g, '-')}`;

      // Remove the target tag and keep others
      const updatedTags = currentTags.filter(tag => tag !== filterToRemove);
      const newUrl = new URL(currentUrl);
      
      if (updatedTags.length) {
        newUrl.pathname = `${this.basePath}/tagged/${updatedTags.join('+')}`;
      } else {
        newUrl.pathname = this.basePath.replace('/tagged', '');
      }

      // Dispatch event to update content
      this.dispatchEvent(new CustomEvent('blog-filter:change', {
        bubbles: true,
        detail: {
          url: newUrl,
          sectionId: document.querySelector('blog-filters').dataset.sectionId,
          updateHistory: true
        }
      }));
    }

    connectedCallback() {
      this.basePath = '/blogs/help-centre'; // Set your base path
    }
  }

  if (!window.customElements.get('blog-filter-link')) {
    window.customElements.define('blog-filter-link', BlogFilterLink, { extends: 'button' });
  }

  var ClearBlogFilters = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener('click', this._clearFilters);
      this.blogFilters = document.querySelector("blog-filters");
    }

    _clearFilters(event) {
      event.preventDefault();

      const currentUrl = new URL(window.location.href);
      const currentTags = currentUrl.pathname.split('/tagged/')[1]?.split('+') || [];
      
      // Define your filter groups (adjust as needed)
      const FILTER_GROUPS = this.blogFilters.dataset.filterGroups.split(',');
      
      // Keep only tags NOT belonging to your filter groups
      const preservedTags = currentTags.filter(tag => {
        const [group] = tag.split('_');
        return !FILTER_GROUPS.includes(group);
      });

      // Build new URL
      const newUrl = new URL(currentUrl);
      if (preservedTags.length) {
        newUrl.pathname = `${this.basePath}/tagged/${preservedTags.join('+')}`;
      } else {
        newUrl.pathname = this.basePath.replace('/tagged', ''); // No tags left
      }

      // Trigger content update
      this.dispatchEvent(new CustomEvent('blog-filter:change', {
        bubbles: true,
        detail: {
          url: newUrl,
          sectionId: document.querySelector('blog-filters').dataset.sectionId,
          updateHistory: true
        }
      }));
    }

    connectedCallback() {
      this.basePath = '/blogs/help-centre'; // Match your base path
    }
  }

  if (!window.customElements.get('clear-blog-filters')) {
    window.customElements.define('clear-blog-filters', ClearBlogFilters, { extends: 'button' });
  }

  var BlogFiltersDrawer = class extends Drawer {
    constructor() {
      super();
      this.addEventListener("dialog:after-hide", this._updateFilters);
    }

    get shouldAppendToBody() {
      return false;
    }

    _updateFilters() {
      const filters = this.querySelector("blog-filters");
      filters.onFilterChanged();
    }
  };

  if (!window.customElements.get("blog-filters-drawer")) {
    window.customElements.define("blog-filters-drawer", BlogFiltersDrawer);
  }


  var BlogFilters = class extends HTMLElement {
    constructor() {
      super();
      this.basePath = 'blogs/help-centre/tagged';
      this.sectionId = this.dataset.sectionId;
      this.initEventListeners();
      this.newUrl = '';
    }

    initEventListeners() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-filter-group][type="checkbox"]')) {
          this.onCheckboxChanged(event.target);
        }
      });
    }

    // Extract current tags from URL (e.g., ["maintopic_faq", "helptopic_about-you"])
    getCurrentTags() {
      const url = new URL(window.location.href);
      const taggedPart = url.pathname.split('/tagged/')[1] || '';
      return taggedPart.split('+').filter(tag => tag.trim() !== '');
    }

    onCheckboxChanged(clickedCheckbox) {
      const group = clickedCheckbox.dataset.filterGroup;
      
      // Uncheck others in the same group
      this.querySelectorAll(`[data-filter-group="${group}"]`).forEach(checkbox => {
        if (checkbox !== clickedCheckbox) checkbox.checked = false;
      });

      const currentTags = this.getCurrentTags();
      const value = clickedCheckbox.dataset.filterValue.replace(/\s+/g, '-').toLowerCase();
      const newTag = `${group}_${value}`;

      const updatedTags = currentTags.filter(tag => !tag.startsWith(`${group}_`));
      if (clickedCheckbox.checked) updatedTags.push(newTag);

      const newUrl = new URL(window.location.href);
      newUrl.search = ''; // Strip ?page=
      newUrl.pathname = updatedTags.length
        ? `${this.basePath}/${updatedTags.join('+')}`
        : this.basePath.replace('/tagged', '');

      this.newUrl = newUrl;

      if (this.hasAttribute("update-on-change")) {
        this.onFilterChanged(this.newUrl); 
      } else {
        cachedFetch(this.newUrl);
      }
    }

    onFilterChanged(newUrl, sectionId, updateHistory) {
      const newUrl2 = newUrl || this.newUrl;

      if (newUrl2 == '' || newUrl2 == window.location.href) {
        return;
      }

      this.dispatchEvent(new CustomEvent('blog-filter:change', {
        bubbles: true,
        detail: {
          url: newUrl2,
          sectionId: sectionId || this.sectionId,
          updateHistory: true // Allow history.pushState
        }
      }));
    }

    // Initialize checkboxes from current URL
    connectedCallback() {
      const currentTags = this.getCurrentTags();
      currentTags.forEach(tag => {
        const [group, value] = tag.split('_');
        const checkbox = this.querySelector(
          `[data-filter-group="${group}"][data-value="${value.replace(/-/g, ' ')}"]`
        );
        if (checkbox) checkbox.checked = true;
      });
    }
  }

  if (!window.customElements.get("blog-filters")) {
    window.customElements.define("blog-filters", BlogFilters);
  }

  let blogFilterAbortController = null;

  document.addEventListener('blog-filter:change', async (event) => {
    console.log("change");
    // Cancel pending requests
    if (blogFilterAbortController) blogFilterAbortController.abort();
    blogFilterAbortController = new AbortController();

    const { url, sectionId, updateHistory } = event.detail;
    const section = document.getElementById(`shopify-section-${sectionId}`);
    console.log(section);
    section?.classList.add('is-loading');

    try {
      // Update history (if enabled)
      if (updateHistory) {
        history.pushState({ url: url.toString() }, '', url);
      }

      // Fetch new content
      const response = await fetch(url.toString(), {
        signal: blogFilterAbortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const parser = new DOMParser();
      const newContent = parser.parseFromString(html, 'text/html');
      console.log(sectionId);
      console.log(newContent);

      // Replace content
      section.innerHTML = newContent.querySelector(`#shopify-section-${sectionId}`).innerHTML;
      section.classList.remove('is-loading');

      // Scroll to results (optional)
      const results = section.querySelector('.support-grid__main');
      if (results) {
        results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

    } catch (err) {
      if (err.name !== 'AbortError') console.error('Filter update failed:', err);
    }
  });

  // Set initial state
  window.addEventListener('DOMContentLoaded', () => {
    history.replaceState({ url: window.location.href }, '', window.location.href);
  });

  // Handle back/forward navigation
  window.addEventListener('popstate', (event) => {
    if (event.state?.url) {
      document.dispatchEvent(new CustomEvent('blog-filter:change', {
        detail: {
          url: new URL(event.state.url),
          sectionId: document.querySelector('blog-filters').dataset.sectionId,
          updateHistory: false // Prevent double pushState
        }
      }));
    }
  });


  var ArticleReview = class extends HTMLElement {
    constructor() {
      super();
      this.articleId = this.getAttribute('data-article-id');
      this.storageKey = `articleRating-${this.articleId}`;
      this.starButtons = this.querySelectorAll('.article-review__stars button');
      this.starsContainer = this.querySelector('.article-review__stars');
      this.headingElement = this.querySelector('.article-review__heading');
      this.copyElement = this.querySelector('.article-review__copy');
      
      this.originalHeading = this.headingElement.textContent;
      this.feedbackHeading = this.headingElement.getAttribute('data-feedback-heading') || "Thank you for your feedback!";
      this.originalCopy = this.copyElement.innerHTML;
      
      this.init();
    }

    init() {
      const savedRating = localStorage.getItem(this.storageKey);
      if (savedRating) {
        this.updateRating(parseInt(savedRating));
      }

      this.starButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const rating = parseInt(e.currentTarget.value);
          this.handleRating(rating);
        });
        
        button.addEventListener('mouseenter', (e) => {
          const hoverRating = parseInt(e.currentTarget.value);
          this.setHoverState(hoverRating);
        });
      });

      this.starsContainer.addEventListener('mouseleave', () => {
        this.clearHoverState();
      });
    }

    handleRating(rating) {
      localStorage.setItem(this.storageKey, rating.toString());
      this.updateRating(rating);
    }

    updateRating(rating) {
      this.currentRating = rating;
      this.starButtons.forEach(button => {
        const buttonRating = parseInt(button.value);
        button.classList.toggle('active', buttonRating <= rating);
      });
      this.showFeedback(rating);
    }

    setHoverState(hoverRating) {
      this.starButtons.forEach(button => {
        const buttonRating = parseInt(button.value);
        button.classList.toggle('hover', buttonRating <= hoverRating);
      });
    }

    clearHoverState() {
      this.starButtons.forEach(button => {
        button.classList.remove('hover');
      });
    }

    showFeedback(rating) {
    
      this.headingElement.textContent = this.feedbackHeading;
      
      let messageType;
      if (rating <= 2) messageType = 'low';
      else if (rating === 3) messageType = 'medium';
      else messageType = 'high';

      const template = this.querySelector(
        `.article-review__message-template[data-type="${messageType}"]`
      );

      if (template) {
        this.copyElement.innerHTML = template.innerHTML;
      }
    }

    resetFeedback() {
      this.headingElement.textContent = this.originalHeading;
      this.copyElement.innerHTML = this.originalCopy;
    }
  }

  if (!window.customElements.get("article-review")) {
    window.customElements.define("article-review", ArticleReview);
  }





  /*------------------------------------*\
    #CUSTOMER ACCOUNT
  \*------------------------------------*/

  var AccountLogin = class extends HTMLElement {
    connectedCallback() {
      this.recoverForm = this.querySelector("#recover");
      this.loginForm = this.querySelector("#login");

      if (window.location.hash === "#recover") {
        this._switchForms();
      }

      new Delegate(this).on("click", '[href="#recover"], [href="#login"]', this._switchForms.bind(this));
    }

    _switchForms(event) {
      if (event) {
        event.preventDefault();
      }
      this.recoverForm.hidden = !this.recoverForm.hidden;
      this.loginForm.hidden = !this.loginForm.hidden;
    }
  };

  if (!window.customElements.get("account-login")) {
    window.customElements.define("account-login", AccountLogin);
  }



  var PrescriptionsBanner = class extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        sessionStorage.removeItem("pb:closed");
      }

      this.addEventListener("dialog:force-close", (event) => {
        this.hide();
        event.stopPropagation();
      });
    }

    connectedCallback() {
      this.elements = document.querySelectorAll("prescriptions-banner");
      this._checkStorage();
    }

    hide() {
      this.elements.forEach((element) => {
        element.classList.remove("show");
        element.classList.add("hide");
      });

      sessionStorage.setItem("pb:closed", "true");
    }

    show() {
      this.elements.forEach((element) => {
        element.classList.remove("hide");
        element.classList.add("show");
      });
    }

    _checkStorage() {
      if (sessionStorage.getItem("pb:closed") === "true") {
        return;
      } else {
        this.show();
      }
    }
  }

  if (!window.customElements.get("prescriptions-banner")) {
    window.customElements.define("prescriptions-banner", PrescriptionsBanner);
  }



  // Smooth scrolling for internal links

  (() => {
    var documentDelegate = new Delegate(document.body);

    documentDelegate.on('click', '[href^="#"], [data-href]', function (event, target) {
      var selector = target.hasAttribute('href') ? target.getAttribute('href') : target.getAttribute('data-href');

      if (selector === '#' || '#recover') {
        return;
      }

      var element = document.querySelector(selector);
      var offset = parseInt(target.getAttribute('data-offset') || 0);

      window.scrollTo({ 
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto", 
        top: element.offsetTop - offset 
      });

      event.preventDefault();
    });

    function isFineHoverDevice() {
      return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    }

    documentDelegate.on("click", ".promo-blocks-card--expandable-touch", function (event, card) {
      if (isFineHoverDevice()) {
        return;
      }

      var link = event.target.closest(".promo-blocks-card__hover-link-text");
      if (link && card.contains(link) && card.classList.contains("is-touch-expanded")) {
        return;
      }

      document.querySelectorAll(".promo-blocks-card--expandable-touch.is-touch-expanded").forEach(function (el) {
        if (el !== card) {
          el.classList.remove("is-touch-expanded");
        }
      });

      card.classList.toggle("is-touch-expanded");
    });

    documentDelegate.on("click", "body", function (event) {
      if (isFineHoverDevice()) {
        return;
      }

      if (event.target.closest(".promo-blocks-card--expandable-touch")) {
        return;
      }

      document.querySelectorAll(".promo-blocks-card--expandable-touch.is-touch-expanded").forEach(function (el) {
        el.classList.remove("is-touch-expanded");
      });
    });
  })();


  class SwiperComponent extends HTMLElement {
    constructor() {
      super();
      this.customSwiperOptions = JSON.parse(this.getAttribute("data-swiper-options")) || {};
      this.swiperInitialized = false;
      this.section = this.closest("section");
      this.observer = null;

      // Need this variable for theme editor events
      this.hasAutoplay = !!this.customSwiperOptions.autoplay;
    }

    get swiperId() {
      return this.getAttribute("swiper-id");
    }
  
    connectedCallback() {
      if (this.section && this.section.classList.contains("shopify-section")) {
        this.observer = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init(); // Initialize Swiper when visible
              observer.disconnect(); // Stop observing after initialization
            }
          });
        });
        this.observer.observe(this.section); // Observe the section
      }

      this.init()
  
      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", () => this.reinit());
        window.addEventListener("shopify:section:select", () => this.reinit());
      
        setTimeout(() => {
          if (!this.swiperInitialized) this.init();
        }, 100);
      } else {
        window.addEventListener("load", () => this.init());
      }
    }

    reinit() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiperInitialized = false;
      }
      this.init();
    }

    init() {
      if (this.swiperInitialized) return;// prevent multiple initializations
      this.swiperInitialized = true;
  
      // Find external navigation buttons automatically
      let prevEl = null, nextEl = null;
      if (this.swiperId) {
        prevEl = document.querySelector(`.swiper-button-prev-${this.swiperId}`);
        nextEl = document.querySelector(`.swiper-button-next-${this.swiperId}`);
      }

      const responsiveOptions = {
        slideCount: this.customSwiperOptions.slideCount,
        slidesPerView: this.customSwiperOptions.slidesPerView || "auto",
        slidesPerViewDesktop: this.customSwiperOptions.slidesPerViewDesktop || this.customSwiperOptions.slidesPerView || 4,
        slidesPerViewTablet: this.customSwiperOptions.slidesPerViewTablet || this.customSwiperOptions.slidesPerViewDesktop || this.customSwiperOptions.slidesPerView || 3,

        enabled: this.customSwiperOptions.enabled ?? true,
        enabledDesktop:
          this.customSwiperOptions.enabledDesktop ?? this.customSwiperOptions.enabled ?? true,
        enabledTablet:
          this.customSwiperOptions.enabledTablet ??
          this.customSwiperOptions.enabledDesktop ??
          this.customSwiperOptions.enabled ??
          true
      }
  
      this.swiperOptions = {
        enabled: responsiveOptions.enabled,
        centeredSlides: this.customSwiperOptions.centeredSlides,
        centeredSlidesBounds: false,
        rewind: false,
        loop: this.customSwiperOptions.loop,
        scrollProgress: true,
        observer: true,
        observeParents: true,
        slidesPerView: responsiveOptions.slidesPerView,
        spaceBetween: 30,
        breakpoints: {
          700: {
            enabled: responsiveOptions.enabledTablet,
            slidesPerView: responsiveOptions.slidesPerViewTablet,
            spaceBetween: this.customSwiperOptions.centeredSlides ? 22 : 30
          },
          1000: {
            enabled: responsiveOptions.enabledDesktop,
            slidesPerView: responsiveOptions.slidesPerViewDesktop,
            spaceBetween: this.customSwiperOptions.centeredSlides ? 4 : 30
          }
        },
        autoplay: this.hasAutoplay ? {
          delay: this.customSwiperOptions.autoplaySpeed || 4000
        } : false,
        navigation: {
          prevEl: document.querySelector(`.swiper-button-prev-${this.swiperId}`),
          nextEl: document.querySelector(`.swiper-button-next-${this.swiperId}`),
          disabledClass: 'swiper-button-disabled'
        },
        pagination: {
          el: document.querySelector(`.swiper-pagination-${this.swiperId}`),
          type: "progressbar"
        }
      }
  
      // Initialize Swiper
      this.swiper = new Swiper(this, this.swiperOptions);
    }
  }
  
  customElements.define("swiper-component", SwiperComponent);

  // Sections switcher: show/hide subsequent sibling sections
  var SectionsSwitcher = class extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this._onPopState = this._onPopState.bind(this);

      this.config = this._readConfig();
      if (!this.config || !this.config.tabs?.length) return;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.buttons = Array.from(this.querySelectorAll("[data-sections-switcher-tab]"));
      this.sectionElement = this.closest(".shopify-section");
      this.controlledSections = this.sectionElement ? this._collectControlledSections() : [];

      if (!this.config.enabled || (this.designMode && this.config.showAllInDesignMode)) {
        this._showAllControlledSections();
        return;
      }

      this.addEventListener("click", this._onClick);
      window.addEventListener("popstate", this._onPopState);

      const initialTabId = this._getInitialTabId();
      this.select(initialTabId, { updateHistory: false });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._onClick);
      window.removeEventListener("popstate", this._onPopState);
    }

    _readConfig() {
      const script = this.querySelector("script[data-sections-switcher-config]");
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.warn("SectionsSwitcher: invalid config JSON", e);
        return null;
      }
    }

    get _paramKey() {
      const key = (this.config.urlParamName || "").trim();
      return key || `ss-${this.config.sectionId}`;
    }

    get _storageKey() {
      return `sections-switcher:${this.config.sectionId}`;
    }

    _collectControlledSections() {
      const total = this.config.tabs.reduce((sum, t) => sum + (Number(t.count) || 0), 0);
      if (total <= 0) return [];

      const sections = [];
      let el = this.sectionElement.nextElementSibling;
      while (el && sections.length < total) {
        if (el.classList && el.classList.contains("shopify-section--sections-switcher-end")) break;
        if (el.classList && el.classList.contains("shopify-section")) sections.push(el);
        el = el.nextElementSibling;
      }
      return sections;
    }

    _showAllControlledSections() {
      this.controlledSections.forEach((sectionEl) => {
        sectionEl.toggleAttribute("hidden", false);
        this._restoreCarouselsInSection(sectionEl);
        this._restoreImagesWithTextInSection(sectionEl);
      });
      this.buttons.forEach((btn) => {
        btn.setAttribute("aria-pressed", "false");
        btn.removeAttribute("aria-current");
        btn.classList.remove("is-selected");
      });
    }

    _getTabIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return params.get(this._paramKey);
    }

    _getTabIdFromSession() {
      try {
        return sessionStorage.getItem(this._storageKey);
      } catch (_) {
        return null;
      }
    }

    _isValidTabId(tabId) {
      return this.config.tabs.some((t) => t.id === tabId);
    }

    _getInitialTabId() {
      const urlTab = this.config.persist?.url ? this._getTabIdFromUrl() : null;
      if (urlTab && this._isValidTabId(urlTab)) return urlTab;

      const sessionTab = this.config.persist?.session ? this._getTabIdFromSession() : null;
      if (sessionTab && this._isValidTabId(sessionTab)) return sessionTab;

      if (this.config.defaultTabId && this._isValidTabId(this.config.defaultTabId)) return this.config.defaultTabId;
      return this.config.tabs[0].id;
    }

    _onClick(event) {
      const button = event.target.closest("[data-sections-switcher-tab]");
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      const tabId = button.getAttribute("data-sections-switcher-tab");
      if (!this._isValidTabId(tabId)) return;

      this.select(tabId, { updateHistory: true });
    }

    _onPopState() {
      if (!this.config.persist?.url) return;
      const tabId = this._getTabIdFromUrl();
      if (tabId && this._isValidTabId(tabId)) {
        this.select(tabId, { updateHistory: false });
      }
    }

    _setPersistence(tabId, { updateHistory }) {
      if (this.config.persist?.session) {
        try {
          sessionStorage.setItem(this._storageKey, tabId);
        } catch (_) {}
      }

      if (this.config.persist?.url) {
        const url = new URL(window.location.href);
        url.searchParams.set(this._paramKey, tabId);
        if (updateHistory) history.pushState({}, "", url);
        else history.replaceState({}, "", url);
      }
    }

    _rangeForTab(tabId) {
      let start = 0;
      for (const tab of this.config.tabs) {
        const count = Number(tab.count) || 0;
        if (tab.id === tabId) return { start, end: start + count - 1, count };
        start += count;
      }
      return { start: 0, end: -1, count: 0 };
    }

    _pauseCarouselsInSection(sectionEl) {
      sectionEl.querySelectorAll("slideshow-carousel, effect-carousel").forEach((carousel) => {
        carousel.player?.pause();
      });
    }

    _restoreCarouselsInSection(sectionEl) {
      sectionEl.querySelectorAll("slideshow-carousel, effect-carousel").forEach((carousel) => {
        if (!carousel.items.length) {
          return;
        }

        const index = Math.min(Math.max(0, carousel.selectedIndex), carousel.items.length - 1);
        const hasSelectedSlide = carousel.items.some((item) => item.classList.contains("is-selected"));

        if (!hasSelectedSlide) {
          carousel.select(index, false, { animate: false });
          return;
        }

        const dots = sectionEl.querySelector(`page-dots[aria-controls="${carousel.id}"]`);
        if (dots && dots.selectedIndex !== index) {
          dots.select(index, false);
        }
      });
    }

    _restoreImagesWithTextInSection(sectionEl) {
      sectionEl.querySelectorAll("images-with-text-carousel, images-with-text-swiper").forEach((element) => {
        requestAnimationFrame(() => {
          if (typeof element.refreshLayout === "function") {
            element.refreshLayout();
          }
        });
      });
    }

    select(tabId, { updateHistory }) {
      this.selectedTabId = tabId;

      // Update button state
      this.buttons.forEach((btn) => {
        const isSelected = btn.getAttribute("data-sections-switcher-tab") === tabId;
        btn.setAttribute("aria-pressed", isSelected ? "true" : "false");
        if (isSelected) btn.setAttribute("aria-current", "true");
        else btn.removeAttribute("aria-current");
        btn.classList.toggle("is-selected", isSelected);
      });

      // Toggle controlled sections
      const { start, end, count } = this._rangeForTab(tabId);
      this.controlledSections.forEach((sectionEl, index) => {
        const shouldShow = count > 0 && index >= start && index <= end;
        const wasHidden = sectionEl.hasAttribute("hidden");

        if (!shouldShow) {
          this._pauseCarouselsInSection(sectionEl);
        }

        sectionEl.toggleAttribute("hidden", !shouldShow);

        if (shouldShow && wasHidden) {
          this._restoreCarouselsInSection(sectionEl);
          this._restoreImagesWithTextInSection(sectionEl);
        }
      });

      this._setPersistence(tabId, { updateHistory });
    }
  };

  if (!window.customElements.get("sections-switcher")) {
    window.customElements.define("sections-switcher", SectionsSwitcher);
  }


  /*----------------------------------------------------------------------------*\
  # Sections / Support Popup
  Displays a small non-modal popup on support articles after a reader scrolls past a threshold.
\*----------------------------------------------------------------------------*/
class SupportPopup extends DialogElement {
  constructor() {
    super();
    this.addEventListener("dialog:force-close", () => {
      this._shouldPersistDismissal = true;
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this._onScroll = this._onScroll || this._handleScroll.bind(this);

    if (Shopify.designMode || this.open || !this.shouldAppearAutomatically) {
      return;
    }

    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this._handleScroll();
  }

  disconnectedCallback() {
    this._teardownScrollListeners();
    super.disconnectedCallback();
  }

  get shouldTrapFocus() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get openFrom() {
    if (window.matchMedia("(max-width: 999px)").matches) {
      return this.getAttribute("mobile-open-from") || this.getAttribute("open-from") || "bottom";
    }

    return this.getAttribute("open-from") || "right";
  }

  get storageKey() {
    return this.getAttribute("storage-key") || "theme:support-popup-appeared";
  }

  get scrollThreshold() {
    const threshold = parseInt(this.getAttribute("scroll-threshold") || "30", 10);

    if (Number.isNaN(threshold)) {
      return 0.3;
    }

    return Math.min(Math.max(threshold, 0), 100) / 100;
  }

  get dismissDurationHours() {
    const hours = parseInt(this.getAttribute("dismiss-duration-hours") || "24", 10);

    if (Number.isNaN(hours) || hours < 1) {
      return 24;
    }

    return hours;
  }

  get shouldAppearAutomatically() {
    return !this._hasAlreadyAppeared();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === "open" && oldValue === null && newValue !== null) {
      this._teardownScrollListeners();
      this._shouldPersistDismissal = false;
    }

    if (name === "open" && oldValue !== null && newValue === null) {
      if (!Shopify.designMode && this._shouldPersistDismissal) {
        this._markAsAppeared();
      }

      this._shouldPersistDismissal = false;
    }
  }

  _getClipPathProperties() {
    switch (this.openFrom) {
      case "left":
        return ["inset(0 100% 0 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
      case "right":
        return ["inset(0 0 0 100% round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
      case "top":
        return ["inset(0 0 100% 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
      default:
        return ["inset(100% 0 0 0 round var(--rounded-md))", "inset(0 0 0 0 round var(--rounded-md))"];
    }
  }

  _showTransition(animate11 = true) {
    const panel = this.querySelector(".support-popup__panel");
    const closeButton = this.querySelector(".outside-close-button");
    const contentChildren = Array.from(this.querySelector(".support-popup__content")?.children || []);
    let animationControls;

    if (!panel) {
      animationControls = Motion_animate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 });
    } else {
      animationControls = Motion_timeline([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [panel, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        contentChildren.length > 0 ? [contentChildren, { opacity: [0, 1] }, { at: "<", duration: 0.15 }] : null,
        closeButton ? [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }] : null
      ].filter(Boolean));
    }

    animate11 ? animationControls.play() : animationControls.finish();

    return animationControls.finished;
  }

  _hideTransition() {
    const panel = this.querySelector(".support-popup__panel");
    const closeButton = this.querySelector(".outside-close-button");
    const contentChildren = Array.from(this.querySelector(".support-popup__content")?.children || []);

    if (!panel) {
      return Motion_animate(this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }).finished;
    }

    return Motion_timeline([
      closeButton ? [closeButton, { opacity: [null, 0] }, { duration: 0.12 }] : null,
      contentChildren.length > 0 ? [contentChildren, { opacity: [null, 0] }, { at: "<", duration: 0.12 }] : null,
      [panel, { clipPath: this._getClipPathProperties().reverse() }, { at: "<", duration: 0.3, easing: [0.86, 0, 0.07, 1] }],
      [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.12 }]
    ].filter(Boolean)).finished;
  }

  _handleScroll() {
    if (this.open || !this.shouldAppearAutomatically || !this.isConnected) {
      this._teardownScrollListeners();
      return;
    }

    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

    if (maxScroll <= 0) {
      return;
    }

    const currentScroll = Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);

    if (currentScroll / maxScroll >= this.scrollThreshold) {
      this.show();
    }
  }

  _teardownScrollListeners() {
    if (!this._onScroll) {
      return;
    }

    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onScroll);
  }

  _hasAlreadyAppeared() {
    try {
      const stored = localStorage.getItem(this.storageKey);

      if (!stored) {
        return false;
      }

      if (stored === "true") {
        return true;
      }

      const dismissedAt = parseInt(stored, 10);

      if (Number.isNaN(dismissedAt)) {
        return false;
      }

      const durationMs = this.dismissDurationHours * 60 * 60 * 1000;

      if (Date.now() - dismissedAt < durationMs) {
        return true;
      }

      localStorage.removeItem(this.storageKey);
      return false;
    } catch (error) {
      return false;
    }
  }

  _markAsAppeared() {
    try {
      localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (error) {
    }
  }
};
if (!window.customElements.get("support-popup")) {
  window.customElements.define("support-popup", SupportPopup);
}